import {formatRequisitionRaisedDate} from '../core/requisitionDates'
import React, {FC, useState, useMemo, useEffect, useRef, useCallback} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {ToastContainer, toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import * as XLSX from 'xlsx'
import {
  getRequisitions,
  getNextRequisitionCode,
  createRequisition,
  deleteRequisition,
  submitRequisition,
  approveVesselRequisition,
  approveShoreRequisition,
  sendReworkToVessel,
  rejectRequisitionFromCompany,
  getRequisitionAttachments,
  getParts,
  getPorts,
  getPortsPage,
  getSubcatalogueTypes,
  testRequisitionAPI,
  testCreateRequisitionWithFormData,
  getRequisitionById,
  updateRequisition,
  getAccountingAccounts,
  getAllAccountingAccounts,
  getAllSubAccounts,
  getAllInventoryCategoriesForAllCompanies,
  getAllInventoryCategoriesForCompany,
  getInventoryCategoriesForResolvedTenant,
  getSubCataloguesBySubAccount,
  getAllSubCatalogues,
  getSubComponents,
  getSubComponentsByPartId,
  getAllVessels,
  getInventoryItemHeads,
  getInventoryItemSubHeads,
  getVesselInventoryProjection,
  getOrCreateMasterInventoryInstance,
  createGeneralInventoryItem,
  createPort,
  updatePort,
  deletePort,
  getUnitsOfMeasure,
  getSubComponentsByEquipment,
  getPartsBySubcomponent,
  getCompanyGroupAdmins,
  getVesselsByCompany,
  getRequisitionFormCompanies,
  getRequisitionFormEquipment,
  getRequisitionFormVessels,
  createInventoryCategory,
  createInventoryItemHead,
  createInventoryItemSubHead,
  updateInventoryItemHead,
  updateInventoryItemSubHead,
  getInventoryAddresses,
  createInventoryAddress,
  updateInventoryAddress,
  deleteInventoryAddress,
  getInventoryItemSubCategories,
  createInventoryItemSubCategory,
  createAccountingAccount,
  createSubAccount,
  updateRequisitionLineQuantity,
  deleteInventoryCategory,
  deleteInventoryItemHead,
  deleteInventoryItemSubHead,
  deleteInventoryItemSubCategory,
  deleteAccountingAccount,
  deleteSubAccount,
} from '../core/_requests'
import {
  type EquipmentLoadStatus,
  type EquipmentOption,
  fetchEquipmentOptionsForVessel,
} from '../core/equipmentOptions'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import {
  LIBRARY_SEARCH_PLACEHOLDERS,
  matchesLibrarySearch,
  type LibrarySearchTab,
} from '../core/requisitionLibrarySearch'
import {formatApiError, isNetworkError} from '../../../shared/utils/apiErrorMessage'
import { 
  SubcatalogueType,
  CreateRequisitionRequest,
  UpdateRequisitionRequest,
  RequisitionUrgency,
  RequisitionType,
  FileRefDto,
  Port,
  Requisition,
  RequisitionFormCompanyLookup,
  RequisitionFormVesselLookup,
  RequisitionMutationResponse,
  RequisitionPageResponse,
  InventoryCategory,
  InventoryItemHead,
  InventoryItemSubHead,
  CreateInventoryItemHeadRequest,
  InventoryItemSubCategory,
  AccountingAccount,
  SubAccount,
  InventoryAddress,
  CreateInventoryAddressRequest,
  SubCatalogue,
  SubComponent,
  Equipment,
  Part,
  Vessel,
  CompanyGroupAdmin,
  CreatePortRequest,
  RequisitionLine,
  RequisitionDisplay,
  VesselInventoryProjection,
  UnitOfMeasure,
} from '../core/_models'
import {RequisitionAttachmentViewer} from './RequisitionAttachmentViewer'
import {RequisitionUrgencyDueDate} from './RequisitionUrgencyDueDate'
import {RequisitionRegionField} from './RequisitionRegionField'
import {requisitionDetailFields} from '../core/requisitionDetailFields'
import {useAuth} from '../../auth'
import CategoryModal from './CategoryModal'
import EditCategoryModal from './EditCategoryModal'
import HeadModal from './HeadModal'
import {
  dedupeInventoryHeadRows as sharedDedupeInventoryHeadRows,
  dedupeInventorySubHeadRows as sharedDedupeInventorySubHeadRows,
  loadScopedInventoryHeads as sharedLoadScopedInventoryHeads,
  loadScopedInventorySubHeads as sharedLoadScopedInventorySubHeads,
} from './procurementServiceHierarchy'
import EditHeadModal from './EditHeadModal'
import SubHeadModal from './SubHeadModal'
import EditSubHeadModal from './EditSubHeadModal'
import AccountingCodeModal from './AccountingCodeModal'
import EditAccountingCodeModal from './EditAccountingCodeModal'
import SubAccountingModal from './SubAccountingModal'
import EditSubAccountingModal from './EditSubAccountingModal'
import SearchableLookupField from './SearchableLookupField'
import RequisitionLECRDocument from './RequisitionLECRDocument'
import ProcurementFilterPanel, {FilterOption} from './ProcurementFilterPanel'
import TermsConditionsLibraryPanel from './TermsConditionsLibraryPanel'
import UomLibraryPanel from './UomLibraryPanel'
import {useProcurementPermissions} from '../core/permissionAccess'
import {PROCUREMENT_PERMS} from '../core/permissions'
import {
  libraryManageAccess,
  libraryViewAccess,
  libraryCrudAccess,
} from '../core/libraryPermissionAccess'
import {procurementTenantCgaId, procurementTenantVesselId} from '../core/procurementTenantContext'
import {normalizeInventoryCategoryRows, toCategoryLookupOptions} from '../core/procurementCategoryCatalog'
import {searchDefects} from '../../QHSE/core/_requests'
import type {QHSEDefectDto} from '../../QHSE/core/_models'
import {previewAttachmentBlob, resolveProcurementViewUrl} from '../core/attachmentUtils'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import {useGlobalFilter} from '../../../shared/context/GlobalFilterContext'
import RequisitionExportModal from './RequisitionExportModal'
import {downloadElementAsPdf} from '../core/exportUtils'
import {buildRequisitionDownloadDoc, RequisitionDownloadDoc} from '../core/requisitionDownload'
import {useNavigate} from 'react-router-dom'

// ===== Demo-only "extra" fields we won't send to backend =====
type ReqExtraMeta = {
  make?: string
  model?: string
  serialNumber?: string
}

interface AddItemsModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  requisitionId: number
  parts: Part[]
  ports: Port[]
  categories: SubcatalogueType[]
  companyGroupAdminId?: number
}

interface AddItemsFormData {
  title: string
  equipmentLink: string
  vesselId?: string
  equipmentId?: string
  description?: string
  type?: any
  urgency?: string
  itemMode?: 'TECHNICAL' | 'GENERAL'
  requestedPortId?: string
  customPortName?: string
  requiredDeliveryDate?: string
  categoryId?: string
  storeSubCategoryId?: string
  storeSubCategoryName?: string
  headId?: string
  subHeadId?: string
  partId?: string
  subComponentId?: string
  itemName?: string
  quantity?: string
  uom?: string
  estimatedUnitPrice?: string
  make?: string
  model?: string
  serialNumber?: string
}

type WorkflowRemarkMeta = {
  stage: 'Review' | 'Rejected'
  remark: string
} | null

// Line item interface for the form
interface LineItemForm {
  id: string // unique ID for React key
  persistedLineId?: number
  existingAttachments?: FileRefDto[]
  equipmentId?: string
  subcomponentId: string
  partId?: number | null
  generalItemId?: string
  inventoryItemId?: string
  manualItemName?: string
  itemName?: string
  quantity: string
  unitOfMeasurement: string
  estimatedUnitPrice?: string
  notes: string
  subcomponentClubbing?: string
  equipmentLink?: string
  make?: string
  model?: string
  customSubcomponentName?: string // Custom subcomponent name when "Other" is selected
  serviceHeadId?: string
  serviceSubHeadId?: string
}

type AddressFormState = {
  id?: number | null
  companyId: number | null
  addressType: 'BILLING' | 'SHIPPING' | 'BOTH'
  addressName: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  country: string
  postalCode: string
  active: boolean
}

// Static subcategory options for Stores flow
const subCategoryStaticOptions = [
  {value: 'IMPA_STORES', label: 'IMPA Stores'},
  {value: 'CHEMICALS', label: 'Chemicals'},
  {value: 'GASES_LUBE', label: 'Gases & Lube oil'},
]

const defaultAddressFormState: AddressFormState = {
  id: null,
  companyId: null,
  addressType: 'BOTH',
  addressName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  active: true,
}

type NormalizedCrewScope = {
  vesselId: number | null
  companyId: number | null
  vesselName: string
  companyName: string
  vesselNameNormalized: string
  companyNameNormalized: string
}

type ScopedInventoryLoadOptions = {
  currentUser: any
  /** Effective scope ALL for inventory picker (e.g. REQUISITION_* permission scope). */
  inventoryScopeAll: boolean
  inventoryScopeCompany: boolean
  inventoryScopeVessel: boolean
  isServicesCategory?: boolean
  selectedVesselId?: number | string | null
  selectedCompanyId?: number | string | null
  selectedHeadId?: number | string | null
  requisitionVesselId?: number | string | null
  requisitionCompanyId?: number | string | null
  requisitionHeadId?: number | string | null
  selectedVesselName?: string | null
  selectedCompanyName?: string | null
  requisitionVesselName?: string | null
  requisitionCompanyName?: string | null
  requireVesselWhenCompanySelected?: boolean
  debugContext?: string
}

type InventoryScopeSource = 'selected' | 'requisition' | 'currentUser' | 'none'

type ScopedInventoryLoadResult<T> = {
  normalizedCrewScope: NormalizedCrewScope
  resolvedScope: NormalizedCrewScope
  selectedVesselId: number | null
  selectedCompanyId: number | null
  requisitionVesselId: number | null
  requisitionCompanyId: number | null
  selectedHeadId: number | null
  requisitionHeadId: number | null
  resolvedHeadId: number | null
  scopeSource: {
    vessel: InventoryScopeSource
    company: InventoryScopeSource
    head: InventoryScopeSource
  }
  deferred: boolean
  deferReason: string | null
  apiScope: {
    vesselId?: number
    companyId?: number
    headId?: number
  }
  rawRows: T[]
  filteredRows: T[]
  rows: T[]
  usedRawFallback: boolean
}

const PROCUREMENT_SCOPE_PATCH_SIGNATURE = 'REQ_SCOPE_PATH_TRACE_2026-04-09_v3'

const toPositiveNumber = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const normalizeScopeName = (value: any): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const dedupeRowsByLogicalKey = <T,>(rows: T[], getKey: (row: T) => string | null): T[] => {
  const uniqueRows: T[] = []
  const seen = new Set<string>()
  ;(rows || []).forEach((row) => {
    const key = getKey(row)
    if (!key || seen.has(key)) return
    seen.add(key)
    uniqueRows.push(row)
  })
  return uniqueRows
}

const dedupeInventoryHeadRows = (rows: InventoryItemHead[]): InventoryItemHead[] => {
  const dedupedByIdOrScope = dedupeRowsByLogicalKey(rows || [], (head) => {
    const id = toPositiveNumber(head?.id)
    if (id) return `id:${id}`

    const name = normalizeScopeName(head?.name)
    if (!name) return null

    const companyId = toPositiveNumber((head as any)?.cgaid?.id ?? (head as any)?.cgaid) ?? 0
    const vesselId = toPositiveNumber((head as any)?.vessel?.id ?? (head as any)?.vesselId) ?? 0
    return `scope:${name}|c:${companyId}|v:${vesselId}`
  })

  // Secondary guard: avoid repeated visible labels in same company/vessel scope.
  return dedupeRowsByLogicalKey(dedupedByIdOrScope, (head) => {
    const name = normalizeScopeName(head?.name)
    if (!name) return null
    const companyId = toPositiveNumber((head as any)?.cgaid?.id ?? (head as any)?.cgaid) ?? 0
    const vesselId = toPositiveNumber((head as any)?.vessel?.id ?? (head as any)?.vesselId) ?? 0
    return `label:${name}|c:${companyId}|v:${vesselId}`
  })
}

const dedupeInventorySubHeadRows = (rows: InventoryItemSubHead[]): InventoryItemSubHead[] => {
  const dedupedByIdOrScope = dedupeRowsByLogicalKey(rows || [], (subHead) => {
    const id = toPositiveNumber(subHead?.id)
    if (id) return `id:${id}`

    const name = normalizeScopeName(subHead?.name)
    if (!name) return null

    const headId = toPositiveNumber(subHead?.inventoryItemHeadId ?? (subHead as any)?.inventoryItemHead?.id) ?? 0
    const companyId =
      toPositiveNumber((subHead as any)?.cgaid?.id ?? (subHead as any)?.cgaid) ??
      toPositiveNumber((subHead as any)?.inventoryItemHead?.cgaid?.id) ??
      0
    const vesselId =
      toPositiveNumber((subHead as any)?.vessel?.id ?? (subHead as any)?.vesselId) ??
      toPositiveNumber((subHead as any)?.inventoryItemHead?.vessel?.id ?? (subHead as any)?.inventoryItemHead?.vesselId) ??
      0
    return `scope:${name}|h:${headId}|c:${companyId}|v:${vesselId}`
  })

  // Secondary guard: avoid repeated visible labels in same head/company/vessel scope.
  return dedupeRowsByLogicalKey(dedupedByIdOrScope, (subHead) => {
    const name = normalizeScopeName(subHead?.name)
    if (!name) return null
    const headId = toPositiveNumber(subHead?.inventoryItemHeadId ?? (subHead as any)?.inventoryItemHead?.id) ?? 0
    const companyId =
      toPositiveNumber((subHead as any)?.cgaid?.id ?? (subHead as any)?.cgaid) ??
      toPositiveNumber((subHead as any)?.inventoryItemHead?.cgaid?.id) ??
      0
    const vesselId =
      toPositiveNumber((subHead as any)?.vessel?.id ?? (subHead as any)?.vesselId) ??
      toPositiveNumber((subHead as any)?.inventoryItemHead?.vessel?.id ?? (subHead as any)?.inventoryItemHead?.vesselId) ??
      0
    return `label:${name}|h:${headId}|c:${companyId}|v:${vesselId}`
  })
}

const dedupeSubComponentRows = (rows: SubComponent[]): SubComponent[] =>
  dedupeRowsByLogicalKey(rows || [], (subComponent) => {
    const id = toPositiveNumber(subComponent?.id)
    if (id) return `id:${id}`
    const name = normalizeScopeName(subComponent?.name)
    if (!name) return null
    const equipmentId = toPositiveNumber(subComponent?.equipmentId) ?? 0
    const partId = toPositiveNumber(subComponent?.part?.id) ?? 0
    return `scope:${name}|e:${equipmentId}|p:${partId}`
  })

const dedupePartRows = (rows: Part[]): Part[] =>
  dedupeRowsByLogicalKey(rows || [], (part) => {
    const id = toPositiveNumber(part?.id)
    if (id) return `id:${id}`
    const name = normalizeScopeName(part?.name)
    if (!name) return null
    const partNo = normalizeScopeName(part?.partNo)
    const subComponentId = toPositiveNumber(part?.subcomponent?.id) ?? 0
    return `scope:${name}|pn:${partNo}|sc:${subComponentId}`
  })

type SubcomponentOptionGroup = {
  key: string
  representativeId: number
  representative: SubComponent
  memberIds: number[]
  displayName: string
  code: string
}

const getSubcomponentDisplayName = (sub: SubComponent | any): string =>
  String((sub as any)?.subComponentFunctionDescription || sub?.name || '').trim()

const getSubcomponentCode = (sub: SubComponent | any): string =>
  String((sub as any)?.subComponentCode || '').trim()

const getSubcomponentGroupKey = (sub: SubComponent | any): string | null => {
  const displayName = normalizeScopeName(getSubcomponentDisplayName(sub))
  if (!displayName) return null

  const code = normalizeScopeName(getSubcomponentCode(sub))
  const equipmentId =
    toPositiveNumber(sub?.equipmentId ?? (sub as any)?.equipment?.id) ?? 0
  const vesselId =
    toPositiveNumber((sub as any)?.vesselId ?? (sub as any)?.vessel?.id) ?? 0
  const partId = toPositiveNumber(sub?.part?.id) ?? 0
  return `eq:${equipmentId}|v:${vesselId}|name:${displayName}|code:${code}|part:${partId}`
}

const groupSubcomponentsForDropdown = (rows: SubComponent[]): SubcomponentOptionGroup[] => {
  const groups = new Map<string, SubcomponentOptionGroup>()

  ;(rows || []).forEach((sub) => {
    const id = toPositiveNumber(sub?.id)
    if (!id) return
    const key = getSubcomponentGroupKey(sub)
    if (!key) return

    const existing = groups.get(key)
    if (!existing) {
      groups.set(key, {
        key,
        representativeId: id,
        representative: sub,
        memberIds: [id],
        displayName: getSubcomponentDisplayName(sub),
        code: getSubcomponentCode(sub),
      })
      return
    }

    if (!existing.memberIds.includes(id)) {
      existing.memberIds.push(id)
      existing.memberIds.sort((a, b) => a - b)
    }

    if (id < existing.representativeId) {
      existing.representativeId = id
      existing.representative = sub
      if (!existing.displayName) {
        existing.displayName = getSubcomponentDisplayName(sub)
      }
      if (!existing.code) {
        existing.code = getSubcomponentCode(sub)
      }
    }
  })

  return Array.from(groups.values()).sort((a, b) => {
    const byName = a.displayName.localeCompare(b.displayName)
    if (byName !== 0) return byName
    const byCode = a.code.localeCompare(b.code)
    if (byCode !== 0) return byCode
    return a.representativeId - b.representativeId
  })
}

const buildSubcomponentGroupLookup = (
  groups: SubcomponentOptionGroup[]
): Map<number, SubcomponentOptionGroup> => {
  const lookup = new Map<number, SubcomponentOptionGroup>()
  ;(groups || []).forEach((group) => {
    group.memberIds.forEach((memberId) => {
      if (!lookup.has(memberId)) lookup.set(memberId, group)
    })
    if (!lookup.has(group.representativeId)) {
      lookup.set(group.representativeId, group)
    }
  })
  return lookup
}

const formatSubcomponentGroupLabel = (group: SubcomponentOptionGroup): string => {
  const baseName = group.displayName || `Sub Component #${group.representativeId}`
  const codeSuffix = group.code ? ` (${group.code})` : ''
  const inferredCount = toPositiveNumber((group.representative as any)?.instanceCount) ?? 0
  const totalMembers = Math.max(group.memberIds.length, inferredCount)
  const countSuffix = totalMembers > 1 ? ` (${totalMembers} linked)` : ''
  return `${baseName}${codeSuffix}${countSuffix}`
}

const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined
  return path.split('.').reduce<any>((acc, key) => {
    if (acc === null || acc === undefined) return undefined
    return acc[key]
  }, obj)
}

const normalizeCrewScopeFromUser = (currentUser: any): NormalizedCrewScope => {
  const vesselId = [
    currentUser?.vesselId,
    currentUser?.vessel?.id,
    currentUser?.vessel?.vesselId,
    currentUser?.roleEntityId,
  ]
    .map(toPositiveNumber)
    .find((value): value is number => value !== null) ?? null

  const companyId = [
    currentUser?.companyGroupAdminId,
    currentUser?.company?.cgaid?.id,
    currentUser?.cgaid?.id,
    currentUser?.companyId,
    currentUser?.vessel?.companyGroupAdminId,
    currentUser?.vessel?.companyGroupAdmin?.id,
    currentUser?.companyGroupAdmin?.id,
    currentUser?.companyAdmin?.cgaid?.id,
  ]
    .map(toPositiveNumber)
    .find((value): value is number => value !== null) ?? null

  const vesselName =
    String(
      currentUser?.vessel?.fleet_name ??
        currentUser?.vessel?.fleetName ??
        currentUser?.vessel?.name ??
        currentUser?.vessel?.vesselName ??
        currentUser?.vesselName ??
        ''
    ).trim() || ''

  const companyName =
    String(
      currentUser?.company?.cgaid?.name ??
        currentUser?.companyGroupAdmin?.name ??
        currentUser?.vessel?.companyGroupAdmin?.name ??
        currentUser?.companyName ??
        ''
    ).trim() || ''

  return {
    vesselId,
    companyId,
    vesselName,
    companyName,
    vesselNameNormalized: normalizeScopeName(vesselName),
    companyNameNormalized: normalizeScopeName(companyName),
  }
}

const collectRowScopeIds = (row: any, paths: string[]): number[] => {
  return paths
    .map((path) => toPositiveNumber(getNestedValue(row, path)))
    .filter((value): value is number => value !== null)
}

const collectRowScopeNames = (row: any, paths: string[]): string[] => {
  return paths
    .map((path) => normalizeScopeName(getNestedValue(row, path)))
    .filter((value) => Boolean(value))
}

const rowMatchesCrewScope = (row: any, normalizedScope: NormalizedCrewScope): boolean => {
  const companyIdCandidates = collectRowScopeIds(row, [
    'cgaid.id',
    'cgaid',
    'companyGroupAdminId',
    'companyId',
    'company.id',
    'company.cgaid.id',
    'inventoryItemCategory.cgaid.id',
    'inventoryItemHead.cgaid.id',
    'head.cgaid.id',
  ])
  const companyNameCandidates = collectRowScopeNames(row, [
    'cgaid.name',
    'companyName',
    'company.name',
    'companyGroupAdmin.name',
    'company.cgaid.name',
    'inventoryItemCategory.cgaid.name',
    'inventoryItemHead.cgaid.name',
    'head.cgaid.name',
  ])

  const vesselIdCandidates = collectRowScopeIds(row, [
    'vesselId',
    'vessel.id',
    'vessel.vesselId',
    'roleEntityId',
    'inventoryItemCategory.vesselId',
    'inventoryItemCategory.vessel.id',
    'inventoryItemHead.vesselId',
    'inventoryItemHead.vessel.id',
    'head.vesselId',
    'head.vessel.id',
  ])
  const vesselNameCandidates = collectRowScopeNames(row, [
    'vesselName',
    'vessel.fleet_name',
    'vessel.fleetName',
    'vessel.name',
    'vessel.vesselName',
    'inventoryItemCategory.vesselName',
    'inventoryItemCategory.vessel.fleet_name',
    'inventoryItemCategory.vessel.fleetName',
    'inventoryItemHead.vesselName',
    'inventoryItemHead.vessel.fleet_name',
    'inventoryItemHead.vessel.fleetName',
    'head.vesselName',
    'head.vessel.fleet_name',
    'head.vessel.fleetName',
  ])

  const companyMatches = (() => {
    if (!normalizedScope.companyId && !normalizedScope.companyNameNormalized) return true
    const matchesById =
      normalizedScope.companyId !== null &&
      companyIdCandidates.some((rowId) => rowId === normalizedScope.companyId)
    const matchesByName =
      Boolean(normalizedScope.companyNameNormalized) &&
      companyNameCandidates.some((rowName) => rowName === normalizedScope.companyNameNormalized)

    if (companyIdCandidates.length > 0 || companyNameCandidates.length > 0) {
      return matchesById || matchesByName
    }
    return false
  })()

  const vesselMatches = (() => {
    if (!normalizedScope.vesselId && !normalizedScope.vesselNameNormalized) return true
    const matchesById =
      normalizedScope.vesselId !== null &&
      vesselIdCandidates.some((rowId) => rowId === normalizedScope.vesselId)
    const matchesByName =
      Boolean(normalizedScope.vesselNameNormalized) &&
      vesselNameCandidates.some((rowName) => rowName === normalizedScope.vesselNameNormalized)

    if (vesselIdCandidates.length > 0 || vesselNameCandidates.length > 0) {
      return matchesById || matchesByName
    }

    // Company-only rows are allowed for vessel-scoped users if company matches.
    return true
  })()

  return companyMatches && vesselMatches
}

const loadRequisitionFormCategories = async (cgaId?: number): Promise<InventoryCategory[]> => {
  const response = cgaId
    ? await getAllInventoryCategoriesForCompany(cgaId)
    : await getInventoryCategoriesForResolvedTenant()
  return normalizeInventoryCategoryRows(response)
}

const resolveInventoryApiScope = (
  options: ScopedInventoryLoadOptions
): {
  normalizedCrewScope: NormalizedCrewScope
  resolvedScope: NormalizedCrewScope
  selectedVesselId: number | null
  selectedCompanyId: number | null
  requisitionVesselId: number | null
  requisitionCompanyId: number | null
  selectedHeadId: number | null
  requisitionHeadId: number | null
  resolvedHeadId: number | null
  scopeSource: {
    vessel: InventoryScopeSource
    company: InventoryScopeSource
    head: InventoryScopeSource
  }
  deferred: boolean
  deferReason: string | null
  apiScope: {vesselId?: number; companyId?: number; headId?: number}
} => {
  const normalizedCrewScope = normalizeCrewScopeFromUser(options.currentUser)
  const selectedVesselId = toPositiveNumber(options.selectedVesselId)
  const selectedCompanyId = toPositiveNumber(options.selectedCompanyId)
  const selectedHeadId = toPositiveNumber(options.selectedHeadId)
  const requisitionVesselId = toPositiveNumber(options.requisitionVesselId)
  const requisitionCompanyId = toPositiveNumber(options.requisitionCompanyId)
  const requisitionHeadId = toPositiveNumber(options.requisitionHeadId)

  const selectedVesselName = String(options.selectedVesselName ?? '').trim()
  const selectedCompanyName = String(options.selectedCompanyName ?? '').trim()
  const requisitionVesselName = String(options.requisitionVesselName ?? '').trim()
  const requisitionCompanyName = String(options.requisitionCompanyName ?? '').trim()

  const resolvedVesselId = selectedVesselId ?? requisitionVesselId ?? normalizedCrewScope.vesselId
  const resolvedCompanyId = selectedCompanyId ?? requisitionCompanyId ?? normalizedCrewScope.companyId
  const resolvedHeadId = selectedHeadId ?? requisitionHeadId ?? null

  const vesselSource: InventoryScopeSource =
    selectedVesselId !== null
      ? 'selected'
      : requisitionVesselId !== null
      ? 'requisition'
      : normalizedCrewScope.vesselId !== null
      ? 'currentUser'
      : 'none'

  const companySource: InventoryScopeSource =
    selectedCompanyId !== null
      ? 'selected'
      : requisitionCompanyId !== null
      ? 'requisition'
      : normalizedCrewScope.companyId !== null
      ? 'currentUser'
      : 'none'
  const headSource: InventoryScopeSource =
    selectedHeadId !== null ? 'selected' : requisitionHeadId !== null ? 'requisition' : 'none'

  const resolvedVesselName =
    (selectedVesselId !== null ? selectedVesselName : '') ||
    (requisitionVesselId !== null ? requisitionVesselName : '') ||
    normalizedCrewScope.vesselName ||
    ''

  const resolvedCompanyName =
    (selectedCompanyId !== null ? selectedCompanyName : '') ||
    (requisitionCompanyId !== null ? requisitionCompanyName : '') ||
    normalizedCrewScope.companyName ||
    ''

  const resolvedScope: NormalizedCrewScope = {
    vesselId: resolvedVesselId,
    companyId: resolvedCompanyId,
    vesselName: resolvedVesselName,
    companyName: resolvedCompanyName,
    vesselNameNormalized: normalizeScopeName(resolvedVesselName),
    companyNameNormalized: normalizeScopeName(resolvedCompanyName),
  }

  const deferred =
    Boolean(options.requireVesselWhenCompanySelected) &&
    selectedCompanyId !== null &&
    selectedVesselId === null
  const deferReason = deferred ? 'company_selected_waiting_for_vessel' : null

  const vesselId = resolvedVesselId ?? undefined
  const companyId = resolvedCompanyId ?? undefined
  const headId = resolvedHeadId ?? undefined

  return {
    normalizedCrewScope,
    resolvedScope,
    selectedVesselId,
    selectedCompanyId,
    requisitionVesselId,
    requisitionCompanyId,
    selectedHeadId,
    requisitionHeadId,
    resolvedHeadId,
    scopeSource: {
      vessel: vesselSource,
      company: companySource,
      head: headSource,
    },
    deferred,
    deferReason,
    apiScope: {
      vesselId,
      companyId,
      headId,
    },
  }
}

const applyCrewScopeFilterWithFallback = <T,>(
  rows: T[],
  normalizedCrewScope: NormalizedCrewScope,
  inventoryScopeVessel: boolean
): {
  filteredRows: T[]
  rows: T[]
  usedRawFallback: boolean
} => {
  if (!inventoryScopeVessel) {
    return {
      filteredRows: rows,
      rows,
      usedRawFallback: false,
    }
  }

  const filteredRows = rows.filter((row) => rowMatchesCrewScope(row as any, normalizedCrewScope))
  const usedRawFallback = rows.length > 0 && filteredRows.length === 0

  return {
    filteredRows,
    rows: usedRawFallback ? rows : filteredRows,
    usedRawFallback,
  }
}

const loadScopedInventoryHeads = async (
  options: ScopedInventoryLoadOptions
): Promise<ScopedInventoryLoadResult<InventoryItemHead>> => {
  const scope = resolveInventoryApiScope(options)
  const logPrefix = options.debugContext || 'Inventory Scope'
  const requestParams = {
    vesselId: scope.apiScope.vesselId ?? null,
    companyId: scope.apiScope.companyId ?? null,
    headId: scope.apiScope.headId ?? null,
  }

  console.log(`[${logPrefix}] selectedCompanyId:`, scope.selectedCompanyId)
  console.log(`[${logPrefix}] selectedVesselId:`, scope.selectedVesselId)
  console.log(`[${logPrefix}] currentRequisition.companyId:`, scope.requisitionCompanyId)
  console.log(`[${logPrefix}] currentRequisition.vesselId:`, scope.requisitionVesselId)
  console.log(`[${logPrefix}] fallback currentUser companyId:`, scope.normalizedCrewScope.companyId)
  console.log(`[${logPrefix}] fallback currentUser vesselId:`, scope.normalizedCrewScope.vesselId)
  console.log(`[${logPrefix}] isServicesCategory:`, Boolean(options.isServicesCategory))
  console.log(`[${logPrefix}] normalizedCrewScope:`, scope.normalizedCrewScope)
  console.log(`[${logPrefix}] resolved apiScope:`, {
    ...scope.apiScope,
    source: scope.scopeSource,
  })

  if (scope.deferred) {
    console.log(`[${logPrefix}] heads load deferred:`, scope.deferReason)
    return {
      normalizedCrewScope: scope.normalizedCrewScope,
      resolvedScope: scope.resolvedScope,
      selectedVesselId: scope.selectedVesselId,
      selectedCompanyId: scope.selectedCompanyId,
      requisitionVesselId: scope.requisitionVesselId,
      requisitionCompanyId: scope.requisitionCompanyId,
      selectedHeadId: scope.selectedHeadId,
      requisitionHeadId: scope.requisitionHeadId,
      resolvedHeadId: scope.resolvedHeadId,
      scopeSource: scope.scopeSource,
      deferred: true,
      deferReason: scope.deferReason,
      apiScope: scope.apiScope,
      rawRows: [],
      filteredRows: [],
      rows: [],
      usedRawFallback: false,
    }
  }

  console.log('HEAD API PARAMS', {
    companyId: requestParams.companyId,
    vesselId: requestParams.vesselId,
    headId: requestParams.headId,
    source: scope.scopeSource,
  })
  console.log(`[${logPrefix}] final request params passed to head API:`, requestParams)

  const apiRows =
    (await getInventoryItemHeads(
      undefined,
      requestParams.companyId ?? undefined
    )) || []
  const rawRows = sharedDedupeInventoryHeadRows(apiRows)

  console.log(`[${logPrefix}] raw heads response length:`, apiRows.length)
  console.log(`[${logPrefix}] deduped heads response length:`, rawRows.length)

  const {filteredRows, rows, usedRawFallback} = applyCrewScopeFilterWithFallback(
    rawRows,
    scope.resolvedScope,
    options.inventoryScopeVessel
  )

  console.log(`[${logPrefix}] filtered heads response count:`, filteredRows.length)

  return {
    normalizedCrewScope: scope.normalizedCrewScope,
    resolvedScope: scope.resolvedScope,
    selectedVesselId: scope.selectedVesselId,
    selectedCompanyId: scope.selectedCompanyId,
    requisitionVesselId: scope.requisitionVesselId,
    requisitionCompanyId: scope.requisitionCompanyId,
    selectedHeadId: scope.selectedHeadId,
    requisitionHeadId: scope.requisitionHeadId,
    resolvedHeadId: scope.resolvedHeadId,
    scopeSource: scope.scopeSource,
    deferred: scope.deferred,
    deferReason: scope.deferReason,
    apiScope: scope.apiScope,
    rawRows,
    filteredRows,
    rows,
    usedRawFallback,
  }
}

const loadScopedInventorySubHeads = async (
  options: ScopedInventoryLoadOptions
): Promise<ScopedInventoryLoadResult<InventoryItemSubHead>> => {
  const scope = resolveInventoryApiScope(options)
  const logPrefix = options.debugContext || 'Inventory Scope'
  const requestParams = {
    vesselId: scope.apiScope.vesselId ?? null,
    headId: scope.apiScope.headId ?? null,
    companyId: scope.apiScope.companyId ?? null,
  }

  console.log(`[${logPrefix}] selectedCompanyId:`, scope.selectedCompanyId)
  console.log(`[${logPrefix}] selectedVesselId:`, scope.selectedVesselId)
  console.log(`[${logPrefix}] currentRequisition.companyId:`, scope.requisitionCompanyId)
  console.log(`[${logPrefix}] currentRequisition.vesselId:`, scope.requisitionVesselId)
  console.log(`[${logPrefix}] fallback currentUser companyId:`, scope.normalizedCrewScope.companyId)
  console.log(`[${logPrefix}] fallback currentUser vesselId:`, scope.normalizedCrewScope.vesselId)
  console.log(`[${logPrefix}] isServicesCategory:`, Boolean(options.isServicesCategory))
  console.log(`[${logPrefix}] normalizedCrewScope:`, scope.normalizedCrewScope)
  console.log(`[${logPrefix}] resolved apiScope:`, {
    ...scope.apiScope,
    source: scope.scopeSource,
  })

  if (scope.deferred) {
    console.log(`[${logPrefix}] subheads load deferred:`, scope.deferReason)
    return {
      normalizedCrewScope: scope.normalizedCrewScope,
      resolvedScope: scope.resolvedScope,
      selectedVesselId: scope.selectedVesselId,
      selectedCompanyId: scope.selectedCompanyId,
      requisitionVesselId: scope.requisitionVesselId,
      requisitionCompanyId: scope.requisitionCompanyId,
      selectedHeadId: scope.selectedHeadId,
      requisitionHeadId: scope.requisitionHeadId,
      resolvedHeadId: scope.resolvedHeadId,
      scopeSource: scope.scopeSource,
      deferred: true,
      deferReason: scope.deferReason,
      apiScope: scope.apiScope,
      rawRows: [],
      filteredRows: [],
      rows: [],
      usedRawFallback: false,
    }
  }

  console.log('SUBHEAD API PARAMS', {
    companyId: requestParams.companyId,
    vesselId: requestParams.vesselId,
    headId: requestParams.headId,
    source: scope.scopeSource,
  })
  console.log(`[${logPrefix}] final request params passed to subhead API:`, requestParams)

  const apiRows =
    (await getInventoryItemSubHeads(
      undefined,
      requestParams.headId ?? undefined,
      requestParams.companyId ?? undefined
    )) || []
  const rawRows = sharedDedupeInventorySubHeadRows(apiRows)

  console.log(`[${logPrefix}] raw subheads response length:`, apiRows.length)
  console.log(`[${logPrefix}] deduped subheads response length:`, rawRows.length)

  const {filteredRows, rows, usedRawFallback} = applyCrewScopeFilterWithFallback(
    rawRows,
    scope.resolvedScope,
    options.inventoryScopeVessel
  )

  console.log(`[${logPrefix}] filtered subheads response count:`, filteredRows.length)

  return {
    normalizedCrewScope: scope.normalizedCrewScope,
    resolvedScope: scope.resolvedScope,
    selectedVesselId: scope.selectedVesselId,
    selectedCompanyId: scope.selectedCompanyId,
    requisitionVesselId: scope.requisitionVesselId,
    requisitionCompanyId: scope.requisitionCompanyId,
    selectedHeadId: scope.selectedHeadId,
    requisitionHeadId: scope.requisitionHeadId,
    resolvedHeadId: scope.resolvedHeadId,
    scopeSource: scope.scopeSource,
    deferred: scope.deferred,
    deferReason: scope.deferReason,
    apiScope: scope.apiScope,
    rawRows,
    filteredRows,
    rows,
    usedRawFallback,
  }
}

const AddItemsModal: FC<AddItemsModalProps> = ({
  visible,
  onClose,
  onSubmit,
  requisitionId,
  parts,
  ports,
  categories,
  companyGroupAdminId,
}) => {
  const {currentUser} = useAuth()
  const {getScope} = useProcurementPermissions()
  const addItemsScope =
    getScope(PROCUREMENT_PERMS.REQUISITION_ADD_ITEMS) ??
    getScope(PROCUREMENT_PERMS.UPDATE_REQUISITION) ??
    getScope(PROCUREMENT_PERMS.READ_REQUISITION)
  const inventoryScopeAll = addItemsScope === 'ALL'
  const inventoryScopeCompany = addItemsScope === 'COMPANY'
  const inventoryScopeVessel = addItemsScope === 'VESSEL'
  // Debug logging
  console.log('AddItemsModal props:', {
    visible,
    requisitionId,
    partsCount: parts?.length || 0,
    portsCount: ports?.length || 0,
    categoriesCount: categories?.length || 0,
    companyGroupAdminId,
  })

  const [formData, setFormData] = useState<AddItemsFormData>({
    title: '',
    equipmentLink: '',
    vesselId: '',
    equipmentId: '',
  })
  const [lineItems, setLineItems] = useState<LineItemForm[]>([
      {
        id: Date.now().toString(),
        equipmentId: '',
        subcomponentId: '',
        partId: null,
          generalItemId: '',
          manualItemName: '',
          quantity: '',
          unitOfMeasurement: '',
          estimatedUnitPrice: '',
        notes: '',
        subcomponentClubbing: '',
        equipmentLink: '',
      customSubcomponentName: '',
      serviceHeadId: '',
      serviceSubHeadId: '',
      },
    ])
  const [unitsOfMeasure, setUnitsOfMeasure] = useState<UnitOfMeasure[]>([])
  const [isLoadingUnitsOfMeasure, setIsLoadingUnitsOfMeasure] = useState(false)
  const [unitsOfMeasureError, setUnitsOfMeasureError] = useState<string | null>(null)
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableSubComponents, setAvailableSubComponents] = useState<SubComponent[]>([])
  const [isLoadingSubComponents, setIsLoadingSubComponents] = useState(false)
  const [currentRequisition, setCurrentRequisition] = useState<Requisition | null>(null)
  const [editRegionId, setEditRegionId] = useState<number | null>(null)
  const [editRegionName, setEditRegionName] = useState<string | null>(null)
  const [editRegionTouched, setEditRegionTouched] = useState(false)
  const [isLoadingRequisition, setIsLoadingRequisition] = useState(false)
  const [requisitionPartId, setRequisitionPartId] = useState<number | null>(null)
  const [selectedPartId, setSelectedPartId] = useState<number | null>(null)
  const [filteredParts, setFilteredParts] = useState<Part[]>([])
  const [allSubComponents, setAllSubComponents] = useState<SubComponent[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [lineAttachmentFiles, setLineAttachmentFiles] = useState<Record<string, File[]>>({})
  const [equipmentOptions, setEquipmentOptions] = useState<Equipment[]>([])
  const [subcomponentOptionsMap, setSubcomponentOptionsMap] = useState<
    Record<number, SubComponent[]>
  >({})
  const [partOptionsMap, setPartOptionsMap] = useState<Record<number, Part[]>>({})
  const [generalInventoryItems, setGeneralInventoryItems] = useState<VesselInventoryProjection[]>([])
  const [isLoadingGeneralInventoryItems, setIsLoadingGeneralInventoryItems] = useState(false)
  const [generalItemSearchTerms, setGeneralItemSearchTerms] = useState<Record<string, string>>({})
  const [generalInventoryPage, setGeneralInventoryPage] = useState(0)
  const [generalInventoryHasMore, setGeneralInventoryHasMore] = useState(true)
  const generalItemSearchTimeoutRef = useRef<Record<string, number | undefined>>({})
  const generalInventorySearchRequestSeqRef = useRef(0)
  const generalInventoryQueryRef = useRef<{
    vesselId: number | null
    inventoryItemSubCategoryId: number | null
    search: string
  }>({
    vesselId: null,
    inventoryItemSubCategoryId: null,
    search: '',
  })

  const loadUnitsOfMeasure = async () => {
    setIsLoadingUnitsOfMeasure(true)
    setUnitsOfMeasureError(null)
    try {
      const rows = await getUnitsOfMeasure()
      setUnitsOfMeasure((rows || []).filter((uom) => uom.active !== false))
    } catch (error) {
      console.error('Error loading units of measure for requisition:', error)
      setUnitsOfMeasure([])
      setUnitsOfMeasureError('Unable to load units of measure.')
    } finally {
      setIsLoadingUnitsOfMeasure(false)
    }
  }

  const uomOptionsFor = (savedValue?: string) => {
    const current = savedValue?.trim() || ''
    if (!current || unitsOfMeasure.some((uom) => uom.code === current)) return unitsOfMeasure
    return [{id: -1, code: current, name: 'Saved value', active: false}, ...unitsOfMeasure]
  }

  useEffect(() => {
    if (visible) void loadUnitsOfMeasure()
  }, [visible])

  // Header meta (moved from Create Requisition step)
  const [inventoryItemHeads, setInventoryItemHeads] = useState<InventoryItemHead[]>([])
  const [inventoryItemSubHeads, setInventoryItemSubHeads] = useState<InventoryItemSubHead[]>([])
  const [inventoryItemSubCategories, setInventoryItemSubCategories] = useState<InventoryItemSubCategory[]>([])
  const [headerHeadId, setHeaderHeadId] = useState<string>('')
  const [headerSubHeadId, setHeaderSubHeadId] = useState<string>('')
  const [headerHeads, setHeaderHeads] = useState<InventoryItemHead[]>([])
  const [headerSubHeads, setHeaderSubHeads] = useState<InventoryItemSubHead[]>([])
  const uniqueHeaderHeads = useMemo(
    () => sharedDedupeInventoryHeadRows(headerHeads || []),
    [headerHeads]
  )
  const uniqueHeaderSubHeads = useMemo(
    () => sharedDedupeInventorySubHeadRows(headerSubHeads || []),
    [headerSubHeads]
  )

  const categoryName = (currentRequisition?.category?.name || '').toUpperCase()
  const addModalIsStoresCategory = categoryName.includes('STORE')
  const addModalIsServicesCategory = categoryName.includes('SERVICE')
  const effectiveRequestFor =
    (addModalIsServicesCategory
      ? 'TECHNICAL'
      : currentRequisition?.requestFor) ||
    (currentRequisition?.lines?.some((l) => l.manualItemName) || currentRequisition?.manualItemName
      ? 'GENERAL_STORES'
      : 'TECHNICAL')
  const isGeneralStores = addModalIsStoresCategory || effectiveRequestFor === 'GENERAL_STORES'
  const derivedEquipmentIdFromLines = useMemo(() => {
    const ids = Array.from(
      new Set(
        (currentRequisition?.lines || [])
          .map(
            (line: any) =>
              line?.equipmentId ||
              line?.subcomponent?.equipmentId ||
              line?.subcomponent?.equipment?.id ||
              null
          )
          .filter((id: any) => id !== null && id !== undefined && !Number.isNaN(Number(id)))
          .map((id: any) => Number(id))
      )
    )
    return ids.length === 1 ? ids[0] : null
  }, [currentRequisition?.lines])

  const requisitionEquipmentId =
    (currentRequisition?.equipmentId as number | null | undefined) ||
    (currentRequisition as any)?.equipment?.id ||
    derivedEquipmentIdFromLines ||
    null
  const hasLockedEquipment = requisitionEquipmentId !== null && requisitionEquipmentId !== undefined
  const lockedEquipmentId =
    requisitionEquipmentId || (formData.equipmentId ? Number(formData.equipmentId) : null)
  const lockedEquipmentNameFromLine = ((currentRequisition?.lines || []) as any[]).find(
    (line) => line?.subcomponent?.equipmentName
  )?.subcomponent?.equipmentName
  const lockedEquipmentName =
    hasLockedEquipment &&
    (equipmentOptions.find((e) => e.id === lockedEquipmentId)?.name ||
      (currentRequisition as any)?.equipment?.name ||
      lockedEquipmentNameFromLine ||
      '')
  const effectiveSubHeadId =
    headerSubHeadId ? Number(headerSubHeadId) : currentRequisition?.inventoryItemSubHeadId ?? null
  const effectiveSubHeadName =
    (effectiveSubHeadId &&
      (inventoryItemSubHeads.find((sh) => sh.id === effectiveSubHeadId)?.name ||
        currentRequisition?.inventoryItemSubHead?.name)) ||
    currentRequisition?.inventoryItemSubHead?.name ||
    ''
  const [editSelectedCompanyId, setEditSelectedCompanyId] = useState<number | null>(null)
  const [editSelectedVesselId, setEditSelectedVesselId] = useState<number | null>(null)
  const editSelectedVesselIdNumber = useMemo(
    () => toPositiveNumber(editSelectedVesselId),
    [editSelectedVesselId]
  )
  const editSelectedCompanyIdNumber = useMemo(
    () => toPositiveNumber(editSelectedCompanyId),
    [editSelectedCompanyId]
  )
  const editRequisitionVesselId = useMemo(
    () => toPositiveNumber(currentRequisition?.vesselId),
    [currentRequisition?.vesselId]
  )
  const editRequisitionCompanyId = useMemo(
    () =>
      toPositiveNumber(
        (currentRequisition as any)?.companyId ??
          (currentRequisition as any)?.companyGroupAdminId ??
          (currentRequisition as any)?.company?.id ??
          (currentRequisition as any)?.vessel?.companyGroupAdminId ??
          (currentRequisition as any)?.vessel?.companyGroupAdmin?.id
      ),
    [currentRequisition]
  )
  const editSelectedHeadId = useMemo(() => toPositiveNumber(headerHeadId), [headerHeadId])

  useEffect(() => {
    if (visible && !isGeneralStores && lockedEquipmentId) {
      loadSubcomponentsForEquipmentLocal(lockedEquipmentId)
      setLineItems((prev) =>
        prev.map((li) => ({
          ...li,
          equipmentId: li.equipmentId || String(lockedEquipmentId),
        }))
      )
    }
  }, [visible, isGeneralStores, lockedEquipmentId])

  useEffect(() => {
    if (visible && requisitionId) {
      loadRequisitionData()
    }
  }, [visible, requisitionId])

  useEffect(() => {
    if (!visible) {
      setEditSelectedCompanyId(null)
      setEditSelectedVesselId(null)
      return
    }

    const vesselIdFromForm = toPositiveNumber(formData.vesselId)
    if (vesselIdFromForm !== editSelectedVesselIdNumber) {
      setEditSelectedVesselId(vesselIdFromForm)
    }
  }, [visible, formData.vesselId, editSelectedVesselIdNumber])

  useEffect(() => {
    if (visible) {
      console.log('ADD ITEM MODAL LOADER TRIGGERED', {
        signature: PROCUREMENT_SCOPE_PATCH_SIGNATURE,
        requisitionId,
        requisitionLoaded: Boolean(currentRequisition?.id),
        isServicesCategory: addModalIsServicesCategory,
        selectedCompanyId: editSelectedCompanyIdNumber,
        selectedVesselId: editSelectedVesselIdNumber,
        requisitionCompanyId: editRequisitionCompanyId,
        requisitionVesselId: editRequisitionVesselId,
      })
    }
    if (!visible || !currentRequisition || !addModalIsServicesCategory) return

    const waitingForSelectedScopeHydration =
      (editRequisitionCompanyId !== null && editSelectedCompanyIdNumber === null) ||
      (editRequisitionVesselId !== null && editSelectedVesselIdNumber === null)
    if (waitingForSelectedScopeHydration) return

    loadHeaderInventoryMeta()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visible,
    currentRequisition?.id,
    currentRequisition?.category?.id,
    editSelectedCompanyIdNumber,
    editSelectedVesselIdNumber,
    editRequisitionVesselId,
    editRequisitionCompanyId,
    addModalIsServicesCategory,
  ])

  useEffect(() => {
    if (!visible || !isGeneralStores || addModalIsServicesCategory) return
    const targetVesselId = currentRequisition?.vesselId
      ? Number(currentRequisition.vesselId)
      : (formData.vesselId ? Number(formData.vesselId) : null)
    const targetSubCategoryId =
      toPositiveNumber((currentRequisition as any)?.inventoryItemSubCategoryId) ??
      (formData.storeSubCategoryId && /^\d+$/.test(formData.storeSubCategoryId)
        ? Number(formData.storeSubCategoryId)
        : null)
    loadGeneralInventoryItems(targetVesselId ?? undefined, targetSubCategoryId ?? undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visible,
    isGeneralStores,
    currentRequisition?.vesselId,
    (currentRequisition as any)?.inventoryItemSubCategoryId,
    formData.vesselId,
    formData.storeSubCategoryId,
  ])

  useEffect(() => {
    if (!isGeneralStores || addModalIsServicesCategory || generalInventoryItems.length === 0) return
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.generalItemId || !item.manualItemName) return item
        const match = generalInventoryItems.find(
          (gi) =>
            gi.itemName &&
            gi.itemName.toLowerCase() === item.manualItemName?.trim().toLowerCase()
        )
        if (!match) return item
        return {
          ...item,
          generalItemId: String(match.masterItemId),
          inventoryItemId:
            match.inventoryItemId != null ? String(match.inventoryItemId) : item.inventoryItemId,
          itemName: match.itemName || item.itemName,
          unitOfMeasurement: item.unitOfMeasurement || match.unitOfMeasurement || '',
        }
      })
    )
  }, [generalInventoryItems, isGeneralStores])

  useEffect(() => {
    if (!visible) return
    setHeaderHeads(inventoryItemHeads)
  }, [visible, inventoryItemHeads, currentRequisition?.category?.id, effectiveRequestFor])

  useEffect(() => {
    if (visible) return
    setGeneralItemSearchTerms({})
    generalInventorySearchRequestSeqRef.current += 1
    setGeneralInventoryItems([])
    setGeneralInventoryPage(0)
    setGeneralInventoryHasMore(true)
    generalInventoryQueryRef.current = {
      vesselId: null,
      inventoryItemSubCategoryId: null,
      search: '',
    }
  }, [visible])

  useEffect(() => {
    if (!visible) return
    if (!addModalIsServicesCategory) {
      setHeaderSubHeads([])
      return
    }
    if (!editSelectedHeadId) {
      setHeaderSubHeads(inventoryItemSubHeads)
      return
    }
    loadHeaderSubHeadsByHead(editSelectedHeadId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visible,
    editSelectedHeadId,
    editSelectedCompanyIdNumber,
    editSelectedVesselIdNumber,
    editRequisitionVesselId,
    editRequisitionCompanyId,
    addModalIsServicesCategory,
  ])

  useEffect(() => {
    if (!addModalIsServicesCategory) return
    setLineItems((prev) =>
      prev.map((li) => ({
        ...li,
        manualItemName: li.manualItemName?.trim() ? li.manualItemName : effectiveSubHeadName || li.manualItemName,
      }))
    )
  }, [addModalIsServicesCategory, effectiveSubHeadName])

  useEffect(() => {
    if (effectiveSubHeadId && errors.serviceSubHead) {
      setErrors((prev) => {
        const next = {...prev}
        delete next.serviceSubHead
        return next
      })
    }
  }, [effectiveSubHeadId, errors.serviceSubHead])

  useEffect(() => {
    if (!visible) return
    const selectedVesselId = formData.vesselId ? Number(formData.vesselId) : null
    if (selectedVesselId) {
      loadEquipmentOptions(selectedVesselId)
    } else {
      setEquipmentOptions([])
    }
  }, [visible, formData.vesselId])

  useEffect(() => {
    if (visible && parts && parts.length > 0) {
      filterPartsByCompany()
    }
  }, [visible, parts, companyGroupAdminId, currentRequisition])

  useEffect(() => {
    if (selectedPartId) {
      loadSubComponentsByPart(selectedPartId)
    }
  }, [selectedPartId])

  const loadHeaderInventoryMeta = async () => {
    try {
      console.log('[Edit Requisition] isServicesCategory:', addModalIsServicesCategory)
      console.log('[Edit Requisition] selectedCompanyId:', editSelectedCompanyIdNumber)
      console.log('[Edit Requisition] selectedVesselId:', editSelectedVesselIdNumber)
      console.log(
        '[Edit Requisition] currentRequisition.companyId:',
        (currentRequisition as any)?.companyId ??
          (currentRequisition as any)?.companyGroupAdminId ??
          null
      )
      console.log(
        '[Edit Requisition] currentRequisition.vesselId:',
        currentRequisition?.vesselId ?? null
      )

      const [headsResult, subHeadsResult] = await Promise.all([
        sharedLoadScopedInventoryHeads({
          currentUser,
          inventoryScopeAll,
          inventoryScopeCompany,
          inventoryScopeVessel,
          isServicesCategory: addModalIsServicesCategory,
          selectedCompanyId: editSelectedCompanyIdNumber,
          selectedVesselId: editSelectedVesselIdNumber,
          requisitionVesselId: editRequisitionVesselId,
          requisitionCompanyId: editRequisitionCompanyId,
          requisitionVesselName:
            (currentRequisition as any)?.vessel?.fleet_name ??
            (currentRequisition as any)?.vessel?.name ??
            null,
          requisitionCompanyName:
            (currentRequisition as any)?.companyName ??
            (currentRequisition as any)?.vessel?.companyGroupAdmin?.name ??
            null,
          debugContext: 'Edit Requisition',
        }),
        sharedLoadScopedInventorySubHeads({
          currentUser,
          inventoryScopeAll,
          inventoryScopeCompany,
          inventoryScopeVessel,
          isServicesCategory: addModalIsServicesCategory,
          selectedCompanyId: editSelectedCompanyIdNumber,
          selectedVesselId: editSelectedVesselIdNumber,
          requisitionVesselId: editRequisitionVesselId,
          requisitionCompanyId: editRequisitionCompanyId,
          requisitionVesselName:
            (currentRequisition as any)?.vessel?.fleet_name ??
            (currentRequisition as any)?.vessel?.name ??
            null,
          requisitionCompanyName:
            (currentRequisition as any)?.companyName ??
            (currentRequisition as any)?.vessel?.companyGroupAdmin?.name ??
            null,
          debugContext: 'Edit Requisition',
        }),
      ])

      console.log('[Edit Requisition] normalized crew scope:', headsResult.normalizedCrewScope)
      console.log('[Edit Requisition] raw heads response:', headsResult.rawRows)
      console.log('[Edit Requisition] filtered heads response:', headsResult.filteredRows)
      console.log('[Edit Requisition] raw subheads response:', subHeadsResult.rawRows)
      console.log('[Edit Requisition] filtered subheads response:', subHeadsResult.filteredRows)
      console.log('ADD ITEM RESOLVED SCOPE', {
        companyId: headsResult.apiScope.companyId ?? null,
        vesselId: headsResult.apiScope.vesselId ?? null,
        headId: headsResult.apiScope.headId ?? null,
        source: headsResult.scopeSource,
        flow: 'edit-modal-heads',
      })
      console.log('ADD ITEM RESOLVED SCOPE', {
        companyId: subHeadsResult.apiScope.companyId ?? null,
        vesselId: subHeadsResult.apiScope.vesselId ?? null,
        headId: subHeadsResult.apiScope.headId ?? null,
        source: subHeadsResult.scopeSource,
        flow: 'edit-modal-subheads',
      })

      setInventoryItemHeads(sharedDedupeInventoryHeadRows(headsResult.rows || []))
      setInventoryItemSubHeads(sharedDedupeInventorySubHeadRows(subHeadsResult.rows || []))
      setHeaderHeads(sharedDedupeInventoryHeadRows(headsResult.rows || []))
      setHeaderSubHeads(sharedDedupeInventorySubHeadRows(subHeadsResult.rows || []))
    } catch (error) {
      console.error('Error loading inventory head/subhead meta:', error)
      setInventoryItemHeads([])
      setInventoryItemSubHeads([])
      setHeaderHeads([])
      setHeaderSubHeads([])
    }
  }

  const loadHeaderSubHeadsByHead = async (headId: number) => {
    if (!headId) {
      setHeaderSubHeads(sharedDedupeInventorySubHeadRows(inventoryItemSubHeads || []))
      return
    }
    try {
      console.log('[Edit Requisition] isServicesCategory:', addModalIsServicesCategory)
      console.log('[Edit Requisition] selectedCompanyId:', editSelectedCompanyIdNumber)
      console.log('[Edit Requisition] selectedVesselId:', editSelectedVesselIdNumber)
      console.log(
        '[Edit Requisition] currentRequisition.companyId:',
        (currentRequisition as any)?.companyId ??
          (currentRequisition as any)?.companyGroupAdminId ??
          null
      )
      console.log(
        '[Edit Requisition] currentRequisition.vesselId:',
        currentRequisition?.vesselId ?? null
      )

      const subHeadsResult = await sharedLoadScopedInventorySubHeads({
        currentUser,
        inventoryScopeAll,
        inventoryScopeCompany,
        inventoryScopeVessel,
        isServicesCategory: addModalIsServicesCategory,
        selectedCompanyId: editSelectedCompanyIdNumber,
        selectedVesselId: editSelectedVesselIdNumber,
        requisitionVesselId: editRequisitionVesselId,
        requisitionCompanyId: editRequisitionCompanyId,
        requisitionVesselName:
          (currentRequisition as any)?.vessel?.fleet_name ??
          (currentRequisition as any)?.vessel?.name ??
          null,
        requisitionCompanyName:
          (currentRequisition as any)?.companyName ??
          (currentRequisition as any)?.vessel?.companyGroupAdmin?.name ??
          null,
        debugContext: 'Edit Requisition',
        selectedHeadId: headId,
      })

      console.log('[Edit Requisition] normalized crew scope:', subHeadsResult.normalizedCrewScope)
      console.log('[Edit Requisition] raw subheads response:', subHeadsResult.rawRows)
      console.log('[Edit Requisition] filtered subheads response:', subHeadsResult.filteredRows)
      console.log('ADD ITEM RESOLVED SCOPE', {
        companyId: subHeadsResult.apiScope.companyId ?? null,
        vesselId: subHeadsResult.apiScope.vesselId ?? null,
        headId: subHeadsResult.apiScope.headId ?? null,
        source: subHeadsResult.scopeSource,
        flow: 'edit-modal-subheads-by-head',
      })

      setHeaderSubHeads(sharedDedupeInventorySubHeadRows(subHeadsResult.rows || []))
    } catch (error) {
      console.error('Error loading subheads by head:', error)
      setHeaderSubHeads([])
    }
  }

  const loadGeneralInventoryItems = async (
    vesselId?: number | null,
    inventoryItemSubCategoryId?: number | null,
    search?: string,
    options?: {page?: number; append?: boolean}
  ) => {
    const page = options?.page ?? 0
    const append = options?.append ?? false
    const normalizedSearch = search?.trim() || ''
    const requestSeq = ++generalInventorySearchRequestSeqRef.current
    generalInventoryQueryRef.current = {
      vesselId: vesselId ?? null,
      inventoryItemSubCategoryId: inventoryItemSubCategoryId ?? null,
      search: normalizedSearch,
    }
    setIsLoadingGeneralInventoryItems(true)
    try {
      if (!vesselId) {
        setGeneralInventoryItems([])
        setGeneralInventoryPage(0)
        setGeneralInventoryHasMore(false)
        return
      }
      const response = await getVesselInventoryProjection({
        vesselId,
        itemFor: 'GENERAL_STORES',
        search: normalizedSearch || undefined,
        page,
        size: 20,
      })
      const categoryId = inventoryItemSubCategoryId ?? null
      const items = (response.content || []).filter((item) =>
        categoryId == null ? true : item.inventoryItemSubCategoryId === categoryId
      )
      if (requestSeq !== generalInventorySearchRequestSeqRef.current) {
        return
      }
      setGeneralInventoryItems((prev) => {
        const merged = append ? [...prev, ...items] : items
        const seen = new Set<string>()
        return merged.filter((item) => {
          const key = String(item.masterItemId)
          if (seen.has(key)) {
            return false
          }
          seen.add(key)
          return true
        })
      })
      setGeneralInventoryPage((response.number ?? page) + 1)
      setGeneralInventoryHasMore(!(response.last ?? true))
    } catch (error) {
      if (requestSeq !== generalInventorySearchRequestSeqRef.current) {
        return
      }
      console.error('Error loading general inventory items:', error)
      if (!append) {
        setGeneralInventoryItems([])
      }
      setGeneralInventoryHasMore(false)
    } finally {
      if (requestSeq !== generalInventorySearchRequestSeqRef.current) {
        return
      }
      setIsLoadingGeneralInventoryItems(false)
    }
  }

  const ensureGeneralItemInInventory = async (
    lineItem: LineItemForm,
    options?: {silent?: boolean; inventoryItemSubCategoryId?: number | null; inventoryItemSubHeadId?: number | null}
  ) => {
    if (!isGeneralStores) return
    if (lineItem.generalItemId && lineItem.inventoryItemId) {
      return
    }

    const vesselId =
      currentRequisition?.vesselId ||
      (formData.vesselId ? Number(formData.vesselId) : null)
    if (!vesselId || !lineItem.generalItemId) {
      return
    }
    try {
      const resolved = await getOrCreateMasterInventoryInstance(
        Number(vesselId),
        Number(lineItem.generalItemId)
      )
      setLineItems((prev) =>
        prev.map((item) =>
          item.id === lineItem.id
            ? {
                ...item,
                inventoryItemId: resolved.id != null ? String(resolved.id) : item.inventoryItemId,
                manualItemName: resolved.manualItemName || item.manualItemName,
                itemName: resolved.manualItemName || item.itemName,
                unitOfMeasurement: resolved.unitOfMeasurement || item.unitOfMeasurement,
              }
            : item
        )
      )
      setGeneralInventoryItems((prev) =>
        prev.map((item) =>
          String(item.masterItemId) === String(lineItem.generalItemId)
            ? {
                ...item,
                inventoryItemId: resolved.id,
                instanceExists: true,
              }
            : item
        )
      )
    } catch (error) {
      console.error('Error creating general inventory item:', error)
      if (!options?.silent) {
        toast.error('Failed to resolve vessel inventory instance')
      }
    }
  }

  const syncGeneralInventoryItemsAfterSave = async (
    savedRequisition?: RequisitionMutationResponse | null
  ) => {
    if (!isGeneralStores || addModalIsServicesCategory) return
    const subCategoryId =
      toPositiveNumber((savedRequisition as any)?.inventoryItemSubCategoryId) ??
      toPositiveNumber((currentRequisition as any)?.inventoryItemSubCategoryId) ??
      (formData.storeSubCategoryId && /^\d+$/.test(formData.storeSubCategoryId)
        ? Number(formData.storeSubCategoryId)
        : null)
    const subHeadId =
      toPositiveNumber((savedRequisition as any)?.inventoryItemSubHeadId) ??
      (headerSubHeadId ? Number(headerSubHeadId) : currentRequisition?.inventoryItemSubHeadId || null)

    for (const item of lineItems) {
      await ensureGeneralItemInInventory(item, {
        silent: true,
        inventoryItemSubCategoryId: subCategoryId,
        inventoryItemSubHeadId: subHeadId,
      })
    }
  }

  const loadAllSubComponents = async () => {
    try {
      console.log('Loading all sub components...')
      const subComponents = dedupeSubComponentRows((await getSubComponents()) || [])
      console.log('All sub components loaded:', subComponents)
      setAllSubComponents(subComponents)
      setAvailableSubComponents(subComponents)
    } catch (error) {
      console.error('Error loading all sub components:', error)
      setAllSubComponents([])
    }
  }

  const loadEquipmentOptions = async (vesselId?: number | null) => {
    try {
      const data = await getRequisitionFormEquipment(vesselId ?? undefined)
      setEquipmentOptions(
        data.map((item) => ({
          id: item.id,
          name: item.name,
          code: item.code ?? undefined,
          vesselId: vesselId ?? null,
          vesselName: null,
          companyId: null,
          companyName: null,
        }))
      )
    } catch (error) {
      console.error('Error loading equipment for requisition add item:', error)
      setEquipmentOptions([])
    }
  }

  const loadSubcomponentsForEquipmentLocal = async (equipmentId: number) => {
    if (subcomponentOptionsMap[equipmentId]) return
    setIsLoadingSubComponents(true)
    try {
      const data = dedupeSubComponentRows((await getSubComponentsByEquipment(equipmentId, false)) || [])
      setSubcomponentOptionsMap((prev) => ({...prev, [equipmentId]: data}))
    } catch (error) {
      console.error('Error loading subcomponents for equipment:', error)
    } finally {
      setIsLoadingSubComponents(false)
    }
  }

  const loadPartsForSubcomponentLocal = async (subcomponentId: number) => {
    try {
      const data = dedupePartRows((await getPartsBySubcomponent(subcomponentId)) || [])
      setPartOptionsMap((prev) => ({...prev, [subcomponentId]: data}))
    } catch (error) {
      console.error('Error loading parts for subcomponent:', error)
    }
  }

  const loadPartsForSubcomponentGroupLocal = async (group: SubcomponentOptionGroup) => {
    const representativeId = toPositiveNumber(group?.representativeId)
    if (!representativeId) return

    const memberIds = Array.from(
      new Set(
        (group?.memberIds || [])
          .map((id) => toPositiveNumber(id))
          .filter((id): id is number => Boolean(id))
      )
    )

    if (memberIds.length === 0) {
      await loadPartsForSubcomponentLocal(representativeId)
      return
    }

    try {
      const allParts = await Promise.all(
        memberIds.map(async (id) => {
          try {
            return (await getPartsBySubcomponent(id)) || []
          } catch (error) {
            console.error('Error loading parts for grouped subcomponent member:', id, error)
            return []
          }
        })
      )
      const mergedParts = dedupePartRows(allParts.flat())
      setPartOptionsMap((prev) => {
        const next = {...prev}
        memberIds.forEach((id) => {
          next[id] = mergedParts
        })
        next[representativeId] = mergedParts
        return next
      })
    } catch (error) {
      console.error('Error loading grouped parts for subcomponent selection:', error)
      await loadPartsForSubcomponentLocal(representativeId)
    }
  }

  const filterPartsForRequisition = async () => {
  try {
    // If parts are still needed elsewhere, do simple safe fallback logic.
    // Do not use old subcatalogue flow anymore.
    setFilteredParts(dedupePartRows(parts || []))
  } catch (error) {
    console.error('Error filtering parts:', error)
    setFilteredParts(dedupePartRows(parts || []))
  }
}

  const filterPartsByCompany = async () => {
    // Resolve company directly from requisition / selected vessel / current user fallback.
    const requisitionCompanyId =
      toPositiveNumber(
        (currentRequisition as any)?.companyId ??
          (currentRequisition as any)?.companyGroupAdminId ??
          (currentRequisition as any)?.company?.id ??
          (currentRequisition as any)?.vessel?.companyGroupAdminId ??
          (currentRequisition as any)?.vessel?.companyGroupAdmin?.id
      ) ??
      toPositiveNumber(companyGroupAdminId) ??
      toPositiveNumber(
        (currentUser as any)?.vessel?.companyGroupAdminId ??
          (currentUser as any)?.vessel?.companyGroupAdmin?.id ??
          (currentUser as any)?.companyGroupAdminId ??
          (currentUser as any)?.companyGroupAdmin?.id
      )

    console.log('Filtering by company ID:', requisitionCompanyId)

    // Filter parts based on company
    if (requisitionCompanyId) {
      try {
        // Get all subcatalogues for the company
        const allSubCatalogues = await getAllSubCatalogues()
        console.log('All subcatalogues:', allSubCatalogues)

        // Filter subcatalogues by company
        const companySubCatalogues = allSubCatalogues.filter((sc) => {
          const scCgaId = sc.cgaid?.id || sc.cgaid
          console.log(
            'Subcatalogue:',
            sc.name,
            'cgaid:',
            scCgaId,
            'matches:',
            scCgaId === requisitionCompanyId
          )
          return scCgaId === requisitionCompanyId
        })
        console.log('Company subcatalogues:', companySubCatalogues.length, companySubCatalogues)

        // Get subcatalogue IDs
        const companySubCatalogueIds = companySubCatalogues.map((sc) => sc.id)
        console.log('Company subcatalogue IDs:', companySubCatalogueIds)

        // Filter parts that belong to company subcatalogues
        const companyParts = parts.filter((part) => {
          if (part.subcatalogue) {
            const belongsToCompany = companySubCatalogueIds.includes(part.subcatalogue.id)
            console.log(
              'Part:',
              part.name,
              'subcatalogue:',
              part.subcatalogue.id,
              'belongs to company:',
              belongsToCompany
            )
            return belongsToCompany
          }
          return false
        })

        console.log('Filtered parts for company:', companyParts.length, companyParts)
        setFilteredParts(dedupePartRows(companyParts))
      } catch (error) {
        console.error('Error filtering parts by company:', error)
        // Fallback to showing all parts
        console.log('Fallback: showing all parts')
        setFilteredParts(dedupePartRows(parts))
      }
    } else {
      // No company context on the requisition: do not assume role — show full part list for picker UX.
      console.log('No company ID, showing all parts:', parts.length)
      setFilteredParts(dedupePartRows(parts))
    }
  }

  const loadRequisitionData = async () => {
    setIsLoadingRequisition(true)
    try {
      console.log('Loading requisition data for ID:', requisitionId)
      const requisition = await getRequisitionById(requisitionId)
      console.log('Requisition loaded:', requisition)

      const requisitionSelectedCompanyId = toPositiveNumber(
        (requisition as any)?.companyId ??
          (requisition as any)?.companyGroupAdminId ??
          (requisition as any)?.company?.id ??
          (requisition as any)?.vessel?.companyGroupAdminId ??
          (requisition as any)?.vessel?.companyGroupAdmin?.id
      )
      const requisitionSelectedVesselId = toPositiveNumber(requisition?.vesselId)

      setEditSelectedCompanyId(requisitionSelectedCompanyId)
      setEditSelectedVesselId(requisitionSelectedVesselId)
      setCurrentRequisition(requisition)
      setEditRegionId(requisition.regionId ?? null)
      setEditRegionName(requisition.regionName ?? null)
      setEditRegionTouched(false)

      const derivedEquipmentIdsOnLoad = Array.from(
        new Set(
          (requisition.lines || [])
            .map(
              (line: any) =>
                line?.equipmentId ||
                line?.subcomponent?.equipmentId ||
                line?.subcomponent?.equipment?.id ||
                null
            )
            .filter((id: any) => id !== null && id !== undefined && !Number.isNaN(Number(id)))
            .map((id: any) => Number(id))
        )
      )
      const derivedEquipmentOnLoad =
        derivedEquipmentIdsOnLoad.length === 1 ? derivedEquipmentIdsOnLoad[0] : null

      setFormData({
        title: requisition.title || '',
        description: requisition.description || '',
        equipmentLink: requisition.equipmentLink || '',
        vesselId: requisition.vesselId ? String(requisition.vesselId) : '',
        equipmentId:
          requisition.equipmentId || derivedEquipmentOnLoad
            ? String(requisition.equipmentId || derivedEquipmentOnLoad)
            : '',
        categoryId: requisition.category?.id ? String(requisition.category.id) : '',
      })

      setHeaderHeadId(
        requisition.inventoryItemHeadId ? String(requisition.inventoryItemHeadId) : ''
      )
      setHeaderSubHeadId(
        requisition.inventoryItemSubHeadId ? String(requisition.inventoryItemSubHeadId) : ''
      )

      const isServicesRequisition = String(requisition?.category?.name || '').toUpperCase().includes('SERVICE')
      const requestFor =
        (isServicesRequisition ? 'TECHNICAL' : requisition.requestFor) ||
        (requisition.lines?.some((l) => l.manualItemName) || requisition.manualItemName
          ? 'GENERAL_STORES'
          : 'TECHNICAL')

      setFormData((prev) => ({
        ...prev,
        itemMode: requestFor === 'GENERAL_STORES' ? 'GENERAL' : 'TECHNICAL',
      }))

      if (requisition.vesselId) {
        loadEquipmentOptions(requisition.vesselId)
      }

      const existingLines = requisition.lines || []

      if (isServicesRequisition) {
        setRequisitionPartId(null)
        setSelectedPartId(null)

        if (existingLines.length > 0) {
          setLineItems(
            existingLines.map((l: any, idx) => ({
              id: `${l.id || Date.now()}_${idx}`,
              persistedLineId: l.id ?? undefined,
              equipmentId: '',
              subcomponentId: '',
              partId: null,
              generalItemId: '',
              manualItemName:
                l?.manualItemName ||
                l?.notes ||
                l?.remarks ||
                l?.inventoryItemSubHead?.name ||
                requisition.inventoryItemSubHead?.name ||
                '',
              itemName:
                l?.manualItemName ||
                l?.notes ||
                l?.remarks ||
                l?.inventoryItemSubHead?.name ||
                requisition.inventoryItemSubHead?.name ||
                '',
              quantity: l?.quantity != null ? String(l.quantity) : '',
              unitOfMeasurement: l?.unitOfMeasurement || '',
              estimatedUnitPrice:
                l?.estimatedUnitPrice !== null && l?.estimatedUnitPrice !== undefined
                  ? String(l.estimatedUnitPrice)
                  : '',
              notes: l?.notes || l?.remarks || '',
              existingAttachments: (l as any)?.attachments || [],
              equipmentLink: '',
              customSubcomponentName: '',
              serviceHeadId: String(
                l?.inventoryItemHeadId ||
                l?.inventoryItemHead?.id ||
                requisition.inventoryItemHeadId ||
                ''
              ),
              serviceSubHeadId: String(
                l?.inventoryItemSubHeadId ||
                l?.inventoryItemSubHead?.id ||
                requisition.inventoryItemSubHeadId ||
                ''
              ),
            }))
          )
        } else {
          setLineItems([
            {
              id: Date.now().toString(),
              equipmentId: '',
              subcomponentId: '',
              partId: null,
              generalItemId: '',
              manualItemName: requisition.manualItemName || requisition.inventoryItemSubHead?.name || '',
              itemName: requisition.manualItemName || requisition.inventoryItemSubHead?.name || '',
              quantity: '',
          unitOfMeasurement: '',
              estimatedUnitPrice: '',
              notes: '',
              equipmentLink: '',
              customSubcomponentName: '',
              serviceHeadId: requisition.inventoryItemHeadId ? String(requisition.inventoryItemHeadId) : '',
              serviceSubHeadId: requisition.inventoryItemSubHeadId ? String(requisition.inventoryItemSubHeadId) : '',
            },
          ])
        }
        return
      }

      if (requestFor === 'GENERAL_STORES') {
        if (existingLines.length > 0) {
          setLineItems(
            existingLines.map((l, idx) => ({
              id: `${l.id || Date.now()}_${idx}`,
              persistedLineId: l.id ?? undefined,
              equipmentId: '',
              subcomponentId: '',
              partId: null,
              generalItemId:
                (l as any).masterItemId != null ? String((l as any).masterItemId) : '',
              inventoryItemId:
                (l as any).inventoryItemId != null ? String((l as any).inventoryItemId) : '',
              manualItemName: l.manualItemName || '',
              itemName: (l as any).itemName || l.manualItemName || '',
              quantity: l.quantity ? String(l.quantity) : '',
              unitOfMeasurement: l.unitOfMeasurement || '',
              estimatedUnitPrice:
                l.estimatedUnitPrice !== null && l.estimatedUnitPrice !== undefined
                  ? String(l.estimatedUnitPrice)
                  : '',
              notes: l.notes || '',
              existingAttachments: (l as any)?.attachments || [],
              customSubcomponentName: '',
            }))
          )
        } else {
          setLineItems([
            {
              id: Date.now().toString(),
              equipmentId: '',
              subcomponentId: '',
              partId: null,
      generalItemId: '',
      manualItemName: '',
      quantity: '',
          unitOfMeasurement: '',
      estimatedUnitPrice: '',
    notes: '',
    equipmentLink: '',
    customSubcomponentName: '',
    },
  ])
        }
        return
      }

      if (existingLines.length > 0) {
        const mappedLineItems: LineItemForm[] = existingLines.map((l: any, idx) => {
          const lineEquipmentId =
            l?.equipmentId ||
            l?.subcomponent?.equipmentId ||
            l?.subcomponent?.equipment?.id ||
            requisition.equipmentId ||
            derivedEquipmentOnLoad
          const lineSubcomponentId = l?.subcomponentId || l?.subcomponent?.id || ''
          const linePartId = l?.partId || l?.part?.id || null
          return {
            id: `${l.id || Date.now()}_${idx}`,
            persistedLineId: l.id ?? undefined,
            equipmentId: lineEquipmentId ? String(lineEquipmentId) : '',
            subcomponentId: lineSubcomponentId ? String(lineSubcomponentId) : '',
            partId: linePartId ? Number(linePartId) : null,
            generalItemId: '',
            manualItemName: l?.manualItemName || '',
            itemName: l?.itemName || l?.manualItemName || '',
            quantity: l?.quantity != null ? String(l.quantity) : '',
            unitOfMeasurement: l?.unitOfMeasurement || '',
            estimatedUnitPrice:
              l?.estimatedUnitPrice !== null && l?.estimatedUnitPrice !== undefined
                ? String(l.estimatedUnitPrice)
                : '',
            notes: l?.notes || l?.remarks || '',
            existingAttachments: (l as any)?.attachments || [],
            subcomponentClubbing: l?.subcomponentClubbing || '',
            equipmentLink: '',
            customSubcomponentName: '',
          }
        })

        setLineItems(mappedLineItems)

        const firstPartId =
          mappedLineItems.find((line) => line.partId != null)?.partId ||
          requisition.partId ||
          null
        if (firstPartId) {
          setRequisitionPartId(firstPartId)
          setSelectedPartId(firstPartId)
        } else {
          setRequisitionPartId(null)
          setSelectedPartId(null)
        }

        const equipmentIds = Array.from(
          new Set(
            mappedLineItems
              .map((line) => Number(line.equipmentId))
              .filter((eqId) => !Number.isNaN(eqId) && eqId > 0)
          )
        )
        await Promise.all(equipmentIds.map((eqId) => loadSubcomponentsForEquipmentLocal(eqId)))

        const subcomponentIds = Array.from(
          new Set(
            mappedLineItems
              .map((line) => Number(line.subcomponentId))
              .filter((subId) => !Number.isNaN(subId) && subId > 0)
          )
        )
        await Promise.all(subcomponentIds.map((subId) => loadPartsForSubcomponentLocal(subId)))
      } else {
        console.warn('No part ID found in requisition')
        console.warn('Requisition data:', requisition)
        setSelectedPartId(requisition.partId || null)
        setRequisitionPartId(requisition.partId || null)
        setLineItems([
          {
            id: Date.now().toString(),
            equipmentId:
              requisition.equipmentId || derivedEquipmentOnLoad
                ? String(requisition.equipmentId || derivedEquipmentOnLoad)
                : '',
            subcomponentId: '',
            partId: requisition.partId || null,
            manualItemName: '',
            quantity: requisition.quantity != null ? String(requisition.quantity) : '',
          unitOfMeasurement: '',
            estimatedUnitPrice: '',
            notes: '',
          },
        ])
      }
    } catch (error: any) {
      console.error('Error loading requisition:', error)
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to load requisition data'
      toast.error('Failed to load requisition data')
      onClose()
    } finally {
      setIsLoadingRequisition(false)
    }
  }

  const loadSubComponentsByPart = async (partId: number) => {
    setIsLoadingSubComponents(true)
    try {
      console.log('Loading sub components for part ID:', partId)

      // Use already loaded subcomponents if available
      let subComponentsToFilter = allSubComponents
      if (allSubComponents.length === 0) {
        subComponentsToFilter = dedupeSubComponentRows((await getSubComponents()) || [])
        setAllSubComponents(subComponentsToFilter)
      }

      console.log('All sub components received:', subComponentsToFilter)

      const filtered = partId
        ? subComponentsToFilter.filter((sc) => sc.part?.id === partId)
        : subComponentsToFilter
      console.log('Filtered sub components for part', partId, ':', filtered)

      // Get already added subcomponent IDs from the requisition's lines array
      const addedSubComponentIds =
        currentRequisition?.lines?.map((line) => line.subcomponentId) || []
      console.log('Already added subcomponent IDs:', addedSubComponentIds)

      // Filter out subcomponents that are already added
      const availableForAdding = dedupeSubComponentRows(
        filtered.filter((sc) => !addedSubComponentIds.includes(sc.id))
      )
      console.log('Available subcomponents (excluding already added):', availableForAdding.length)

      setAvailableSubComponents(availableForAdding)
    } catch (error) {
      console.error('Error loading sub components:', error)
      setAvailableSubComponents([])
    } finally {
      setIsLoadingSubComponents(false)
    }
  }

  const handlePartChange = (partId: string) => {
    // Handle "Other" option for custom part
    if (partId === 'other') {
      setSelectedPartId(null)
      setRequisitionPartId(null)
      setAvailableSubComponents([])
      // Clear part-related errors when selecting "Other"
      setErrors((prev) => {
        const newErrors = {...prev}
        delete newErrors.partId
        return newErrors
      })
      // Clear auto-filled fields when selecting "Other"
      setLineItems((prev) =>
        prev.map((item) => ({
          ...item,
          subcomponentId: '',
          customSubcomponentName: '',
          partId: null,
          make: '',
          model: '',
          subcomponentClubbing: '',
        }))
      )
      return
    }

    const partIdNum = parseInt(partId)
    setSelectedPartId(partIdNum)
    setRequisitionPartId(partIdNum)

    // Find the selected part to get its details
    const selectedPart = parts.find((p) => p.id === partIdNum)
    console.log('Selected part:', selectedPart)

    // Auto-fill Make/Model from the selected part
    if (selectedPart) {
      setLineItems((prev) =>
        prev.map((item) => ({
          ...item,
          subcomponentId: '', // Clear subcomponent selection
          customSubcomponentName: '', // Clear custom subcomponent name
          partId: partIdNum,
          make: selectedPart.manufacturer || '', // Use manufacturer as Make
          model: selectedPart.partNo || '', // Use partNo as Model
          subcomponentClubbing: '',
          unitOfMeasurement: '', // Require a current library value after part changes
        }))
      )
      console.log('Auto-filled fields:', {
        make: selectedPart.manufacturer,
        model: selectedPart.partNo,
      })
    } else {
      // Clear subcomponent selections when part changes
      setLineItems((prev) =>
        prev.map((item) => ({
          ...item,
          subcomponentId: '',
          customSubcomponentName: '',
          partId: partIdNum,
          unitOfMeasurement: '', // Require a current library value after part changes
        }))
      )
    }

    // Load subcomponents for the new part
    if (partIdNum) {
      loadSubComponentsByPart(partIdNum)
    } else {
      setAvailableSubComponents([])
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files)
      setSelectedFiles((prev) => [...prev, ...filesArray])
      console.log(
        'Files added:',
        filesArray.length,
        'Total files:',
        selectedFiles.length + filesArray.length
      )
    }
  }

  const handleLineAttachmentChange = (lineItemId: string, files: FileList | null) => {
    if (!files) {
      return
    }
    const filesArray = Array.from(files)
    if (filesArray.length === 0) {
      return
    }
    setLineAttachmentFiles((prev) => ({
      ...prev,
      [lineItemId]: [...(prev[lineItemId] || []), ...filesArray],
    }))
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const removeLineAttachment = (lineItemId: string, fileIndex: number) => {
    setLineAttachmentFiles((prev) => ({
      ...prev,
      [lineItemId]: (prev[lineItemId] || []).filter((_, idx) => idx !== fileIndex),
    }))
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const findSubcomponentById = (id: number) => {
    const pool = [
      ...availableSubComponents,
      ...allSubComponents,
      ...Object.values(subcomponentOptionsMap).flat(),
    ]
    return pool.find((sc) => sc.id === id)
  }

  const resolvePartIdForLine = (lineItem: LineItemForm): number | null => {
    if (lineItem.partId) return lineItem.partId
    const subIdNum = parseInt(lineItem.subcomponentId)
    if (!isNaN(subIdNum)) {
      const sub = findSubcomponentById(subIdNum)
    if (sub?.part?.id) return sub.part.id
  }
  if (requisitionPartId) return requisitionPartId
  if (selectedPartId) return selectedPartId
  return null
}

  const resolvePartForLine = (lineItem: LineItemForm) => {
    const pid = resolvePartIdForLine(lineItem)
    if (!pid) return null
    return (
      filteredParts.find((p) => p.id === pid) ||
      parts.find((p) => p.id === pid) ||
      allSubComponents.find((sc) => sc.part?.id === pid)?.part ||
      null
    )
  }

  const formatPartLabel = (p: any) => {
    if (!p) return ''
    const name = p.name || ''
    const code = p.code ? ` - ${p.code}` : ''
    const manufacturer = p && 'manufacturer' in p && p.manufacturer ? ` (${p.manufacturer})` : ''
    return `${name}${code}${manufacturer}`
  }

  const formatGeneralItemLabel = (item: VesselInventoryProjection) => {
    if (!item) return ''
    const itemName = item.itemName?.trim() || ''
    const code = item.itemCode ? ` | Code: ${item.itemCode}` : ''
    const rob =
      item.instanceExists && item.currentQty != null
        ? ` | ROB: ${item.currentQty}`
        : ' | Not yet added to vessel inventory'
    return `${itemName}${code}${rob}`.trim()
  }

  const resolveGeneralItemDisplayValue = (lineItem: LineItemForm) => {
    if (lineItem.generalItemId === 'other') {
      return '+ Other (Item not in list)'
    }
    if (lineItem.generalItemId) {
      const matchedItem =
        generalInventoryItems.find(
          (item) => String(item.masterItemId) === String(lineItem.generalItemId)
        ) ||
        filteredParts.find((item: any) => String(item.id) === String(lineItem.generalItemId))

      if (matchedItem) {
        return formatGeneralItemLabel(matchedItem as VesselInventoryProjection)
      }

      return lineItem.itemName || lineItem.manualItemName || `Item #${lineItem.generalItemId}`
    }

    return lineItem.manualItemName || lineItem.itemName || ''
  }

  const getGeneralInventoryLookupOptionsForLine = (lineItem: LineItemForm) => {
    const taken = takenGeneralItemIdsForLine(lineItem.id)
    const options: {id: string | number; label: string}[] = generalInventoryItems
      .filter((item) => {
        return (
          !taken.has(String(item.masterItemId)) ||
          String(lineItem.generalItemId) === String(item.masterItemId)
        )
      })
      .map((item) => ({
        id: item.masterItemId,
        label: formatGeneralItemLabel(item) || `Item #${item.masterItemId}`,
      }))

    if (
      lineItem.generalItemId &&
      lineItem.generalItemId !== 'other' &&
      !options.some((option) => String(option.id) === String(lineItem.generalItemId))
    ) {
      options.unshift({
        id: Number(lineItem.generalItemId),
        label:
          lineItem.itemName ||
          lineItem.manualItemName ||
          `Item #${lineItem.generalItemId}`,
      })
    }

    const currentSearch = generalItemSearchTerms[lineItem.id]?.trim()
    const otherLabel =
      currentSearch &&
      currentSearch.toLowerCase() !== 'other' &&
      currentSearch.toLowerCase() !== 'others'
        ? `+ Other (Add custom item: "${currentSearch}")`
        : '+ Other (Item not in list)'

    // Always provide the option to add a custom item not in master catalogue
    options.push({
      id: 'other',
      label: otherLabel,
    })

    return options
  }

  const searchGeneralInventoryOptions = useCallback(
    (lineItemId: string, query: string) => {
      setGeneralItemSearchTerms((prev) => ({
        ...prev,
        [lineItemId]: query,
      }))
      const vesselId =
        currentRequisition?.vesselId || (formData.vesselId ? Number(formData.vesselId) : null)
      if (!vesselId || addModalIsServicesCategory || !isGeneralStores) {
        return
      }
      window.clearTimeout(generalItemSearchTimeoutRef.current[lineItemId])
      generalItemSearchTimeoutRef.current[lineItemId] = window.setTimeout(() => {
        loadGeneralInventoryItems(
          vesselId,
          toPositiveNumber((currentRequisition as any)?.inventoryItemSubCategoryId) ??
            (formData.storeSubCategoryId && /^\d+$/.test(formData.storeSubCategoryId)
              ? Number(formData.storeSubCategoryId)
              : null),
          query.trim() || undefined,
          {page: 0, append: false}
        )
      }, 300)
    },
    [
      addModalIsServicesCategory,
      currentRequisition,
      formData.storeSubCategoryId,
      formData.vesselId,
      isGeneralStores,
    ]
  )

  const loadMoreGeneralInventoryOptions = useCallback(
    (lineItemId: string) => {
      if (isLoadingGeneralInventoryItems || !generalInventoryHasMore) {
        return
      }
      const vesselId =
        currentRequisition?.vesselId || (formData.vesselId ? Number(formData.vesselId) : null)
      if (!vesselId || addModalIsServicesCategory || !isGeneralStores) {
        return
      }
      const query = generalItemSearchTerms[lineItemId] ?? generalInventoryQueryRef.current.search
      loadGeneralInventoryItems(
        vesselId,
        toPositiveNumber((currentRequisition as any)?.inventoryItemSubCategoryId) ??
          (formData.storeSubCategoryId && /^\d+$/.test(formData.storeSubCategoryId)
            ? Number(formData.storeSubCategoryId)
            : null),
        query.trim() || undefined,
        {page: generalInventoryPage, append: true}
      )
    },
    [
      addModalIsServicesCategory,
      currentRequisition,
      formData.storeSubCategoryId,
      formData.vesselId,
      generalInventoryHasMore,
      generalInventoryPage,
      generalItemSearchTerms,
      isGeneralStores,
      isLoadingGeneralInventoryItems,
    ]
  )

  const handleGeneralItemSelection = async (lineItemId: string, value: string) => {
    handleLineItemChange(lineItemId, 'generalItemId', value)
    if (!value) {
      return
    }
    if (value === 'other') {
      const currentSearch = generalItemSearchTerms[lineItemId]?.trim() || ''
      const isGeneric =
        !currentSearch ||
        currentSearch.toLowerCase() === 'other' ||
        currentSearch.toLowerCase() === 'others'
      setLineItems((prev) =>
        prev.map((item) =>
          item.id === lineItemId
            ? {
                ...item,
                generalItemId: 'other',
                inventoryItemId: '',
                manualItemName:
                  item.manualItemName || (!isGeneric ? currentSearch : ''),
                itemName: item.itemName || (!isGeneric ? currentSearch : ''),
              }
            : item
        )
      )
      return
    }
    const line = lineItems.find((item) => item.id === lineItemId)
    if (!line) {
      return
    }
    const selected = generalInventoryItems.find(
      (item) => String(item.masterItemId) === String(value)
    )
    if (!selected) {
      return
    }
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? {
              ...item,
              manualItemName: selected.itemName || item.manualItemName,
              itemName: selected.itemName || item.itemName,
              inventoryItemId:
                selected.inventoryItemId != null
                  ? String(selected.inventoryItemId)
                  : item.inventoryItemId,
              unitOfMeasurement: selected.unitOfMeasurement || item.unitOfMeasurement || '',
            }
          : item
      )
    )
    setGeneralItemSearchTerms((prev) => {
      const next = {...prev}
      delete next[lineItemId]
      return next
    })
    if (selected.inventoryItemId == null) {
      await ensureGeneralItemInInventory({
        ...line,
        generalItemId: value,
        manualItemName: selected.itemName || line.manualItemName,
        itemName: selected.itemName || line.itemName,
        unitOfMeasurement: selected.unitOfMeasurement || line.unitOfMeasurement || '',
      })
    }
  }

  const resolveEquipmentIdForLine = (lineItem?: LineItemForm): number | null => {
    if (hasLockedEquipment && lockedEquipmentId) return lockedEquipmentId
    if (!lineItem?.equipmentId) return null
    const parsed = Number(lineItem.equipmentId)
    return Number.isNaN(parsed) ? null : parsed
  }

  const getSubcomponentOptionsForLine = (lineItem: LineItemForm): SubComponent[] => {
    const eqId = resolveEquipmentIdForLine(lineItem)
    if (eqId) {
      const mappedSubcomponents = subcomponentOptionsMap[eqId]
      if (mappedSubcomponents) return dedupeSubComponentRows(mappedSubcomponents)
      const fallbackForEquipment = dedupeSubComponentRows(
        allSubComponents.filter((sc) => sc.equipmentId === eqId)
      )
      if (fallbackForEquipment.length > 0) return fallbackForEquipment
    }
    if (availableSubComponents.length > 0) return dedupeSubComponentRows(availableSubComponents)
    return dedupeSubComponentRows(allSubComponents)
  }

  const getGroupedSubcomponentOptionsForLine = (
    lineItem: LineItemForm
  ): SubcomponentOptionGroup[] => {
    const options = getSubcomponentOptionsForLine(lineItem)
    return groupSubcomponentsForDropdown(options)
  }

  const resolveSubcomponentGroupForLine = (
    lineItem: LineItemForm,
    selectedSubcomponentId: number
  ): SubcomponentOptionGroup | null => {
    if (!selectedSubcomponentId) return null
    const lookup = buildSubcomponentGroupLookup(getGroupedSubcomponentOptionsForLine(lineItem))
    return lookup.get(selectedSubcomponentId) || null
  }

  const resolveSubcomponentSelectionValueForLine = (lineItem: LineItemForm): string => {
    const selectedId = toPositiveNumber(lineItem?.subcomponentId)
    if (!selectedId) return ''
    const group = resolveSubcomponentGroupForLine(lineItem, selectedId)
    return group ? String(group.representativeId) : String(selectedId)
  }

  const partsForEquipment = (equipmentId?: string | number | null): Part[] => {
    if (!equipmentId) return []
    const eqId = Number(equipmentId)
    if (Number.isNaN(eqId)) return []
    const subs = subcomponentOptionsMap[eqId] || []
    const partsFromSubs = subs
      .map((sc) => sc.part)
      .filter((p): p is Part => Boolean(p))
    return partsFromSubs.filter((part, index, arr) => arr.findIndex((p) => p.id === part.id) === index)
  }

  const getPartOptionsForLine = (lineItem: LineItemForm): Part[] => {
    if (lineItem.subcomponentId && !Number.isNaN(Number(lineItem.subcomponentId))) {
      const subcomponentIdNum = Number(lineItem.subcomponentId)
      const subGroup = resolveSubcomponentGroupForLine(lineItem, subcomponentIdNum)
      const groupedIds = subGroup?.memberIds?.length
        ? subGroup.memberIds
        : [subcomponentIdNum]
      const mappedParts = dedupePartRows(
        groupedIds.flatMap((id) => partOptionsMap[id] || [])
      )
      if (mappedParts.length > 0) return mappedParts
      const selectedSubcomponent = findSubcomponentById(subcomponentIdNum)
      if (selectedSubcomponent?.part) return [selectedSubcomponent.part as Part]
    }
    const eqParts = dedupePartRows(partsForEquipment(resolveEquipmentIdForLine(lineItem)))
    if (eqParts.length > 0) return eqParts
    return dedupePartRows(filteredParts)
  }

  const takenGeneralItemIdsForLine = (excludeLineId: string) =>
    new Set(
      lineItems
        .filter(
          (li) =>
            li.id !== excludeLineId &&
            li.generalItemId &&
            li.generalItemId !== 'other'
        )
        .map((li) => String(li.generalItemId))
    )

  const getServiceSubHeadName = (subHeadId?: string | null) => {
    if (!subHeadId) return ''
    return (
      uniqueHeaderSubHeads.find((sh) => String(sh.id) === String(subHeadId))?.name ||
      headerSubHeads.find((sh) => String(sh.id) === String(subHeadId))?.name ||
      inventoryItemSubHeads.find((sh) => String(sh.id) === String(subHeadId))?.name ||
      ''
    )
  }

  const takenPartIdsForLine = (excludeLineId: string) =>
    new Set(
      lineItems
        .filter((li) => li.id !== excludeLineId && li.partId != null)
        .map((li) => Number(li.partId))
        .filter((id) => Number.isFinite(id) && id > 0)
    )

  const getPartSelectOptionsForLine = (lineItem: LineItemForm): Part[] => {
    const base = getPartOptionsForLine(lineItem)
    const taken = takenPartIdsForLine(lineItem.id)
    const current = lineItem.partId != null ? Number(lineItem.partId) : NaN
    return base.filter((p) => !taken.has(p.id) || (Number.isFinite(current) && p.id === current))
  }

  const handleLineItemChange = (lineItemId: string, field: keyof LineItemForm, value: string) => {
    const technicalRequisition = !isGeneralStores
    const lockedEquipmentId = hasLockedEquipment ? requisitionEquipmentId : null

    let derivedPartId: number | null = null
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item
        const updated: LineItemForm = {...item, [field]: value}

        if (field === 'partId') {
          updated.partId = value ? Number(value) : null
        }

        if (field === 'generalItemId') {
          updated.partId = null
          updated.subcomponentClubbing = ''
          updated.generalItemId = value
          if (value) {
            const selected = generalInventoryItems.find((gi) => String(gi.masterItemId) === value)
            updated.manualItemName = selected?.itemName || ''
            updated.itemName = selected?.itemName || item.itemName || ''
            updated.inventoryItemId =
              selected?.inventoryItemId != null ? String(selected.inventoryItemId) : ''
            if (selected?.unitOfMeasurement) {
              updated.unitOfMeasurement = selected.unitOfMeasurement
            }
          } else {
            updated.manualItemName = ''
            updated.itemName = ''
            updated.inventoryItemId = ''
          }
        }

        if (field === 'serviceHeadId') {
          const previousSubHeadName = getServiceSubHeadName(item.serviceSubHeadId)
          updated.serviceSubHeadId = ''
          if (!item.manualItemName?.trim() || item.manualItemName.trim() === previousSubHeadName) {
            updated.manualItemName = ''
            updated.itemName = ''
          }
        }

        if (field === 'serviceSubHeadId') {
          const previousSubHeadName = getServiceSubHeadName(item.serviceSubHeadId)
          const nextSubHeadName = getServiceSubHeadName(value)
          if (
            !item.manualItemName?.trim() ||
            (previousSubHeadName && item.manualItemName.trim() === previousSubHeadName)
          ) {
            updated.manualItemName = nextSubHeadName
          }
          updated.itemName = nextSubHeadName || item.itemName || ''
        }

        if (field === 'equipmentId') {
          if (hasLockedEquipment) {
            return item
          }
          if (technicalRequisition) {
            const selectedVal = value ? Number(value) : null
            const existingEquip = lockedEquipmentId
              ? lockedEquipmentId
              : prev.find((li) => li.equipmentId && li.id !== lineItemId)?.equipmentId
              ? Number(prev.find((li) => li.equipmentId && li.id !== lineItemId)!.equipmentId)
              : null
            if (existingEquip && selectedVal && existingEquip !== selectedVal) {
              toast.error('Technical requisition can use only one equipment')
              return item
            }
          }
          updated.subcomponentId = ''
          updated.partId = null
          updated.subcomponentClubbing = ''
          if (value) {
            const eqIdNum = Number(value)
            if (!isNaN(eqIdNum)) {
              loadSubcomponentsForEquipmentLocal(eqIdNum)
            }
          }
        }

        if (field === 'subcomponentId') {
          const subIdNum = parseInt(value)
          if (!isNaN(subIdNum)) {
            const subGroup =
              resolveSubcomponentGroupForLine(updated, subIdNum) ||
              resolveSubcomponentGroupForLine(item, subIdNum)
            const representativeId = subGroup?.representativeId || subIdNum
            const groupedMemberIds = subGroup?.memberIds?.length
              ? subGroup.memberIds
              : [representativeId]

            updated.subcomponentId = String(representativeId)
            updated.subcomponentClubbing =
              groupedMemberIds.length > 1
                ? `group:${subGroup?.key || representativeId}|ids:${groupedMemberIds.join(',')}`
                : ''

            const sub = findSubcomponentById(representativeId) || subGroup?.representative
            if (!hasLockedEquipment && sub?.equipmentId) {
              updated.equipmentId = String(sub.equipmentId)
              loadSubcomponentsForEquipmentLocal(sub.equipmentId)
            }
            if (sub?.part?.id) {
              derivedPartId = sub.part.id
              updated.partId = sub.part.id
            } else {
              if (subGroup && groupedMemberIds.length > 1) {
                loadPartsForSubcomponentGroupLocal(subGroup)
              } else {
                loadPartsForSubcomponentLocal(representativeId)
              }
            }
          } else {
            updated.partId = null
            updated.subcomponentClubbing = ''
          }
        }

        return updated
      })
    )

    if (field === 'serviceHeadId') {
      const selectedHeadId = Number(value)
      if (!Number.isNaN(selectedHeadId) && selectedHeadId > 0) {
        loadHeaderSubHeadsByHead(selectedHeadId)
      } else {
        setHeaderSubHeads(inventoryItemSubHeads)
      }
    }

    if (field === 'subcomponentId' && derivedPartId) {
      setSelectedPartId(derivedPartId)
      setRequisitionPartId(derivedPartId)
    }

    // Clear error for this field
    const errorKey = `${lineItemId}_${field}`
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = {...prev}
        delete newErrors[errorKey]
        return newErrors
      })
    }
    if (field === 'generalItemId' && errors[`${lineItemId}_manualItemName`]) {
      setErrors((prev) => {
        const newErrors = {...prev}
        delete newErrors[`${lineItemId}_manualItemName`]
        return newErrors
      })
    }
  }

  const addLineItem = () => {
    if (addModalIsServicesCategory) {
      setLineItems((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          equipmentId: '',
          subcomponentId: '',
          partId: null,
          generalItemId: '',
          manualItemName: '',
          itemName: '',
          quantity: '',
          unitOfMeasurement: '',
          estimatedUnitPrice: '',
          notes: '',
          equipmentLink: '',
          customSubcomponentName: '',
          serviceHeadId: '',
          serviceSubHeadId: '',
        },
      ])
      return
    }

    if (isGeneralStores) {
      setLineItems((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          equipmentId: lockedEquipmentId ? String(lockedEquipmentId) : '',
          subcomponentId: '',
          partId: null,
          generalItemId: '',
          manualItemName: '',
          quantity: '',
          unitOfMeasurement: '',
          estimatedUnitPrice: '',
          notes: '',
          equipmentLink: '',
          customSubcomponentName: '',
          serviceHeadId: '',
          serviceSubHeadId: '',
        },
      ])
      return
    }

    setLineItems((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        equipmentId: lockedEquipmentId ? String(lockedEquipmentId) : '',
        subcomponentId: '',
        partId: null,
        manualItemName: '',
        itemName: '',
        quantity: '',
          unitOfMeasurement: '',
        estimatedUnitPrice: '',
        notes: '',
        equipmentLink: '',
        customSubcomponentName: '',
        make: '',
        model: '',
        subcomponentClubbing: '',
        serviceHeadId: '',
        serviceSubHeadId: '',
      },
    ])
  }

  const removeLineItem = (lineItemId: string) => {
    if (lineItems.length === 1) {
      toast.error('At least one line item is required')
      return
    }
    setLineItems((prev) => prev.filter((item) => item.id !== lineItemId))
    setLineAttachmentFiles((prev) => {
      const next = {...prev}
      delete next[lineItemId]
      return next
    })
  }

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {}

    // Validate each line item
    lineItems.forEach((item) => {
      // Services flow
      if (addModalIsServicesCategory) {
        if (!item.serviceSubHeadId) {
          newErrors[`${item.id}_serviceSubHeadId`] = 'Sub Head is required for services'
        }
        if (!item.manualItemName || !item.manualItemName.trim()) {
          newErrors[`${item.id}_manualItemName`] = 'Remark is required'
        }
        if (!item.quantity || Number(item.quantity) <= 0) {
          newErrors[`${item.id}_quantity`] = 'Quantity must be greater than 0'
        }
        if (!item.unitOfMeasurement) {
          newErrors[`${item.id}_unitOfMeasurement`] = 'Unit of measurement is required'
        }
        return
      }

      // General stores flow
      if (isGeneralStores) {
        if (!item.manualItemName || !item.manualItemName.trim()) {
          newErrors[`${item.id}_manualItemName`] = 'Item name is required'
        }
        if (!item.quantity || Number(item.quantity) <= 0) {
          newErrors[`${item.id}_quantity`] = 'Quantity must be greater than 0'
        }
        if (!item.unitOfMeasurement) {
          newErrors[`${item.id}_unitOfMeasurement`] = 'Unit of measurement is required'
        }
        return
      }

      // Technical spares flow
      const eqId = lockedEquipmentId || (item.equipmentId ? Number(item.equipmentId) : null)
      if (!eqId) {
        newErrors[`${item.id}_equipmentId`] = 'Equipment is required for technical requisitions'
      }
      if (!item.subcomponentId) {
        newErrors[`${item.id}_subcomponentId`] = 'Sub Component is required'
      }
      const resolvedPartIdForValidation =
        item.partId ?? resolvePartIdForLine(item) ?? currentRequisition?.partId ?? null
      if (!resolvedPartIdForValidation) {
        newErrors[`${item.id}_partId`] = 'Part is required (select a subcomponent or part)'
      }
      if (!item.quantity) newErrors[`${item.id}_quantity`] = 'Quantity is required'
      if (!item.unitOfMeasurement) {
        newErrors[`${item.id}_unitOfMeasurement`] = 'Unit of measurement is required'
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    if (!currentRequisition) {
      toast.error('Requisition data not loaded')
      return
    }
    setIsSubmitting(true)
    try {
      // Auto-create any custom "other" general items into this vessel's General Items Library
      const effectiveVesselId =
        currentRequisition?.vesselId ||
        (formData.vesselId ? Number(formData.vesselId) : null)
      const subCategoryId =
        toPositiveNumber((currentRequisition as any)?.inventoryItemSubCategoryId) ??
        (formData.storeSubCategoryId && /^\d+$/.test(formData.storeSubCategoryId)
          ? Number(formData.storeSubCategoryId)
          : null)

      if (isGeneralStores && effectiveVesselId) {
        for (let i = 0; i < lineItems.length; i++) {
          const item = lineItems[i]
          if (
            (item.generalItemId === 'other' || !item.inventoryItemId) &&
            item.manualItemName?.trim()
          ) {
            try {
              const created = await createGeneralInventoryItem({
                vesselId: Number(effectiveVesselId),
                itemFor: 'GENERAL_STORES',
                manualItemName: item.manualItemName.trim(),
                unitOfMeasurement: item.unitOfMeasurement || '',
                currentQty: 0,
                reorderLevel: 0,
                minThreshold: 0,
                maxThreshold: 0,
                unitCost: 0,
                location: '',
                inventoryItemSubCategoryId: subCategoryId ? Number(subCategoryId) : null,
              })
              if (created?.id) {
                item.inventoryItemId = String(created.id)
                item.generalItemId = ''
              }
            } catch (createErr) {
              console.error('Error auto-creating custom general inventory item for vessel:', createErr)
            }
          }
        }
      }

      const currentLineById = new Map<number, RequisitionLine>()
      ;(currentRequisition.lines || []).forEach((line) => {
        if (line?.id != null) {
          currentLineById.set(Number(line.id), line as RequisitionLine)
        }
      })

      const updatedLines: RequisitionLine[] = lineItems.map((item) => {
        const existingLine =
          item.persistedLineId != null ? currentLineById.get(Number(item.persistedLineId)) : undefined
        if (addModalIsServicesCategory) {
          const notes = [item.notes, item.equipmentLink].filter((v) => v && v.trim().length > 0).join(' | ') || undefined
          const serviceRemark =
            item.manualItemName?.trim() || getServiceSubHeadName(item.serviceSubHeadId) || 'Service Item'
          return {
            id: item.persistedLineId,
            manualItemName: serviceRemark,
            quantity: parseFloat(item.quantity),
            unitOfMeasurement: item.unitOfMeasurement ?? undefined,
            notes,
            attachments: existingLine?.attachments || [],
            inventoryItemSubHeadId: item.serviceSubHeadId ? Number(item.serviceSubHeadId) : undefined,
            inventoryItemHeadId: item.serviceHeadId ? Number(item.serviceHeadId) : undefined,
          }
        }

        if (isGeneralStores) {
          const notes =
            [item.notes, item.equipmentLink].filter((v) => v && v.trim().length > 0).join(' | ') ||
            undefined
          const masterItemIdNum =
            item.generalItemId &&
            item.generalItemId !== 'other' &&
            !isNaN(Number(item.generalItemId))
              ? Number(item.generalItemId)
              : undefined
          return {
            id: item.persistedLineId,
            inventoryItemId: item.inventoryItemId ? Number(item.inventoryItemId) : undefined,
            masterItemId: masterItemIdNum,
            manualItemName: item.manualItemName?.trim() || '',
            quantity: parseFloat(item.quantity),
            unitOfMeasurement: item.unitOfMeasurement ?? undefined,
            estimatedUnitPrice: item.estimatedUnitPrice
              ? parseFloat(item.estimatedUnitPrice)
              : undefined,
            notes,
            attachments: existingLine?.attachments || [],
          }
        }

        const resolvedPartId = item.partId ?? resolvePartIdForLine(item)
        const resolvedPartIdNumber = resolvedPartId ?? currentRequisition?.partId ?? 0
        const selectedSubcomponentId = item.subcomponentId ? parseInt(item.subcomponentId) : null
        const selectedSubcomponentGroup =
          selectedSubcomponentId && !Number.isNaN(selectedSubcomponentId)
            ? resolveSubcomponentGroupForLine(item, selectedSubcomponentId)
            : null
        const selectedSubcomponentRepresentativeId =
          selectedSubcomponentGroup?.representativeId ?? selectedSubcomponentId
        const groupedSubcomponentClubbing =
          selectedSubcomponentGroup && selectedSubcomponentGroup.memberIds.length > 1
            ? `group:${selectedSubcomponentGroup.key}|ids:${selectedSubcomponentGroup.memberIds.join(',')}`
            : undefined

        return {
          id: item.persistedLineId,
          partId: resolvedPartIdNumber,
          subcomponentId: selectedSubcomponentRepresentativeId,
          subcomponentClubbing:
            item.subcomponentClubbing?.trim() || groupedSubcomponentClubbing || undefined,
          quantity: parseFloat(item.quantity),
          unitOfMeasurement: item.unitOfMeasurement ?? undefined,
          notes: item.notes || undefined,
          attachments: existingLine?.attachments || [],
        }
      })

      // Update the requisition with merged lines
      // IMPORTANT: Preserve all existing fields to prevent data loss
      console.log('=== ADD ITEMS DEBUG ===')
      console.log(
        'Current requisition inventoryItemHeadId:',
        currentRequisition.inventoryItemHeadId
      )
      console.log(
        'Current requisition inventoryItemSubHeadId:',
        currentRequisition.inventoryItemSubHeadId
      )
      console.log('Current requisition make:', currentRequisition.make)
      console.log('Current requisition model:', currentRequisition.model)

      const updatePayload: UpdateRequisitionRequest = {
        title: formData.title || currentRequisition.title,
        requisition_code: currentRequisition.requisition_code,
        description: formData.description || currentRequisition.description,
        regionId: editRegionTouched ? (editRegionId ?? 0) : undefined,
        type: currentRequisition.type,
        requestFor: currentRequisition.requestFor || undefined,
        categoryId: currentRequisition.category?.id ?? undefined,
        inventoryItemSubCategoryId: currentRequisition.inventoryItemSubCategoryId ?? undefined,
        inventoryItemHeadId: addModalIsStoresCategory
          ? undefined
          : headerHeadId
          ? Number(headerHeadId)
          : currentRequisition.inventoryItemHeadId ?? undefined,
        inventoryItemSubHeadId: addModalIsStoresCategory
          ? undefined
          : headerSubHeadId
          ? Number(headerSubHeadId)
          : currentRequisition.inventoryItemSubHeadId ?? undefined,
        equipmentId: lockedEquipmentId ?? currentRequisition.equipmentId ?? undefined,
        make: currentRequisition.make || lineItems[0]?.make || undefined,
        model: currentRequisition.model || lineItems[0]?.model || undefined,
        vesselId: currentRequisition.vesselId,
        requestedPortId: currentRequisition.requestedPortId,
        customPortName: currentRequisition.customPortName,
        requiredDeliveryDate: currentRequisition.requiredDeliveryDate,
        equipmentLink: isGeneralStores
          ? undefined
          : formData.equipmentLink || currentRequisition.equipmentLink || undefined,
        status: currentRequisition.status,
        lines: updatedLines,
      }

      console.log('Update payload inventoryItemHeadId:', updatePayload.inventoryItemHeadId)
      console.log('Update payload inventoryItemSubHeadId:', updatePayload.inventoryItemSubHeadId)
      console.log('Update payload with preserved fields:', updatePayload)
      console.log('Selected files to upload:', selectedFiles.length)
      const lineFilesByIndex: Record<number, File[]> = {}
      lineItems.forEach((item, idx) => {
        const files = lineAttachmentFiles[item.id]
        if (files && files.length > 0) {
          lineFilesByIndex[idx] = files
        }
      })
      console.log('=== END DEBUG ===')

      // Pass files to updateRequisition
      const savedRequisition = await updateRequisition(
        requisitionId,
        updatePayload,
        selectedFiles,
        lineFilesByIndex
      )
      await syncGeneralInventoryItemsAfterSave(savedRequisition)

      // Show success message
      const filesMessage =
        selectedFiles.length > 0 ? ` with ${selectedFiles.length} attachment(s)` : ''
      const lineAttachmentCount = Object.values(lineAttachmentFiles).reduce(
        (acc, files) => acc + files.length,
        0
      )
      const lineFilesMessage =
        lineAttachmentCount > 0 ? ` and ${lineAttachmentCount} line attachment(s)` : ''
      toast.success(
        `Requisition updated successfully with ${lineItems.length} line item(s)${filesMessage}${lineFilesMessage}.`
      )

      onSubmit()
      handleClose()
    } catch (error: any) {
      console.error('Error adding items:', error)
      toast.error('Failed to add items')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'STORES' as any,
      urgency: 'MEDIUM',
      itemMode: 'TECHNICAL',
      requestedPortId: '',
      customPortName: '',
      requiredDeliveryDate: '',
      categoryId: '',
      headId: '',
      subHeadId: '',
      partId: '',
      subComponentId: '',
      itemName: '',
      quantity: '',
      uom: 'PCS',
      estimatedUnitPrice: '',
      equipmentLink: '',
      equipmentId: '',
      make: '',
      model: '',
      serialNumber: '',
    })
    setLineItems([
      {
        id: Date.now().toString(),
        equipmentId: '',
        subcomponentId: '',
        partId: null,
        generalItemId: '',
        manualItemName: '',
        itemName: '',
        quantity: '',
          unitOfMeasurement: '',
        estimatedUnitPrice: '',
        notes: '',
        customSubcomponentName: '',
      },
    ])
    setErrors({})
    setAvailableSubComponents([])
    setRequisitionPartId(null)
    setSelectedPartId(null)
    setFilteredParts([])
    setAllSubComponents([])
    setSelectedFiles([])
    setLineAttachmentFiles({})
    setHeaderHeadId('')
    setHeaderSubHeadId('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!visible) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center procurement-document-modal'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div
        className='modal-dialog modal-dialog-centered modal-lg'
        role='document'
        style={{maxHeight: '95vh', margin: '1.75rem auto'}}
      >
        <div
          className='modal-content bg-white'
          style={{color: '#181C32', display: 'flex', flexDirection: 'column', maxHeight: '95vh'}}
        >
          <div className='modal-header' style={{flexShrink: 0}}>
            <div>
              <h5 className='modal-title'>Edit Requisition</h5>
              <p className='text-muted mb-0 fs-7'>
                Requisition ID: <strong>{currentRequisition?.requisition_code || '-'}</strong>
                <span className='ms-3'>
                  Status:{' '}
                  <span className='badge badge-light-info'>
                    {currentRequisition?.status || 'DRAFT'}
                  </span>
                </span>
              </p>
            </div>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <div className='modal-body' style={{overflowY: 'auto', flexGrow: 1, minHeight: 0}}>
            <div className='alert alert-info d-flex align-items-center mb-4'>
              <i className='fas fa-info-circle me-2'></i>
              <div>
                <strong>Edit Requisition:</strong> Update requisition details and existing line items.
                You can add, modify, or remove items before saving.
              </div>
            </div>

            {isLoadingRequisition ? (
              <div className='text-center py-4'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <p className='text-muted mt-2'>Loading requisition data...</p>
              </div>
            ) : (
              <>
                {/* Header Classification (moved from Create step) */}
                <div className='row g-3 mb-4'>
                  <div className='col-md-12'>
                    <div className='d-flex flex-wrap align-items-center justify-content-between'>
                      <div className='text-muted fs-7'>
                        Requisition For:{' '}
                        <span className='fw-bold text-dark'>
                          {isGeneralStores ? 'General Stores' : 'Technical'}
                        </span>
                      </div>
                      {currentRequisition?.category?.id && (
                        <div className='text-muted fs-7'>
                          Category:{' '}
                          <span className='fw-bold text-dark'>
                            {currentRequisition.category.name || `ID: ${currentRequisition.category.id}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                {lineItems.map((lineItem, index) => (
                  <div
                    key={lineItem.id}
                    className='border rounded p-3 mb-3'
                    style={{backgroundColor: '#f8f9fa'}}
                  >
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h6 className='fw-bold mb-0' style={{color: '#181C32'}}>
                        Add Item #{index + 1}
                      </h6>
                      {lineItems.length > 1 && (
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger'
                          onClick={() => removeLineItem(lineItem.id)}
                        >
                          <i className='fas fa-trash'></i> Remove
                        </button>
                      )}
                    </div>

                    <div className='row g-3'>
                      {addModalIsServicesCategory && (
                        <>
                          <div className='col-md-6'>
                            <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                              Head
                            </label>
                            <SearchableSelect
                              className='form-select'
                              value={lineItem.serviceHeadId}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'serviceHeadId', e.target.value)
                              }
                            >
                              <option value=''>Select Head</option>
                              {uniqueHeaderHeads.map((head) => (
                                <option key={head.id} value={head.id}>
                                  {head.name}
                                </option>
                              ))}
                            </SearchableSelect>
                          </div>

                          <div className='col-md-6'>
                            <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                              Sub Head (required)
                            </label>
                            <SearchableSelect
                              className={`form-select ${errors[`${lineItem.id}_serviceSubHeadId`] ? 'is-invalid' : ''}`}
                              value={lineItem.serviceSubHeadId}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'serviceSubHeadId', e.target.value)
                              }
                            >
                              <option value=''>Select Sub Head</option>
                              {uniqueHeaderSubHeads
                                .filter((sh) =>
                                  lineItem.serviceHeadId ? sh.inventoryItemHeadId === Number(lineItem.serviceHeadId) : true
                                )
                                .map((sub) => (
                                  <option key={sub.id} value={sub.id}>
                                    {sub.name}
                                  </option>
                                ))}
                            </SearchableSelect>
                            {errors[`${lineItem.id}_serviceSubHeadId`] && (
                              <div className='invalid-feedback'>{errors[`${lineItem.id}_serviceSubHeadId`]}</div>
                            )}
                          </div>

                          <div className='col-md-12'>
                            <label
                              className='form-label required fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Remark
                            </label>
                            <input
                              type='text'
                              className={`form-control ${
                                errors[`${lineItem.id}_manualItemName`] ? 'is-invalid' : ''
                              }`}
                              value={lineItem.manualItemName || ''}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'manualItemName', e.target.value)
                              }
                              placeholder={
                                getServiceSubHeadName(lineItem.serviceSubHeadId) ||
                                'Enter service remark'
                              }
                              style={{color: '#000'}}
                            />
                            {errors[`${lineItem.id}_manualItemName`] && (
                              <div className='invalid-feedback'>
                                {errors[`${lineItem.id}_manualItemName`]}
                              </div>
                            )}
                          </div>

                          <div className='col-md-3'>
                            <label
                              className='form-label required fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Quantity
                            </label>
                            <input
                              type='number'
                              className={`form-control ${
                                errors[`${lineItem.id}_quantity`] ? 'is-invalid' : ''
                              }`}
                              value={lineItem.quantity}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'quantity', e.target.value)
                              }
                              placeholder='e.g., 10'
                              style={{color: '#000'}}
                            />
                            {errors[`${lineItem.id}_quantity`] && (
                              <div className='invalid-feedback'>
                                {errors[`${lineItem.id}_quantity`]}
                              </div>
                            )}
                          </div>

                          <div className='col-md-3'>
                            <label
                              className='form-label required fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Unit
                            </label>
                            <SearchableSelect
                              className={`form-select ${
                                errors[`${lineItem.id}_unitOfMeasurement`] ? 'is-invalid' : ''
                              }`}
                              value={lineItem.unitOfMeasurement}
                              onChange={(e) =>
                                handleLineItemChange(
                                  lineItem.id,
                                  'unitOfMeasurement',
                                  e.target.value
                                )
                              }
                              style={{color: '#000'}}
                            >
                              {isLoadingUnitsOfMeasure && <option value=''>Loading units...</option>}
                              {!isLoadingUnitsOfMeasure && unitsOfMeasureError && <option value=''>Unable to load units</option>}
                              {!isLoadingUnitsOfMeasure && !unitsOfMeasureError && <option value=''>Select Unit</option>}
                              {uomOptionsFor(lineItem.unitOfMeasurement).map((uom) => (
                                <option key={`${lineItem.id}-${uom.id}-${uom.code}`} value={uom.code}>
                                  {uom.code}{uom.name ? ` - ${uom.name}` : ''}{uom.active === false ? ' (saved value)' : ''}
                                </option>
                              ))}
                            </SearchableSelect>
                            {errors[`${lineItem.id}_unitOfMeasurement`] && (
                              <div className='invalid-feedback'>
                                {errors[`${lineItem.id}_unitOfMeasurement`]}
                              </div>
                            )}
                          </div>

                          <div className='col-md-6'>
                            <label
                              className='form-label fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Equipment Link / Reference (optional)
                            </label>
                            <input
                              type='text'
                              className='form-control'
                              value={lineItem.equipmentLink || ''}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'equipmentLink', e.target.value)
                              }
                              placeholder='e.g., work order link or reference ID'
                              style={{color: '#000'}}
                            />
                          </div>

                          <div className='col-md-6'>
                            <label
                              className='form-label fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Additional Notes (optional)
                            </label>
                            <input
                              type='text'
                              className='form-control'
                              value={lineItem.notes}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'notes', e.target.value)
                              }
                              placeholder='Any special instructions'
                              style={{color: '#000'}}
                            />
                          </div>
                        </>
                      )}

                      {!addModalIsServicesCategory && isGeneralStores && (
                        <>
                          {addModalIsServicesCategory ? (
                            <div className='col-md-12'>
                              <label
                                className='form-label required fw-semibold fs-6 mb-2'
                                style={{color: '#181C32'}}
                              >
                                Service Item (from Sub Head)
                              </label>
                              <div
                                className={`form-control bg-light ${
                                  !effectiveSubHeadId ? 'is-invalid' : ''
                                }`}
                                style={{color: '#000'}}
                              >
                                {effectiveSubHeadName || 'Select Sub Head above to use as item'}
                              </div>
                              {!effectiveSubHeadId && (
                                <div className='invalid-feedback d-block'>
                                  Select a Sub Head to add service items.
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className='col-md-6'>
                              <SearchableLookupField
                                label='Item Name'
                                required
                                value={lineItem.generalItemId || ''}
                                searchText={generalItemSearchTerms[lineItem.id]}
                                displayValue={resolveGeneralItemDisplayValue(lineItem)}
                                options={getGeneralInventoryLookupOptionsForLine(lineItem)}
                                onChange={(value) => handleGeneralItemSelection(lineItem.id, value)}
                                onSearchTextChange={(value) =>
                                  searchGeneralInventoryOptions(lineItem.id, value)
                                }
                                placeholder={
                                  isLoadingGeneralInventoryItems
                                    ? 'Loading items...'
                                    : 'Type item name, code, part no, or IMPA'
                                }
                                helperText='Search master catalogue by code or name. ROB is vessel-specific.'
                                showHelperText={false}
                                error={errors[`${lineItem.id}_manualItemName`]}
                                wrapperClassName='w-100'
                                listId={`requisition-general-item-suggestions-${lineItem.id}`}
                                isLoading={isLoadingGeneralInventoryItems}
                                hasMore={generalInventoryHasMore}
                                onLoadMore={() => loadMoreGeneralInventoryOptions(lineItem.id)}
                                noOptionsText='No matching items found'
                              />
                              {(lineItem.generalItemId === 'other' ||
                                (!lineItem.generalItemId &&
                                  (lineItem.manualItemName || lineItem.itemName))) && (
                                <div className='mt-2'>
                                  <label className='form-label required fs-7 text-gray-700 fw-semibold'>
                                    Custom Item Name
                                  </label>
                                  <input
                                    type='text'
                                    className={`form-control ${
                                      errors[`${lineItem.id}_manualItemName`] ? 'is-invalid' : ''
                                    }`}
                                    value={lineItem.manualItemName || ''}
                                    onChange={(e) =>
                                      handleLineItemChange(
                                        lineItem.id,
                                        'manualItemName',
                                        e.target.value
                                      )
                                    }
                                    placeholder='Enter custom item name'
                                    style={{color: '#000'}}
                                  />
                                  <div className='form-text text-muted fs-8 mt-1'>
                                    This item will be saved into this vessel's General Items Library.
                                  </div>
                                </div>
                              )}
                              {errors[`${lineItem.id}_manualItemName`] && (
                                <div className='invalid-feedback'>
                                  {errors[`${lineItem.id}_manualItemName`]}
                                </div>
                              )}
                            </div>
                          )}

                          <div className='col-md-3'>
                            <label
                              className='form-label required fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Quantity
                            </label>
                            <input
                              type='number'
                              className={`form-control ${
                                errors[`${lineItem.id}_quantity`] ? 'is-invalid' : ''
                              }`}
                              value={lineItem.quantity}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'quantity', e.target.value)
                              }
                              placeholder='e.g., 10'
                              style={{color: '#000'}}
                            />
                            {errors[`${lineItem.id}_quantity`] && (
                              <div className='invalid-feedback'>
                                {errors[`${lineItem.id}_quantity`]}
                              </div>
                            )}
                          </div>

                          <div className='col-md-3'>
                            <label
                              className='form-label required fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Unit
                            </label>
                            <SearchableSelect
                              className={`form-select ${
                                errors[`${lineItem.id}_unitOfMeasurement`] ? 'is-invalid' : ''
                              }`}
                              value={lineItem.unitOfMeasurement}
                              onChange={(e) =>
                                handleLineItemChange(
                                  lineItem.id,
                                  'unitOfMeasurement',
                                  e.target.value
                                )
                              }
                              style={{color: '#000'}}
                            >
                              {isLoadingUnitsOfMeasure && <option value=''>Loading units...</option>}
                              {!isLoadingUnitsOfMeasure && unitsOfMeasureError && <option value=''>Unable to load units</option>}
                              {!isLoadingUnitsOfMeasure && !unitsOfMeasureError && <option value=''>Select Unit</option>}
                              {uomOptionsFor(lineItem.unitOfMeasurement).map((uom) => (
                                <option key={`${lineItem.id}-${uom.id}-${uom.code}`} value={uom.code}>
                                  {uom.code}{uom.name ? ` - ${uom.name}` : ''}{uom.active === false ? ' (saved value)' : ''}
                                </option>
                              ))}
                            </SearchableSelect>
                            {errors[`${lineItem.id}_unitOfMeasurement`] && (
                              <div className='invalid-feedback'>
                                {errors[`${lineItem.id}_unitOfMeasurement`]}
                              </div>
                            )}
                          </div>

                          {addModalIsServicesCategory && (
                            <div className='col-md-6'>
                              <label
                                className='form-label fw-semibold fs-6 mb-2'
                                style={{color: '#181C32'}}
                              >
                                Equipment Link / Reference (optional)
                              </label>
                              <input
                                type='text'
                                className='form-control'
                                value={lineItem.equipmentLink || ''}
                                onChange={(e) =>
                                  handleLineItemChange(lineItem.id, 'equipmentLink', e.target.value)
                                }
                                placeholder='e.g., work order link or reference ID'
                                style={{color: '#000'}}
                              />
                            </div>
                          )}

                          <div className={addModalIsServicesCategory ? 'col-md-6' : 'col-md-12'}>
                            <label
                              className='form-label fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              {addModalIsServicesCategory ? 'Remarks (optional)' : 'Notes'}
                            </label>
                            <input
                              type='text'
                              className='form-control'
                              value={lineItem.notes}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'notes', e.target.value)
                              }
                              placeholder='Any special instructions'
                              style={{color: '#000'}}
                            />
                          </div>
                        </>
                      )}

                      {!addModalIsServicesCategory && !isGeneralStores && (
                        <>
                          {hasLockedEquipment && lockedEquipmentId ? (
                            <div className='col-md-12'>
                              <label
                                className='form-label fw-semibold fs-6 mb-2'
                                style={{color: '#181C32'}}
                              >
                                Equipment (locked)
                              </label>
                              <div className='form-control bg-light' style={{color: '#000'}}>
                                {lockedEquipmentName ||
                                  currentRequisition?.equipmentLink ||
                                  `Equipment ID: ${lockedEquipmentId}`}
                              </div>
                            </div>
                          ) : (
                            <div className='col-md-12'>
                              <label
                                className='form-label required fw-semibold fs-6 mb-2'
                                style={{color: '#181C32'}}
                              >
                                Equipment
                              </label>
                              <SearchableSelect
                                className={`form-select ${
                                  errors[`${lineItem.id}_equipmentId`] ? 'is-invalid' : ''
                                }`}
                                value={lineItem.equipmentId || ''}
                                onChange={(e) =>
                                  handleLineItemChange(lineItem.id, 'equipmentId', e.target.value)
                                }
                                style={{color: '#000'}}
                              >
                                <option value=''>
                                  {equipmentOptions.length === 0
                                    ? 'No equipment available'
                                    : 'Select Equipment'}
                                </option>
                                {equipmentOptions.map((equipment) => (
                                  <option key={equipment.id} value={equipment.id}>
                                    {equipment.code
                                      ? `${equipment.name} (${equipment.code})`
                                      : equipment.name}
                                  </option>
                                ))}
                              </SearchableSelect>
                              {errors[`${lineItem.id}_equipmentId`] && (
                                <div className='invalid-feedback'>
                                  {errors[`${lineItem.id}_equipmentId`]}
                                </div>
                              )}
                            </div>
                          )}

                          <div className='col-md-6'>
                            <label
                              className='form-label required fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Sub Component
                            </label>
                            <SearchableSelect
                              className={`form-select ${
                                errors[`${lineItem.id}_subcomponentId`] ? 'is-invalid' : ''
                              }`}
                              value={resolveSubcomponentSelectionValueForLine(lineItem)}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'subcomponentId', e.target.value)
                              }
                              disabled={
                                isLoadingSubComponents ||
                                getGroupedSubcomponentOptionsForLine(lineItem).length === 0
                              }
                              style={{color: '#000'}}
                            >
                              <option value=''>
                                {isLoadingSubComponents
                                  ? 'Loading subcomponents...'
                                  : getGroupedSubcomponentOptionsForLine(lineItem).length === 0
                                  ? 'No sub components available'
                                  : 'Select Sub Component'}
                              </option>
                              {getGroupedSubcomponentOptionsForLine(lineItem).map((group) => (
                                <option key={group.representativeId} value={group.representativeId}>
                                  {formatSubcomponentGroupLabel(group)}
                                </option>
                              ))}
                            </SearchableSelect>
                            {errors[`${lineItem.id}_subcomponentId`] && (
                              <div className='invalid-feedback'>
                                {errors[`${lineItem.id}_subcomponentId`]}
                              </div>
                            )}
                          </div>

                          {/* Part Selection */}
                          <SearchableLookupField
                            label='Part'
                            required
                            value={lineItem.partId ? String(lineItem.partId) : ''}
                            options={getPartSelectOptionsForLine(lineItem).map((part) => ({
                              id: part.id,
                              label: formatPartLabel(part) || `Part ${part.id}`,
                            }))}
                            onChange={(value) =>
                              handleLineItemChange(
                                lineItem.id,
                                'partId',
                                value
                              )
                            }
                            placeholder={
                              !lineItem.subcomponentId
                                ? 'Select sub component first'
                                : 'Type to search parts'
                            }
                            helperText='Type to search existing parts for this sub-component.'
                            disabled={!lineItem.subcomponentId}
                            error={errors[`${lineItem.id}_partId`]}
                            listId={`requisition-line-part-suggestions-${lineItem.id}`}
                          />

                          {/* Quantity */}
                          <div className='col-md-6'>
                            <label
                              className='form-label required fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Quantity
                            </label>
                            <input
                              type='number'
                              className={`form-control ${
                                errors[`${lineItem.id}_quantity`] ? 'is-invalid' : ''
                              }`}
                              value={lineItem.quantity}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'quantity', e.target.value)
                              }
                              placeholder='e.g., 10'
                              style={{color: '#000'}}
                            />
                            {errors[`${lineItem.id}_quantity`] && (
                              <div className='invalid-feedback'>
                                {errors[`${lineItem.id}_quantity`]}
                              </div>
                            )}
                          </div>

                          {/* Unit of Measurement */}
                          <div className='col-md-6'>
                            <label
                              className='form-label required fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Unit of Measurement
                            </label>
                            <SearchableSelect
                              className={`form-select ${
                                errors[`${lineItem.id}_unitOfMeasurement`] ? 'is-invalid' : ''
                              }`}
                              value={lineItem.unitOfMeasurement}
                              onChange={(e) =>
                                handleLineItemChange(
                                  lineItem.id,
                                  'unitOfMeasurement',
                                  e.target.value
                                )
                              }
                              style={{color: '#000'}}
                            >
                              {isLoadingUnitsOfMeasure && <option value=''>Loading units...</option>}
                              {!isLoadingUnitsOfMeasure && unitsOfMeasureError && <option value=''>Unable to load units</option>}
                              {!isLoadingUnitsOfMeasure && !unitsOfMeasureError && <option value=''>Select Unit</option>}
                              {uomOptionsFor(lineItem.unitOfMeasurement).map((uom) => (
                                <option key={`${lineItem.id}-${uom.id}-${uom.code}`} value={uom.code}>
                                  {uom.code}{uom.name ? ` - ${uom.name}` : ''}{uom.active === false ? ' (saved value)' : ''}
                                </option>
                              ))}
                            </SearchableSelect>
                            {errors[`${lineItem.id}_unitOfMeasurement`] && (
                              <div className='invalid-feedback'>
                                {errors[`${lineItem.id}_unitOfMeasurement`]}
                              </div>
                            )}
                          </div>

                          {/* Remarks */}
                          <div className='col-md-12'>
                            <label
                              className='form-label fw-semibold fs-6 mb-2'
                              style={{color: '#181C32'}}
                            >
                              Remarks
                            </label>
                            <input
                              type='text'
                              className='form-control'
                              value={lineItem.notes}
                              onChange={(e) =>
                                handleLineItemChange(lineItem.id, 'notes', e.target.value)
                              }
                              placeholder='e.g., Qty increased as per tech'
                              style={{color: '#000'}}
                            />
                          </div>
                        </>
                      )}

                      <div className='col-12'>
                        <label className='form-label fw-semibold fs-7 mb-2' style={{color: '#181C32'}}>
                          Item Attachments
                        </label>
                        <input
                          type='file'
                          className='form-control'
                          multiple
                          accept='.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png'
                          onChange={(e) => handleLineAttachmentChange(lineItem.id, e.target.files)}
                        />
                        {(lineAttachmentFiles[lineItem.id] || []).length > 0 && (
                          <div className='mt-2 d-flex flex-column gap-1'>
                            {(lineAttachmentFiles[lineItem.id] || []).map((file, fileIndex) => (
                              <div
                                key={`${lineItem.id}-attachment-${fileIndex}`}
                                className='d-flex align-items-center justify-content-between border rounded px-2 py-1'
                              >
                                <small className='text-dark text-truncate pe-2'>{file.name}</small>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-danger'
                                  onClick={() => removeLineAttachment(lineItem.id, fileIndex)}
                                >
                                  <i className='fas fa-times'></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(lineItem.existingAttachments || []).length > 0 && (
                          <div className='mt-2 d-flex flex-wrap gap-2'>
                            {(lineItem.existingAttachments || []).map((attachment, idx) => {
                              const attachmentRef = {
                                id: attachment?.id ?? null,
                                attachmentKey: attachment?.attachmentKey ?? null,
                                url: attachment?.url ?? null,
                                viewUrl: attachment?.viewUrl ?? null,
                                downloadUrl: attachment?.downloadUrl ?? null,
                              }
                              const resolvedUrl = resolveProcurementViewUrl(attachmentRef)
                              return (
                                <button
                                  key={`${lineItem.id}-existing-attachment-${idx}`}
                                  type='button'
                                  className='btn btn-sm btn-light-primary'
                                  onClick={() => void previewAttachmentBlob(attachmentRef)}
                                  disabled={!resolvedUrl}
                                >
                                  Open {attachment.fileName || `Attachment ${idx + 1}`}
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add More Buttons */}
                <div className='mb-3'>
                  <div className='row g-2'>
                    <div className='col-md-12'>
                      <button
                        type='button'
                        className='btn btn-light-primary w-100'
                        onClick={addLineItem}
                      >
                        <i className='fas fa-plus me-2'></i>
                        Add Item
                      </button>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className='border-top pt-3 mt-3'>
                  <h6 className='fw-bold mb-3' style={{color: '#181C32'}}>
                    Additional Information
                  </h6>

                  <div className='row g-3'>
                    <div className='col-md-12'>
                      <RequisitionRegionField regionId={editRegionId} regionName={editRegionName}
                        onChange={(id, name) => { setEditRegionId(id); setEditRegionName(name); setEditRegionTouched(true) }} />
                    </div>
                    <div className='col-md-12'>
                      <label
                        className='form-label fw-semibold fs-6 mb-2'
                        style={{color: '#181C32'}}
                      >
                        Requisition Description
                      </label>
                      <textarea
                        className='form-control'
                        name='description'
                        value={formData.description || ''}
                        onChange={handleInputChange}
                        placeholder='Update requisition description'
                        rows={2}
                        style={{color: '#000'}}
                      />
                    </div>

                    {!isGeneralStores && (
                      <div className='col-md-12'>
                        <label
                          className='form-label fw-semibold fs-6 mb-2'
                          style={{color: '#181C32'}}
                        >
                          Equipment Link/Reference
                        </label>
                        <input
                          type='text'
                          className='form-control'
                          name='equipmentLink'
                          value={formData.equipmentLink}
                          onChange={handleInputChange}
                          placeholder='Link to related equipment or system'
                          style={{color: '#000'}}
                        />
                      </div>
                    )}


                    <div className='col-12'>
                      <label
                        className='form-label fw-semibold fs-6 mb-2'
                        style={{color: '#181C32'}}
                      >
                        Attachments
                      </label>
                      <div
                        className='border rounded p-5 text-center'
                        style={{
                          borderStyle: 'dashed',
                          borderColor: '#E4E6EF',
                          backgroundColor: '#F9F9F9',
                          cursor: 'pointer',
                        }}
                        onClick={() => document.getElementById('addItemsFileInput')?.click()}
                      >
                        <input
                          type='file'
                          id='addItemsFileInput'
                          multiple
                          onChange={handleFileChange}
                          style={{display: 'none'}}
                          accept='.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png'
                        />
                        <div className='mb-2'>
                          <svg
                            width='48'
                            height='48'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='#A1A5B7'
                            strokeWidth='2'
                          >
                            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                            <polyline points='17 8 12 3 7 8' />
                            <line x1='12' y1='3' x2='12' y2='15' />
                          </svg>
                        </div>
                        <p className='text-muted mb-1'>
                          Drag and drop files here, or click to browse
                        </p>
                        <p className='text-muted fs-7 mb-0'>
                          Support for images, PDF, and documents
                        </p>
                      </div>

                      {/* Display selected files */}
                      {selectedFiles.length > 0 && (
                        <div className='mt-3'>
                          <p className='fw-semibold mb-2' style={{color: '#181C32'}}>
                            Selected Files ({selectedFiles.length}):
                          </p>
                          <div className='list-group'>
                            {selectedFiles.map((file, index) => (
                              <div
                                key={index}
                                className='list-group-item d-flex justify-content-between align-items-center'
                              >
                                <div className='d-flex align-items-center'>
                                  <i className='fas fa-file me-2 text-primary'></i>
                                  <div>
                                    <div className='fw-semibold'>{file.name}</div>
                                    <small className='text-muted'>
                                      {(file.size / 1024).toFixed(2)} KB
                                    </small>
                                  </div>
                                </div>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-danger'
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    removeFile(index)
                                  }}
                                >
                                  <i className='fas fa-times'></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div
            className='modal-footer'
            style={{flexShrink: 0, borderTop: '1px solid #E4E6EF', padding: '1rem'}}
          >
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn_primary'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
// END OF AddItemsModal COMPONENT

interface CreateRequisitionModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  onCreated?: (requisitionId: number) => void
  vesselId: number | null
  parts: Part[]
  ports: Port[]
  setPorts: React.Dispatch<React.SetStateAction<Port[]>>
  categories: SubcatalogueType[]
  onSaveExtras: (code: string, extras: ReqExtraMeta) => void
}

const CreateRequisitionModal: FC<CreateRequisitionModalProps> = ({
  visible,
  onClose,
  onSubmit,
  onCreated,
  vesselId: propVesselId,
  parts,
  ports,
  setPorts,
  categories,
  onSaveExtras,
}) => {
  const {currentUser} = useAuth()
  const {getScope} = useProcurementPermissions()
  const createScope =
    getScope(PROCUREMENT_PERMS.CREATE_REQUISITION) ?? getScope(PROCUREMENT_PERMS.READ_REQUISITION)
  const inventoryScopeAll = createScope === 'ALL'
  const inventoryScopeCompany = createScope === 'COMPANY'
  const canSelectVesselInCreate = !inventoryScopeAll && inventoryScopeCompany
  const inventoryScopeVessel = createScope === 'VESSEL'

  const [selectedVesselId, setSelectedVesselId] = useState<number | null>(
    propVesselId ?? currentUser?.vessel?.id ?? null
  )
  const [availableVessels, setAvailableVessels] = useState<RequisitionFormVesselLookup[]>([])

  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    type: 'STORES' as any,
    urgency: 'MEDIUM' as RequisitionUrgency,
    itemMode: 'TECHNICAL' as 'TECHNICAL' | 'GENERAL',
    requestedPortId: '',
    customPortName: '',
    requiredDeliveryDate: '',
    categoryId: '',
    headId: '',
    subHeadId: '',
    partId: '',
    subComponentId: '',
    itemName: '',
    quantity: '',
    uom: 'PCS',
    estimatedUnitPrice: '',
    equipmentLink: '',
    equipmentId: '',
    storeSubCategoryId: '',
    storeSubCategoryName: '',
    linkedDefectId: '',
    linkedDefectNumber: '',
    linkedDefectTitle: '',
    make: '',
    model: '',
    serialNumber: '',
  })
  const [createRegionId, setCreateRegionId] = useState<number | null>(null)
  const [createRegionName, setCreateRegionName] = useState<string | null>(null)

  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [requisitionCode, setRequisitionCode] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isCreatingPort, setIsCreatingPort] = useState(false)
  const [createdPortId, setCreatedPortId] = useState<number | null>(null)
  const [portSearchText, setPortSearchText] = useState('')

  const [inventoryCategories, setInventoryCategories] = useState<InventoryCategory[]>([])
  const [inventoryItemHeads, setInventoryItemHeads] = useState<InventoryItemHead[]>([])
  const [inventoryItemSubHeads, setInventoryItemSubHeads] = useState<InventoryItemSubHead[]>([])
  const [createFormHeads, setCreateFormHeads] = useState<InventoryItemHead[]>([])
  const [createFormSubHeads, setCreateFormSubHeads] = useState<InventoryItemSubHead[]>([])
  const [inventoryItemSubCategories, setInventoryItemSubCategories] = useState<InventoryItemSubCategory[]>([])
  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[]>([])
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([])
  const [subCatalogues, setSubCatalogues] = useState<SubCatalogue[]>([])
  const [filteredAccountingCodes, setFilteredAccountingCodes] = useState<AccountingAccount[]>([])
  const [filteredSubAccounts, setFilteredSubAccounts] = useState<SubAccount[]>([])
  const [filteredSubCatalogues, setFilteredSubCatalogues] = useState<SubCatalogue[]>([])
  const [filteredParts, setFilteredParts] = useState<Part[]>([])
  const [filteredSubComponents, setFilteredSubComponents] = useState<SubComponent[]>([])
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)
  const [isLoadingSubCatalogues, setIsLoadingSubCatalogues] = useState(false)
  const [companyOptions, setCompanyOptions] = useState<RequisitionFormCompanyLookup[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null)
  const [selectedCompanyUsername, setSelectedCompanyUsername] = useState<string>('')
  const [formEquipmentOptions, setFormEquipmentOptions] = useState<EquipmentOption[]>([])
  const [equipmentLoadStatus, setEquipmentLoadStatus] = useState<EquipmentLoadStatus>('idle')
  const [equipmentLoadMessage, setEquipmentLoadMessage] = useState<string | null>(null)
  const equipmentRequestRef = useRef(0)
  const [defectOptions, setDefectOptions] = useState<QHSEDefectDto[]>([])
  const [isLoadingDefectOptions, setIsLoadingDefectOptions] = useState(false)
  const selectedCompanyIdNumber = useMemo(
    () => toPositiveNumber(selectedCompanyId),
    [selectedCompanyId]
  )
  const selectedVesselIdNumber = useMemo(
    () => toPositiveNumber(selectedVesselId),
    [selectedVesselId]
  )
  const createFallbackScope = useMemo(
    () => normalizeCrewScopeFromUser(currentUser as any),
    [currentUser]
  )
  const createScopedCompanyId = useMemo(
    () => selectedCompanyIdNumber ?? createFallbackScope.companyId,
    [selectedCompanyIdNumber, createFallbackScope.companyId]
  )
  const createScopedVesselId = useMemo(
    () => selectedVesselIdNumber ?? createFallbackScope.vesselId,
    [selectedVesselIdNumber, createFallbackScope.vesselId]
  )
  const resolvedCategoryCgaId = useMemo(() => {
    const tenantCgaId = procurementTenantCgaId(currentUser)
    if (inventoryScopeAll) {
      return selectedCompanyIdNumber ?? tenantCgaId ?? null
    }
    return tenantCgaId ?? createScopedCompanyId ?? null
  }, [currentUser, inventoryScopeAll, selectedCompanyIdNumber, createScopedCompanyId])
  const selectedHeadIdNumber = useMemo(() => toPositiveNumber(formData.headId), [formData.headId])
  const uniqueCreateFormHeads = useMemo(
    () => sharedDedupeInventoryHeadRows(createFormHeads || []),
    [createFormHeads]
  )
  const uniqueCreateFormSubHeads = useMemo(
    () => sharedDedupeInventorySubHeadRows(createFormSubHeads || []),
    [createFormSubHeads]
  )
  const dedupedDefectOptions = useMemo(
    () =>
      dedupeRowsByLogicalKey(defectOptions || [], (defect) => {
        const id = toPositiveNumber(defect?.id)
        if (id) return `id:${id}`
        const number = normalizeScopeName(defect?.defectNumber)
        const vesselId = toPositiveNumber(defect?.vesselId) ?? 0
        return number ? `scope:${number}|v:${vesselId}` : null
      }),
    [defectOptions]
  )

  const selectedCategory = inventoryCategories.find((c) => c.id === Number(formData.categoryId))
  const categoryLabel = (selectedCategory?.name || '').toUpperCase()
  const hasCategorySelected = Boolean(formData.categoryId)
  const isServicesCategory = categoryLabel.includes('SERVICE')
  const isSparesCategory = categoryLabel.includes('SPARE')
  const storeSubCategoryOptions = useMemo(() => {
    if (inventoryItemSubCategories.length > 0) {
      const seen = new Set<string>()
      return inventoryItemSubCategories
        .filter((sc) => sc?.id != null && Boolean(sc?.name?.trim()))
        .filter((sc) => {
          const key = sc.name.trim().toLowerCase()
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        .map((sc) => ({value: String(sc.id), label: sc.name}))
    }
    return subCategoryStaticOptions.map((opt) => ({value: opt.value, label: opt.label}))
  }, [inventoryItemSubCategories])

  const createFormCategoryOptions = useMemo(
    () => toCategoryLookupOptions(inventoryCategories),
    [inventoryCategories],
  )

  const getPortDisplayLabel = useCallback((port: Port) => {
    const primaryName = port.name || port.main_port || ''
    const codeSuffix = port.code ? ` (${port.code})` : ''
    const countrySuffix = port.country ? ` - ${port.country}` : ''
    return `${primaryName}${codeSuffix}${countrySuffix}`.trim()
  }, [])

  const filteredPortSuggestions = useMemo(() => {
    const query = portSearchText.trim().toLowerCase()
    if (!query) return ports.slice(0, 100)
    return ports
      .filter((port) =>
        `${port.name || ''} ${port.main_port || ''} ${port.code || ''} ${port.country || ''}`
          .toLowerCase()
          .includes(query)
      )
      .slice(0, 100)
  }, [ports, portSearchText])

  const loadFormEquipmentOptions = (vesselId?: number | null) => {
    if (!vesselId) {
      equipmentRequestRef.current += 1
      setFormEquipmentOptions([])
      setEquipmentLoadStatus('idle')
      setEquipmentLoadMessage(null)
      return
    }

    const requestId = ++equipmentRequestRef.current
    setEquipmentLoadStatus('loading')
    setEquipmentLoadMessage(null)

    void fetchEquipmentOptionsForVessel(vesselId, (id) => getRequisitionFormEquipment(id)).then((result) => {
      if (requestId !== equipmentRequestRef.current) return

      setFormEquipmentOptions(result.options)
      setEquipmentLoadStatus(result.status)
      setEquipmentLoadMessage(result.message ?? null)

      if (result.status === 'error' || result.status === 'forbidden') {
        console.error('Error loading equipment for requisition form:', result.message)
      }
    })
  }

  const loadCompaniesAndVessels = async () => {
    try {
      const companies = await getRequisitionFormCompanies()
      setCompanyOptions(companies)
      setSelectedCompanyId(null)
      setSelectedCompanyUsername('')
      setAvailableVessels([])
    } catch (error) {
      console.error('Error loading companies/vessels:', error)
      toast.error('Failed to load companies/vessels. Please try again.')
    }
  }

  const loadVesselsForCompany = async (companyUsername?: string) => {
    try {
      const normalizedCompanyUsername = String(companyUsername ?? '').trim()
      if (!normalizedCompanyUsername) {
        setAvailableVessels([])
        setSelectedVesselId(null)
        return
      }

      const vessels = await getRequisitionFormVessels(normalizedCompanyUsername)
      setAvailableVessels(vessels)

      if (vessels.length > 0) {
        setSelectedVesselId(vessels[0].id)
      } else {
        setSelectedVesselId(null)
      }
    } catch (error) {
      console.error('Error loading vessels:', error)
      toast.error('Failed to load vessels. Please try again.')
    }
  }

  const loadCreateFormHeads = async () => {
    try {
      console.log('[Create Requisition Add Item] selectedCompanyId:', createScopedCompanyId)
      console.log('[Create Requisition Add Item] selectedVesselId:', createScopedVesselId)
      console.log(
        '[Create Requisition Add Item] fallback currentUser companyId:',
        createFallbackScope.companyId
      )
      console.log(
        '[Create Requisition Add Item] fallback currentUser vesselId:',
        createFallbackScope.vesselId
      )
      console.log('[Create Requisition Add Item] isServicesCategory:', isServicesCategory)
      const headsResult = await sharedLoadScopedInventoryHeads({
        currentUser,
        inventoryScopeAll,
        inventoryScopeCompany,
        inventoryScopeVessel,
        isServicesCategory,
        selectedVesselId: createScopedVesselId,
        selectedCompanyId: createScopedCompanyId,
        selectedCompanyName:
          companyOptions.find((company) => company.id === createScopedCompanyId)?.name ?? null,
        debugContext: 'Create Requisition',
        requireVesselWhenCompanySelected: Boolean(inventoryScopeAll && createScopedCompanyId),
      })
      setCreateFormHeads(sharedDedupeInventoryHeadRows(headsResult.rows || []))
    } catch (error) {
      console.error('Error loading requisition heads:', error)
      setCreateFormHeads([])
    }
  }

  const loadCreateFormSubHeads = async (headId?: number) => {
    try {
      console.log('[Create Requisition Add Item] selectedCompanyId:', createScopedCompanyId)
      console.log('[Create Requisition Add Item] selectedVesselId:', createScopedVesselId)
      console.log(
        '[Create Requisition Add Item] fallback currentUser companyId:',
        createFallbackScope.companyId
      )
      console.log(
        '[Create Requisition Add Item] fallback currentUser vesselId:',
        createFallbackScope.vesselId
      )
      console.log('[Create Requisition Add Item] isServicesCategory:', isServicesCategory)
      const subHeadsResult = await sharedLoadScopedInventorySubHeads({
        currentUser,
        inventoryScopeAll,
        inventoryScopeCompany,
        inventoryScopeVessel,
        isServicesCategory,
        selectedVesselId: createScopedVesselId,
        selectedCompanyId: createScopedCompanyId,
        selectedCompanyName:
          companyOptions.find((company) => company.id === createScopedCompanyId)?.name ?? null,
        debugContext: 'Create Requisition',
        requireVesselWhenCompanySelected: Boolean(inventoryScopeAll && createScopedCompanyId),
        selectedHeadId: headId,
      })
      setCreateFormSubHeads(sharedDedupeInventorySubHeadRows(subHeadsResult.rows || []))
    } catch (error) {
      console.error('Error loading requisition subheads:', error)
      setCreateFormSubHeads([])
    }
  }

  const ensureAccountingDataLoaded = async () => {
    if (accountingAccounts.length > 0 && subAccounts.length > 0) return
    try {
      const [accountingData, subAccountsData] = await Promise.all([
        getAllAccountingAccounts(),
        getAllSubAccounts(),
      ])
      setAccountingAccounts(accountingData || [])
      setSubAccounts(subAccountsData || [])
    } catch (error) {
      console.error('Error loading accounting data:', error)
    }
  }

  const loadInventoryData = async () => {
    try {
      const subCategoryData = await getInventoryItemSubCategories(true)
      setInventoryItemSubCategories(subCategoryData || [])
      console.log('=== Inventory Data Loading Complete ===')
    } catch (error) {
      console.error('Error loading inventory data:', error)
    }
  }

  const loadInventoryDataForVessel = async (vesselId: number) => {
    try {
      const selectedVessel = availableVessels.find((v) => v.id === vesselId)

      if (!selectedVessel) {
        console.error('Selected vessel not found:', vesselId)
        return
      }

      const headsResult = await sharedLoadScopedInventoryHeads({
        currentUser,
        inventoryScopeAll,
        inventoryScopeCompany,
        inventoryScopeVessel,
        isServicesCategory,
        selectedVesselId: vesselId,
        selectedCompanyId: createScopedCompanyId,
        selectedCompanyName:
          companyOptions.find((company) => company.id === createScopedCompanyId)?.name ?? null,
        debugContext: 'Create Requisition',
        requireVesselWhenCompanySelected: Boolean(inventoryScopeAll && createScopedCompanyId),
      })
      const subHeadsResult = await sharedLoadScopedInventorySubHeads({
        currentUser,
        inventoryScopeAll,
        inventoryScopeCompany,
        inventoryScopeVessel,
        isServicesCategory,
        selectedVesselId: vesselId,
        selectedCompanyId: createScopedCompanyId,
        selectedCompanyName:
          companyOptions.find((company) => company.id === createScopedCompanyId)?.name ?? null,
        debugContext: 'Create Requisition',
        requireVesselWhenCompanySelected: Boolean(inventoryScopeAll && createScopedCompanyId),
      })
      setInventoryItemHeads(sharedDedupeInventoryHeadRows(headsResult.rows || []))
      setInventoryItemSubHeads(sharedDedupeInventorySubHeadRows(subHeadsResult.rows || []))
      setCreateFormSubHeads([])

      setFilteredParts([])
      setFilteredSubComponents([])

      console.log('=== Vessel-specific head/subhead loading complete ===')
    } catch (error) {
      console.error('Error loading inventory data for vessel:', error)
      setCreateFormHeads([])
      setCreateFormSubHeads([])
    }
  }

  const loadSubCatalogues = async (subAccountId: number) => {
    setIsLoadingSubCatalogues(true)
    try {
      console.log('Loading sub catalogues for sub account ID:', subAccountId)

      const selectedVessel = availableVessels.find((v) => v.id === selectedVesselIdNumber)
      const vesselTypeString =
        typeof selectedVessel?.vesselType === 'string'
          ? selectedVessel.vesselType
          : undefined

      const vesselIdToUse = selectedVesselIdNumber ?? undefined

      const subCataloguesData = await getSubCataloguesBySubAccount(
        subAccountId,
        vesselIdToUse ?? undefined,
        vesselTypeString ?? undefined
      )

      setFilteredSubCatalogues(subCataloguesData)
    } catch (error) {
      console.error('Error loading sub catalogues:', error)
      setFilteredSubCatalogues([])
    } finally {
      setIsLoadingSubCatalogues(false)
    }
  }

  const loadSubComponentsByPart = async (partId: number) => {
    try {
      console.log('Loading sub components for part ID:', partId)
      const partSubComponents = dedupeSubComponentRows((await getSubComponentsByPartId(partId)) || [])
      setFilteredSubComponents(partSubComponents)
    } catch (error) {
      console.error('Error loading sub components:', error)
      setFilteredSubComponents([])
    }
  }

  const loadDefectOptions = useCallback(async () => {
    if (!visible) return

    const scopedCompanyId = inventoryScopeAll ? selectedCompanyIdNumber : createScopedCompanyId
    const scopedVesselId = createScopedVesselId
    const companyGroupId = scopedCompanyId ?? undefined
    const vesselId = scopedVesselId ?? undefined

    setIsLoadingDefectOptions(true)
    try {
      const defects = await searchDefects({
        companyGroupId,
        vesselId,
        activeOnly: true,
      })
      setDefectOptions(defects || [])
    } catch (error) {
      console.error('Error loading defect options for requisition create:', error)
      setDefectOptions([])
    } finally {
      setIsLoadingDefectOptions(false)
    }
  }, [visible, inventoryScopeAll, selectedCompanyIdNumber, createScopedCompanyId, createScopedVesselId])

  const loadNextCode = async (vesselIdForCode?: number | null) => {
    try {
      const isAPIReachable = await testRequisitionAPI()
      if (!isAPIReachable) {
        console.warn('API not reachable, using fallback code generation')
      }

      const nextCode = await getNextRequisitionCode(vesselIdForCode ?? undefined)
      setRequisitionCode(nextCode)
      setFormData((prev) => ({...prev, title: nextCode}))
    } catch (error) {
      console.error('Error loading next code:', error)

      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      const randomNum = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, '0')
      const fallbackCode = `REQ-${timestamp}-${randomNum}`

      setRequisitionCode(fallbackCode)
      setFormData((prev) => ({...prev, title: fallbackCode}))
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      title: requisitionCode || '',
      description: '',
      type: 'STORES' as any,
      urgency: 'MEDIUM',
      itemMode: 'TECHNICAL',
      requestedPortId: '',
      customPortName: '',
      requiredDeliveryDate: '',
      categoryId: '',
      headId: '',
      subHeadId: '',
      partId: '',
    subComponentId: '',
      equipmentId: '',
      storeSubCategoryId: '',
      storeSubCategoryName: '',
      linkedDefectId: '',
      linkedDefectNumber: '',
      linkedDefectTitle: '',
      itemName: '',
      quantity: '',
      uom: 'PCS',
      estimatedUnitPrice: '',
      equipmentLink: '',
      make: '',
      model: '',
      serialNumber: '',
    })
    setCreateRegionId(null)
    setCreateRegionName(null)

    setErrors({})
    setSelectedFiles([])
    setFilteredSubCatalogues([])
    setFilteredSubComponents([])
    setFilteredParts([])
    setIsCreatingPort(false)
    setCreatedPortId(null)
    setPortSearchText('')
    setFormEquipmentOptions([])
    setEquipmentLoadStatus('idle')
    setEquipmentLoadMessage(null)
    equipmentRequestRef.current += 1
    setCreateFormHeads([])
    setCreateFormSubHeads([])
  }

  // Company-wide categories: load when tenant scope resolves; never gated by vessel.
  useEffect(() => {
    if (!visible) return

    let cancelled = false

    const loadCompanyCategories = async () => {
      setIsLoadingCategories(true)
      try {
        let response: unknown
        if (inventoryScopeAll && !resolvedCategoryCgaId) {
          response = await getAllInventoryCategoriesForAllCompanies()
        } else if (inventoryScopeVessel) {
          response = await loadRequisitionFormCategories()
        } else if (resolvedCategoryCgaId) {
          response = await loadRequisitionFormCategories(resolvedCategoryCgaId)
        } else {
          if (!cancelled) {
            setInventoryCategories([])
            toast.error('Could not resolve company for catalogue. Please contact your administrator.')
          }
          return
        }

        const rows = normalizeInventoryCategoryRows(response)
        if (!cancelled) {
          setInventoryCategories(rows)
        }
      } catch (error) {
        console.error('Error loading company categories:', error)
        if (!cancelled) {
          setInventoryCategories([])
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCategories(false)
        }
      }
    }

    loadCompanyCategories()

    return () => {
      cancelled = true
    }
  }, [visible, resolvedCategoryCgaId, inventoryScopeAll, inventoryScopeVessel, currentUser])

  // Run once when modal opens; don't re-run on category/item changes
  useEffect(() => {
    if (!visible) return

    loadNextCode(selectedVesselIdNumber)
    resetForm()
    loadInventoryData()

    if (inventoryScopeAll) {
      loadCompaniesAndVessels()
    } else if (canSelectVesselInCreate) {
      loadScopedVesselsForCompanyUser()
    }
  }, [visible])

  useEffect(() => {
    if (!visible || inventoryScopeAll) return

    if (!selectedCompanyIdNumber && createFallbackScope.companyId) {
      setSelectedCompanyId(createFallbackScope.companyId)
    }

    if (inventoryScopeVessel && !selectedVesselIdNumber && createFallbackScope.vesselId) {
      setSelectedVesselId(createFallbackScope.vesselId)
    }
  }, [
    visible,
    inventoryScopeAll,
    inventoryScopeVessel,
    selectedCompanyIdNumber,
    selectedVesselIdNumber,
    createFallbackScope.companyId,
    createFallbackScope.vesselId,
  ])

  // Reload heads/subheads when company or vessel changes for superadmin (categories are company-wide).
  useEffect(() => {
    if (!visible || !inventoryScopeAll) return
    if (selectedCompanyIdNumber && !selectedVesselIdNumber) {
      return
    }
    loadInventoryData()
  }, [selectedCompanyIdNumber, selectedVesselIdNumber, visible, inventoryScopeAll])

  useEffect(() => {
    if (!visible) return
    loadDefectOptions()
  }, [visible, loadDefectOptions])

  useEffect(() => {
    if (!formData.linkedDefectId) return
    const stillExists = dedupedDefectOptions.some(
      (defect) => Number(defect.id) === Number(formData.linkedDefectId)
    )
    if (!stillExists) {
      setFormData((prev) => ({
        ...prev,
        linkedDefectId: '',
        linkedDefectNumber: '',
        linkedDefectTitle: '',
      }))
    }
  }, [formData.linkedDefectId, dedupedDefectOptions])

  // reload heads/equipment when vessel changes for superadmin only (categories are company-wide)
  useEffect(() => {
    if (visible && inventoryScopeAll && selectedVesselIdNumber) {
      console.log(
        'Vessel selection changed, reloading categories for vessel ID:',
        selectedVesselIdNumber
      )
      loadNextCode(selectedVesselIdNumber)
      loadInventoryDataForVessel(selectedVesselIdNumber)
    }
  }, [selectedVesselIdNumber, visible, inventoryScopeAll, isSparesCategory])

  // Company-scoped users (company/CGA admins and operators) use the selected
  // vessel when requesting the next server-generated requisition code. The
  // vessel lookup DTO exposes fleetName; numbering remains owned by the API.
  useEffect(() => {
    if (!visible || !inventoryScopeCompany || !selectedVesselIdNumber) return
    loadNextCode(selectedVesselIdNumber)
  }, [selectedVesselIdNumber, visible, inventoryScopeCompany])

  useEffect(() => {
    if (!visible) return
    setFormData((prev) => ({...prev, equipmentId: ''}))
  }, [selectedVesselIdNumber, visible])

  useEffect(() => {
    if (!visible || !isServicesCategory) {
      setCreateFormHeads([])
      setCreateFormSubHeads([])
      return
    }
    if (inventoryScopeAll && selectedCompanyIdNumber && !selectedVesselIdNumber) {
      setCreateFormHeads([])
      setCreateFormSubHeads([])
      return
    }
    setFormData((prev) => ({
      ...prev,
      headId: '',
      subHeadId: '',
    }))
    loadCreateFormHeads()
    loadCreateFormSubHeads()
  }, [
    createScopedVesselId,
    createScopedCompanyId,
    visible,
    isServicesCategory,
    inventoryScopeAll,
    inventoryScopeCompany,
  ])

  useEffect(() => {
    if (!visible || !isServicesCategory) {
      if (!isServicesCategory) {
        setCreateFormSubHeads([])
      }
      return
    }
    if (inventoryScopeAll && selectedCompanyIdNumber && !selectedVesselIdNumber) {
      setCreateFormSubHeads([])
      return
    }
    loadCreateFormSubHeads(selectedHeadIdNumber ?? undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visible,
    isServicesCategory,
    selectedHeadIdNumber,
    createScopedVesselId,
    createScopedCompanyId,
    inventoryScopeAll,
  ])

  // load equipment separately for spares; no form reset
  useEffect(() => {
    if (!visible) return

    if (formData.itemMode === 'TECHNICAL' && isSparesCategory && selectedVesselIdNumber) {
      loadFormEquipmentOptions(selectedVesselIdNumber)
    } else {
      setFormEquipmentOptions([])
    }
  }, [visible, formData.itemMode, selectedVesselIdNumber, isSparesCategory])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target

    const resolveCategoryKind = (idStr?: string) => {
      const cat = inventoryCategories.find((c) => c.id === Number(idStr))
      const label = (cat?.name || '').toUpperCase()

      if (label.includes('STORE')) return 'STORES'
      if (label.includes('SERVICE')) return 'SERVICES'
      if (label.includes('GENERAL')) return 'GENERAL'
      if (label.includes('SPARE')) return 'SPARES'
      return ''
    }

    if (name === 'categoryId') {
      console.log('=== Category Changed ===')
      console.log('Selected Category ID:', value)

      const kind = resolveCategoryKind(value)
      const isSpares = kind === 'SPARES'
      setFilteredParts([])
      setFilteredSubComponents([])
      setCreateFormSubHeads([])

      setFormData((prev) => ({
        ...prev,
        categoryId: value,
        itemMode: isSpares ? 'TECHNICAL' : kind === 'SERVICES' ? 'TECHNICAL' : 'GENERAL',
        headId: '',
        subHeadId: '',
        partId: '',
        subComponentId: '',
        equipmentId: '',
        storeSubCategoryId: '',
        storeSubCategoryName: '',
      }))

      setErrors((prev) => ({
        ...prev,
        categoryId: '',
        equipmentId: '',
        headId: '',
        subHeadId: '',
      }))

      setCreateFormHeads([])
      if (value) {
        loadCreateFormHeads()
      }

      return
    }

    if (name === 'itemMode') {
      const nextMode = value as 'TECHNICAL' | 'GENERAL'
      setFormData((prev) => ({
        ...prev,
        itemMode: nextMode,
        equipmentId: nextMode === 'TECHNICAL' ? prev.equipmentId : '',
      }))

      if (nextMode !== 'TECHNICAL') {
        setFormEquipmentOptions([])
      }
      return
    }

    if (name === 'equipmentId') {
      setFormData((prev) => ({...prev, equipmentId: value}))
      return
    }

    if (name === 'subCatalogueId') {
      if (value) {
        const subCatalogueId = parseInt(value)
        const partsForSubCatalogue = parts.filter(
          (part) => part.subcatalogue?.id === subCatalogueId
        )
        setFilteredParts(dedupePartRows(partsForSubCatalogue))
      } else {
        const selectedCategory = inventoryCategories.find(
          (cat) => cat.id === parseInt(formData.categoryId)
        )

        const selectedVessel = availableVessels.find((v) => v.id === selectedVesselIdNumber)
        const vesselTypeString =
          typeof selectedVessel?.vesselType === 'string'
            ? selectedVessel.vesselType
            : undefined

        const partsForCategory = parts.filter((part) => {
          const partCategoryId = part.subcatalogue?.subCatalogueType?.id
          const matchesByCategoryId = partCategoryId === parseInt(formData.categoryId)
          const partVesselType = part.subcatalogue?.vesselType
          const matchesByVesselType = vesselTypeString ? partVesselType === vesselTypeString : false
          return matchesByCategoryId || matchesByVesselType
        })

        setFilteredParts(dedupePartRows(partsForCategory))
      }

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        partId: '',
        itemName: '',
      }))
    } else if (name === 'partId' && value) {
      const selectedPart =
        filteredParts.find((part) => part.id === parseInt(value)) ||
        parts.find((part) => part.id === parseInt(value))

      loadSubComponentsByPart(parseInt(value))

      if (selectedPart) {
        const autoFilledUom =
          selectedPart.uom?.code ||
          selectedPart.uom?.symbol ||
          selectedPart.uom?.name ||
          (typeof selectedPart.uom === 'string' ? selectedPart.uom : null) ||
          'PCS'

        const autoFilledMake = selectedPart.manufacturer || ''

        setFormData((prev) => ({
          ...prev,
          partId: value,
          itemName: selectedPart.name,
          uom: autoFilledUom,
          make: autoFilledMake,
          model: selectedPart.partNo || '',
          subComponentId: '',
        }))
      }
    } else if (name === 'headId') {
      setFormData((prev) => ({
        ...prev,
        headId: value,
        subHeadId: '',
      }))
      setCreateFormSubHeads([])
      if (value) {
        loadCreateFormSubHeads(Number(value))
      }
    } else if (name === 'linkedDefectId') {
      const selectedDefect = dedupedDefectOptions.find((defect) => Number(defect.id) === Number(value))
      setFormData((prev) => ({
        ...prev,
        linkedDefectId: value,
        linkedDefectNumber: value ? selectedDefect?.defectNumber || '' : '',
        linkedDefectTitle: value
          ? (selectedDefect?.displayTitle || selectedDefect?.description || '').trim()
          : '',
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files)
      setSelectedFiles((prev) => [...prev, ...filesArray])
    }
  }

  const loadScopedVesselsForCompanyUser = async () => {
    try {
      const companyUsername = (currentUser as any)?.companyGroupAdmin?.username
      const companyGroupAdminId =
        (currentUser as any)?.companyGroupAdminId ??
        (currentUser as any)?.companyGroupAdmin?.id ??
        (currentUser as any)?.companyAdmin?.cgaid?.id ??
        null

      let vessels: RequisitionFormVesselLookup[] = []
      if (companyUsername) {
        vessels = await getRequisitionFormVessels(companyUsername)
      } else {
        const allVessels = await getRequisitionFormVessels()
        vessels = companyGroupAdminId
          ? allVessels.filter((v) => v.companyGroupAdminId === companyGroupAdminId)
          : allVessels
      }

      setAvailableVessels(vessels)

      const currentUserVesselId = (currentUser as any)?.vessel?.id ?? null
      if (currentUserVesselId && vessels.some((v) => v.id === currentUserVesselId)) {
        setSelectedVesselId(currentUserVesselId)
      } else if (vessels.length > 0) {
        setSelectedVesselId(vessels[0].id)
      } else {
        setSelectedVesselId(null)
      }
    } catch (error) {
      console.error('Error loading company-scoped vessels:', error)
      setAvailableVessels([])
      setSelectedVesselId(null)
      toast.error('Failed to load vessels for your company')
    }
  }

  const handlePortSearchChange = (value: string) => {
    setPortSearchText(value)

    const normalized = value.trim().toLowerCase()
    const matchedPort = ports.find(
      (port) => getPortDisplayLabel(port).trim().toLowerCase() === normalized
    )

    if (matchedPort) {
      setCreatedPortId(null)
      setFormData((prev) => ({
        ...prev,
        requestedPortId: String(matchedPort.id),
        customPortName: '',
      }))
      if (errors.requestedPortId || errors.customPortName) {
        setErrors((prev) => ({
          ...prev,
          requestedPortId: '',
          customPortName: '',
        }))
      }
      return
    }

    if (!normalized) {
      setCreatedPortId(null)
      setFormData((prev) => ({
        ...prev,
        requestedPortId: '',
        customPortName: '',
      }))
    } else {
      setCreatedPortId(null)
      setFormData((prev) => ({
        ...prev,
        requestedPortId: 'other',
        customPortName: value,
      }))
    }
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleAddPort = async () => {
    if (!formData.customPortName.trim()) {
      toast.error('Please enter a port name')
      return
    }

    setIsCreatingPort(true)
    try {
      const newPort = await createPort({
        name: formData.customPortName.trim(),
        main_port: formData.customPortName.trim(),
      })

      setCreatedPortId(newPort.id)

      setFormData((prev) => ({
        ...prev,
        requestedPortId: newPort.id.toString(),
        customPortName: '',
      }))
      setPortSearchText(getPortDisplayLabel(newPort))

      const updatedPorts = await getPorts()
      setPorts(updatedPorts)

      toast.success(`Port "${newPort.name}" created successfully!`)
    } catch (error: any) {
      console.error('Error creating port:', error)
      toast.error('Failed to create port')
    } finally {
      setIsCreatingPort(false)
    }
  }

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {}

    if (!formData.name?.trim()) newErrors.name = 'Requisition name is required'
    if (!formData.categoryId) newErrors.categoryId = 'Category is required'

    const isTechnical = formData.itemMode === 'TECHNICAL'
    const requiresEquipment = isTechnical && isSparesCategory

    if (requiresEquipment && !formData.equipmentId) {
      newErrors.equipmentId = 'Equipment is required for technical requisitions'
    }

    // Head/Sub Head are optional header classifiers in create flow.

    if (!formData.requestedPortId) {
      newErrors.requestedPortId = 'Requested Port is required'
    } else if (formData.requestedPortId === 'other') {
      if (!createdPortId && !formData.customPortName.trim()) {
        newErrors.customPortName = 'Please enter a port name and click "Add Port"'
      } else if (!createdPortId && formData.customPortName.trim()) {
        newErrors.customPortName = 'Please click "Add Port" to create the port before submitting'
      }
    }

    // Required Delivery Date must be present and not in the past
    if (!formData.requiredDeliveryDate) {
      newErrors.requiredDeliveryDate = 'Required Delivery Date is required'
    } else {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const reqDate = new Date(formData.requiredDeliveryDate)
      const reqDateOnly = new Date(reqDate.getFullYear(), reqDate.getMonth(), reqDate.getDate())
      if (reqDateOnly < today) {
        newErrors.requiredDeliveryDate = 'Required Delivery Date cannot be in the past'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    if (!selectedVesselId) {
      toast.error('Please select a vessel before creating a requisition')
      return
    }

    setIsSubmitting(true)
    try {
      const selectedCategoryId = parseInt(formData.categoryId)

      const payload: CreateRequisitionRequest = {
        name: formData.name?.trim(),
        title: formData.title || requisitionCode,
        requisition_code: requisitionCode,
        description: formData.description || 'N/A',
        regionId: createRegionId,
        type: formData.urgency,
        requestFor: formData.itemMode === 'GENERAL' ? 'GENERAL_STORES' : 'TECHNICAL',
        categoryId: selectedCategoryId,
        inventoryItemSubCategoryId:
          formData.storeSubCategoryId && /^\d+$/.test(formData.storeSubCategoryId)
            ? Number(formData.storeSubCategoryId)
            : undefined,
        inventoryItemSubCategoryName: formData.storeSubCategoryName || undefined,
        vesselId: selectedVesselId,
        requestedPortId:
          formData.requestedPortId === 'other'
            ? createdPortId || 0
            : parseInt(formData.requestedPortId),
        customPortName: undefined,
        requiredDeliveryDate:
          formData.requiredDeliveryDate || new Date().toISOString().split('T')[0],
        linkedDefectId: formData.linkedDefectId ? Number(formData.linkedDefectId) : undefined,
        linkedDefectNumber: formData.linkedDefectNumber?.trim() || undefined,
        linkedDefectTitle: formData.linkedDefectTitle?.trim() || undefined,
        inventoryItemHeadId: formData.headId ? Number(formData.headId) : undefined,
        inventoryItemSubHeadId: formData.subHeadId ? Number(formData.subHeadId) : undefined,
        equipmentId:
          formData.itemMode === 'TECHNICAL' && formData.equipmentId
            ? Number(formData.equipmentId)
            : undefined,
        status: 'DRAFT',
      }

      const result = await createRequisition(payload, selectedFiles)

      toast.success('Requisition created successfully! Please add item details.')
      onSubmit()
      onCreated?.(result.id)
      handleClose()
    } catch (error: any) {
      console.error('Error creating requisition:', error)

      let errorMessage = 'Failed to create requisition'

      if (error.message.includes('Cannot connect to API server')) {
        errorMessage =
          'Cannot connect to API server. Please check your internet connection and try again.'
      } else if (error.message.includes('Method not allowed')) {
        errorMessage =
          'The API does not support creating requisitions. Please contact your administrator.'
      } else if (error.message.includes('API endpoint not found')) {
        errorMessage = 'API endpoint not found. Please check the server configuration.'
      } 

      toast.error('Failed to create requisition. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const mapDaysToUrgency = (days: string): RequisitionUrgency => {
    if (days === '0') return 'HIGH'
    if (days === '15') return 'MEDIUM'
    return 'LOW'
  }

  if (!visible) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center procurement-document-modal'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div
        className='modal-dialog modal-xl modal-dialog-centered'
        role='document'
        style={{maxHeight: '95vh', margin: '1.75rem auto'}}
      >
        <div
          className='modal-content bg-white'
          style={{color: '#181C32', display: 'flex', flexDirection: 'column', maxHeight: '95vh'}}
        >
          <div className='modal-header' style={{flexShrink: 0}}>
            <div>
              <h5 className='modal-title'>Create New Requisition</h5>
              <p className='text-muted mb-0 fs-7'>
                Requisition Code: <strong>{requisitionCode}</strong>
              </p>
            </div>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <div className='modal-body' style={{overflowY: 'auto', flexGrow: 1, minHeight: 0}}>
            <div className='row g-3'>
              <div className='col-md-12'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                  Requisition Code
                </label>
                <input
                  type='text'
                  className='form-control'
                  name='title'
                  value={requisitionCode || formData.title}
                  onChange={() => {}}
                  placeholder='Will use Requisition Code'
                  style={{color: '#000'}}
                  disabled
                />
              </div>

              <div className='col-md-12'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                  Requisition Name
                </label>
                <input
                  type='text'
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder='Enter short searchable requisition name'
                  style={{color: '#000'}}
                />
                {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
              </div>

              {inventoryScopeAll && (
                <>
                  <div className='col-md-12'>
                    <label className='form-label required fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                      Company
                    </label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedCompanyId ?? ''}
                      onChange={(e) => {
                        const cId = toPositiveNumber(e.target.value)
                        setSelectedCompanyId(cId)
                        setSelectedVesselId(null)

                        const company = companyOptions.find((c) => c.id === cId)
                        const username = String(company?.username || '').trim()
                        setSelectedCompanyUsername(username)

                        if (username) {
                          console.log('[Create Requisition] loading vessels by companyUsername:', username)
                          loadVesselsForCompany(username)
                        } else {
                          setAvailableVessels([])
                        }
                      }}
                      style={{color: '#000'}}
                    >
                      <option value=''>Select company</option>
                      {companyOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name || c.username || c.id}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>

                  <div className='col-md-12'>
                    <label
                      className='form-label required fw-semibold fs-6 mb-2'
                      style={{color: '#181C32'}}
                    >
                      Vessel
                    </label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedVesselId ?? ''}
                      onChange={(e) => {
                        const newVesselId = toPositiveNumber(e.target.value)
                        setSelectedVesselId(newVesselId)
                      }}
                      style={{color: '#000'}}
                    >
                    <option value=''>All vessels</option>
                    {availableVessels.map((vessel) => (
                      <option key={vessel.id} value={vessel.id}>
                        {vessel.fleetName} ({vessel.vesselType})
                      </option>
                    ))}
                    </SearchableSelect>
                    <div className='form-text'>Select the company then a vessel for this requisition</div>
                  </div>
                </>
              )}

              {canSelectVesselInCreate && (
                <div className='col-md-12'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                    Vessel
                  </label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedVesselId ?? ''}
                    onChange={(e) => {
                      const newVesselId = toPositiveNumber(e.target.value)
                      setSelectedVesselId(newVesselId)
                    }}
                    style={{color: '#000'}}
                  >
                    <option value=''>Select vessel</option>
                    {availableVessels.map((vessel) => (
                      <option key={vessel.id} value={vessel.id}>
                        {vessel.fleetName} ({vessel.vesselType})
                      </option>
                    ))}
                  </SearchableSelect>
                  <div className='form-text'>Select vessel for this requisition</div>
                </div>
              )}

              <div className='col-md-12'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                  Description
                </label>
                <textarea
                  className='form-control'
                  name='description'
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder='Describe the item or service needed'
                  style={{color: '#000'}}
                />
              </div>

              <div className='col-md-12'>
                <RequisitionRegionField regionId={createRegionId} regionName={createRegionName}
                  onChange={(id, name) => { setCreateRegionId(id); setCreateRegionName(name) }} />
              </div>

              <SearchableLookupField
                label='Category'
                required
                value={formData.categoryId ? String(formData.categoryId) : ''}
                options={createFormCategoryOptions}
                onChange={(value) =>
                  handleInputChange({target: {name: 'categoryId', value}} as any)
                }
                placeholder={isLoadingCategories ? 'Loading categories...' : 'Type to search categories'}
                helperText='Type to filter available categories.'
                disabled={isLoadingCategories}
                error={errors.categoryId}
                listId='requisition-category-suggestions'
              />

              {isServicesCategory && (
                <SearchableLookupField
                  label='Head'
                  value={formData.headId}
                  options={uniqueCreateFormHeads.map((head) => ({
                    id: head.id,
                    label: head.name || `Head ${head.id}`,
                  }))}
                  onChange={(value) =>
                    handleInputChange({target: {name: 'headId', value}} as any)
                  }
                  placeholder='Type to search heads'
                  helperText='Optional. Search the company library.'
                  listId='requisition-head-suggestions'
                />
              )}

              {isServicesCategory && (
                <SearchableLookupField
                  label='Sub Head'
                  value={formData.subHeadId}
                  options={uniqueCreateFormSubHeads.map((subHead) => ({
                    id: subHead.id,
                    label: subHead.name || `Sub Head ${subHead.id}`,
                  }))}
                  onChange={(value) =>
                    handleInputChange({target: {name: 'subHeadId', value}} as any)
                  }
                  placeholder='Type to search sub heads'
                  helperText='Optional. Search the company library.'
                  listId='requisition-subhead-suggestions'
                />
              )}

              <div className='col-md-6'>
                <label
                  className='form-label required fw-semibold fs-6 mb-2'
                  style={{color: '#181C32'}}
                >
                  Type (Urgency)
                </label>
                <SearchableSelect
                  className='form-select'
                  value={
                    formData.urgency === 'HIGH' ? '0' : formData.urgency === 'MEDIUM' ? '15' : '90'
                  }
                  onChange={(e) => {
                    const mapped = mapDaysToUrgency(e.target.value)
                    setFormData((prev) => ({...prev, urgency: mapped}))
                  }}
                >
                  <option value='90'>90 days (Normal)</option>
                  <option value='15'>15 days (Medium)</option>
                  <option value='0'>0 Days (Urgent)</option>
                </SearchableSelect>
              </div>

              {hasCategorySelected && categoryLabel.includes('STORE') && (
                <div className='col-md-6'>
                  <label
                    className='form-label fw-semibold fs-6 mb-2'
                    style={{color: '#181C32'}}
                  >
                    Sub Category (optional)
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='storeSubCategoryId'
                    value={formData.storeSubCategoryId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        storeSubCategoryId: e.target.value,
                        storeSubCategoryName:
                          storeSubCategoryOptions.find((o) => o.value === e.target.value)?.label ||
                          '',
                      }))
                    }
                  >
                    <option value=''>Select Sub Category (optional)</option>
                    {storeSubCategoryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              {hasCategorySelected && isSparesCategory && (
                <div className='col-md-6'>
                  <label
                    className='form-label required fw-semibold fs-6 mb-2'
                    style={{color: '#181C32'}}
                  >
                    Equipment
                  </label>
                  <SearchableSelect
                    className={`form-select ${errors.equipmentId ? 'is-invalid' : ''}`}
                    name='equipmentId'
                    value={formData.equipmentId}
                    onChange={handleInputChange}
                    disabled={!selectedVesselId || equipmentLoadStatus === 'loading'}
                  >
                    <option value=''>
                      {!selectedVesselId
                        ? 'Select vessel first'
                        : equipmentLoadStatus === 'loading'
                          ? 'Loading equipment...'
                          : equipmentLoadStatus === 'empty'
                            ? 'No equipment found for selected vessel'
                            : equipmentLoadStatus === 'forbidden'
                              ? 'Access denied'
                              : equipmentLoadStatus === 'error'
                                ? 'Unable to load equipment'
                                : 'Select Equipment'}
                    </option>
                    {formEquipmentOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SearchableSelect>
                  {equipmentLoadMessage &&
                    equipmentLoadStatus !== 'loaded' &&
                    equipmentLoadStatus !== 'loading' && (
                      <div className='form-text text-danger'>{equipmentLoadMessage}</div>
                    )}
                  {errors.equipmentId && <div className='invalid-feedback'>{errors.equipmentId}</div>}
                </div>
              )}

              <div className='col-md-6'>
                <label
                  className='form-label required fw-semibold fs-6 mb-2'
                  style={{color: '#181C32'}}
                >
                  Requested Port
                </label>
                <input
                  type='text'
                  className={`form-control ${errors.requestedPortId ? 'is-invalid' : ''}`}
                  value={portSearchText}
                  onChange={(e) => handlePortSearchChange(e.target.value)}
                  placeholder='Type and search port'
                  list='requisition-port-suggestions'
                  style={{
                    backgroundColor: '#F5F8FA',
                    border: '1px solid #E4E6EF',
                    color: '#181C32',
                    fontSize: '14px',
                  }}
                />
                <datalist id='requisition-port-suggestions'>
                  {filteredPortSuggestions.map((port) => (
                    <option key={port.id} value={getPortDisplayLabel(port)} />
                  ))}
                </datalist>
                <div className='form-text'>
                  Type to filter ports and select from suggestions. If not available, type new port and click Add Port.
                </div>

                {errors.requestedPortId && (
                  <div className='invalid-feedback d-block'>{errors.requestedPortId}</div>
                )}

                {formData.requestedPortId === 'other' && (
                  <div className='mt-3'>
                    <label
                      className='form-label required fw-semibold fs-6 mb-2'
                      style={{color: '#181C32'}}
                    >
                      Port Name
                    </label>

                    <div className='d-flex gap-2'>
                      <input
                        type='text'
                        className={`form-control ${errors.customPortName ? 'is-invalid' : ''}`}
                        name='customPortName'
                        value={formData.customPortName}
                        onChange={(e) => {
                          const value = e.target.value
                          setFormData((prev) => ({
                            ...prev,
                            customPortName: value,
                            requestedPortId: value.trim() ? 'other' : '',
                          }))
                          setPortSearchText(value)
                        }}
                        placeholder='Enter port name'
                        disabled={isCreatingPort}
                        style={{
                          backgroundColor: '#F5F8FA',
                          border: '1px solid #E4E6EF',
                          color: '#181C32',
                          fontSize: '14px',
                        }}
                      />

                      <button
                        type='button'
                        className='btn btn-primary'
                        onClick={handleAddPort}
                        disabled={isCreatingPort || !formData.customPortName.trim()}
                        style={{
                          minWidth: '100px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isCreatingPort ? (
                          <>
                            <span
                              className='spinner-border spinner-border-sm me-2'
                              role='status'
                            ></span>
                            Adding...
                          </>
                        ) : (
                          'Add Port'
                        )}
                      </button>
                    </div>

                    {errors.customPortName && (
                      <div className='invalid-feedback d-block'>{errors.customPortName}</div>
                    )}

                    {createdPortId && (
                      <div className='alert alert-success mt-2 mb-0'>
                        <i className='fas fa-check-circle me-2'></i>
                        Port created successfully! You can now create the requisition.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                  Required Delivery Date
                </label>
                <input
                  type='date'
                  className={`form-control ${errors.requiredDeliveryDate ? 'is-invalid' : ''}`}
                  name='requiredDeliveryDate'
                  value={formData.requiredDeliveryDate}
                  onChange={handleInputChange}
                  style={{color: '#000'}}
                />
                {errors.requiredDeliveryDate && (
                  <div className='invalid-feedback d-block'>{errors.requiredDeliveryDate}</div>
                )}
              </div>

              <div className='col-md-12'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#181C32'}}>
                  Link Defect (optional)
                </label>
                <SearchableSelect
                  className='form-select'
                  name='linkedDefectId'
                  value={(formData as any).linkedDefectId || ''}
                  onChange={handleInputChange}
                  disabled={isLoadingDefectOptions}
                  style={{color: '#000'}}
                >
                  <option value=''>
                    {isLoadingDefectOptions
                      ? 'Loading defects...'
                      : dedupedDefectOptions.length > 0
                      ? 'Select Defect'
                      : 'No defects available'}
                  </option>
                  {dedupedDefectOptions.map((defect) => (
                    <option key={defect.id} value={String(defect.id)}>
                      {(defect.defectNumber || `Defect #${defect.id}`) +
                        ` - ${(defect.displayTitle || defect.description || 'No description').slice(0, 80)}`}
                    </option>
                  ))}
                </SearchableSelect>
                {(formData as any).linkedDefectNumber && (
                  <div className='form-text'>
                    Linked: {(formData as any).linkedDefectNumber}
                    {(formData as any).linkedDefectTitle
                      ? ` - ${(formData as any).linkedDefectTitle}`
                      : ''}
                  </div>
                )}
              </div>

            </div>
          </div>

          <div
            className='modal-footer'
            style={{flexShrink: 0, borderTop: '1px solid #E4E6EF', padding: '1rem'}}
          >
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>

            <button
              type='button'
              className='btn btn_primary'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Requisition'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ViewRequisitionModalProps {
  visible: boolean
  onClose: () => void
  requisition: RequisitionDisplay | null
  attachments?: FileRefDto[]
  onAttachmentClick?: (attachment: FileRefDto) => void
  viewLines: RequisitionLine[]
  viewLinesLoading: boolean
  canEditQuantities?: boolean
  quantityDrafts?: Record<number, string>
  updatingLineId?: number | null
  onQuantityDraftChange?: (lineId: number, value: string) => void
  onSaveLineQuantity?: (lineId: number) => void
  canSubmitAction?: boolean
  canAddItemsAction?: boolean
  canVesselApproveAction?: boolean
  canShoreApproveAction?: boolean
  canReviewAction?: boolean
  canRejectAction?: boolean
  canDeleteAction?: boolean
  canDownloadAction?: boolean
  onSubmitAction?: () => void
  onAddItemsAction?: () => void
  onVesselApproveAction?: () => void
  onShoreApproveAction?: () => void
  onReviewAction?: () => void
  onRejectAction?: () => void
  onDeleteAction?: () => void
  onDownloadAction?: () => void
}

const ViewRequisitionModal: FC<ViewRequisitionModalProps> = ({
  visible,
  onClose,
  requisition,
  attachments,
  onAttachmentClick,
  viewLines,
  viewLinesLoading,
  canEditQuantities = false,
  quantityDrafts = {},
  updatingLineId,
  onQuantityDraftChange,
  onSaveLineQuantity,
  canSubmitAction = false,
  canAddItemsAction = false,
  canVesselApproveAction = false,
  canShoreApproveAction = false,
  canReviewAction = false,
  canRejectAction = false,
  canDeleteAction = false,
  canDownloadAction = false,
  onSubmitAction,
  onAddItemsAction,
  onVesselApproveAction,
  onShoreApproveAction,
  onReviewAction,
  onRejectAction,
  onDeleteAction,
  onDownloadAction,
}) => {
  if (!visible || !requisition) return null

  const getStatusBadge = (status: string) => {
    const statusMap: {[key: string]: {bg: string; text: string}} = {
      Draft: {bg: 'bg-secondary', text: 'Draft'},
      Submitted: {bg: 'bg-info', text: 'Submitted'},
      'Vessel Approved': {bg: 'bg-primary', text: 'Vessel Approved'},
      'Shore Approved': {bg: 'bg-success', text: 'Shore Approved'},
      'RFQ Rework Pending': {bg: 'bg-warning', text: 'RFQ Rework Pending'},
      'Partially Completed': {bg: 'bg-warning', text: 'Partially Completed'},
      'To Be Revised': {bg: 'bg-warning', text: 'To Be Revised'},
      Rejected: {bg: 'bg-danger', text: 'Rejected'},
    }
    const statusInfo = statusMap[status] || {bg: 'bg-secondary', text: status}
    return <span className={`badge ${statusInfo.bg} text-white`}>{statusInfo.text}</span>
  }

  const getUrgencyBadge = (urgency: string) => {
    const urgencyMap: {[key: string]: {bg: string; text: string}} = {
      High: {bg: 'bg-danger', text: 'High'},
      Medium: {bg: 'bg-warning', text: 'Medium'},
      Low: {bg: 'bg-info', text: 'Low'},
    }
    const urgencyInfo = urgencyMap[urgency] || {bg: 'bg-secondary', text: urgency}
    return <span className={`badge ${urgencyInfo.bg} text-white`}>{urgencyInfo.text}</span>
  }

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div
        className='modal-dialog modal-xl modal-dialog-centered procurement-view-modal-dialog'
        role='document'
        style={{
          width: 'min(92vw, 1400px)',
          maxWidth: 'min(92vw, 1400px)',
          minWidth: 'min(1100px, 92vw)',
          margin: '1rem auto',
        }}
      >
        <div
          className='modal-content procurement-document-modal-content bg-white'
          style={{display: 'flex', flexDirection: 'column', maxHeight: '95vh'}}
        >
          <div className='modal-header border-bottom procurement-print-hide' style={{flexShrink: 0}}>
            <div>
              <h3 className='modal-title fw-bold text-dark mb-1'>Requisition Details</h3>
              <p className='text-muted mb-0 fs-7'>{requisition.reqNumber}</p>
            </div>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div className='modal-body procurement-document-modal-body' style={{overflowY: 'auto', flexGrow: 1, minHeight: 0}}>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div className='d-flex gap-2'>
                {getStatusBadge(requisition.status)}
                {getUrgencyBadge(requisition.urgency)}
                <span className='badge bg-light text-dark'>
                  {viewLines?.length ?? 0} {(viewLines?.length ?? 0) === 1 ? 'Item' : 'Items'}
                </span>
              </div>
              <div className='d-flex align-items-center gap-2 procurement-print-hide'>
                {canSubmitAction && (
                  <button type='button' className='btn btn-sm btn-primary' onClick={onSubmitAction}>
                    Submit
                  </button>
                )}
                {canVesselApproveAction && (
                  <button
                    type='button'
                    className='btn btn-sm btn-success'
                    onClick={onVesselApproveAction}
                  >
                    Approve
                  </button>
                )}
                {canShoreApproveAction && (
                  <button
                    type='button'
                    className='btn btn-sm btn-success'
                    onClick={onShoreApproveAction}
                  >
                    Shore Approve
                  </button>
                )}
                {canReviewAction && (
                  <button
                    type='button'
                    className='btn btn-sm btn-warning text-dark'
                    onClick={onReviewAction}
                  >
                    Review
                  </button>
                )}
                {canRejectAction && (
                  <button type='button' className='btn btn-sm btn-danger' onClick={onRejectAction}>
                    Reject
                  </button>
                )}
                {canAddItemsAction && (
                  <button type='button' className='btn btn-sm btn-info' onClick={onAddItemsAction}>
                    Add Items
                  </button>
                )}
                {canDeleteAction && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-danger'
                    onClick={onDeleteAction}
                  >
                    Delete
                  </button>
                )}
                {canDownloadAction && (
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    onClick={onDownloadAction}
                  >
                    Download PDF
                  </button>
                )}
              </div>
            </div>

            {requisition.workflowRemark && (
              <div
                className={`alert py-2 px-3 mb-3 ${
                  requisition.workflowStage === 'Rejected' ? 'alert-danger' : 'alert-warning'
                }`}
              >
                <div className='fw-bold mb-1'>
                  {requisition.workflowStage === 'Rejected'
                    ? 'Company Rejection Remark'
                    : 'Company Review Remark'}
                </div>
                <div className='mb-0'>{requisition.workflowRemark}</div>
              </div>
            )}

            <RequisitionLECRDocument
              requisition={requisition}
              viewLines={viewLines}
              viewLinesLoading={viewLinesLoading}
              canEditQuantities={canEditQuantities}
              quantityDrafts={quantityDrafts}
              updatingLineId={updatingLineId}
              onQuantityDraftChange={onQuantityDraftChange}
              onSaveLineQuantity={onSaveLineQuantity}
              attachments={attachments}
            />
          </div>

          <div
            className='modal-footer border-top procurement-print-hide'
            style={{flexShrink: 0, padding: '1rem'}}
          >
            <button type='button' className='btn btn-light' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const RequisitionList: FC = () => {
  const navigate = useNavigate()
  const {state: globalState} = useGlobalFilter()
  const {currentUser, auth} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [requisitionData, setRequisitionData] = useState<RequisitionDisplay[]>([])
  const [requisitionTotalElements, setRequisitionTotalElements] = useState(0)
  const [requisitionTotalPages, setRequisitionTotalPages] = useState(1)
  const [requisitionAttachments, setRequisitionAttachments] = useState<{
    [key: number]: FileRefDto[]
  }>({})
  const [sortConfig, setSortConfig] = useState<{
    key: keyof RequisitionDisplay | null
    direction: 'asc' | 'desc'
  }>({key: null, direction: 'asc'})
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [isRequisitionLibrariesOpen, setIsRequisitionLibrariesOpen] = useState(false)
  const [activeRequisitionLibrary, setActiveRequisitionLibrary] = useState<
    'category' | 'head' | 'subHead' | 'subCategory' | 'address' | 'terms' | 'accountingCode' | 'subAccounting' | 'uom' | 'port'
  >('category')
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState<InventoryCategory | null>(null)
  const [editingHead, setEditingHead] = useState<InventoryItemHead | null>(null)
  const [editingSubHead, setEditingSubHead] = useState<InventoryItemSubHead | null>(null)
  const [editingSubCategory, setEditingSubCategory] = useState<InventoryItemSubCategory | null>(null)
  const [editingAccountingCode, setEditingAccountingCode] = useState<AccountingAccount | null>(null)
  const [editingSubAccounting, setEditingSubAccounting] = useState<SubAccount | null>(null)
  const [showHeadForm, setShowHeadForm] = useState(false)
  const [showSubHeadForm, setShowSubHeadForm] = useState(false)
  const [showSubCategoryForm, setShowSubCategoryForm] = useState(false)
  const [showAddressForm, setShowAddressForm] = useState(false)
  const [showAccountingCodeForm, setShowAccountingCodeForm] = useState(false)
  const [showSubAccountingForm, setShowSubAccountingForm] = useState(false)
  const [showPortForm, setShowPortForm] = useState(false)
  const [editingPort, setEditingPort] = useState<Port | null>(null)
  const [portForm, setPortForm] = useState({name: '', latitude: '', longitude: ''})
  const [portLibraryRows, setPortLibraryRows] = useState<Port[]>([])
  const [portLibraryPage, setPortLibraryPage] = useState(0)
  const [portLibrarySize, setPortLibrarySize] = useState(10)
  const [portLibraryTotalElements, setPortLibraryTotalElements] = useState(0)
  const [portLibraryTotalPages, setPortLibraryTotalPages] = useState(0)
  const [portLibrarySearch, setPortLibrarySearch] = useState('')
  const [debouncedPortLibrarySearch, setDebouncedPortLibrarySearch] = useState('')
  const [portLibraryLoading, setPortLibraryLoading] = useState(false)
  const [portLibrarySaving, setPortLibrarySaving] = useState(false)
  const [libraryCompanyFilterId, setLibraryCompanyFilterId] = useState<number | null>(null)
  const [libraryVesselFilterId, setLibraryVesselFilterId] = useState<number | null>(null)
  const [libraryHeadFilterId, setLibraryHeadFilterId] = useState<number | null>(null)
  const [librarySearchByTab, setLibrarySearchByTab] = useState<Record<LibrarySearchTab, string>>({
    category: '',
    head: '',
    subHead: '',
    subCategory: '',
    address: '',
    terms: '',
    accountingCode: '',
    subAccounting: '',
  })
  const [debouncedLibrarySearchByTab, setDebouncedLibrarySearchByTab] = useState(librarySearchByTab)
  const [libraryCompanyMasterOptions, setLibraryCompanyMasterOptions] = useState<CompanyGroupAdmin[]>([])
  const [subCategoryForm, setSubCategoryForm] = useState({name: '', description: '', active: true})
  const [addressForm, setAddressForm] = useState<AddressFormState>(defaultAddressFormState)
  type VesselNoLinesConfirmState = {open: boolean; reqId: number | null}
  const [vesselNoLinesConfirm, setVesselNoLinesConfirm] = useState<VesselNoLinesConfirmState>({
    open: false,
    reqId: null,
  })
  const [viewLines, setViewLines] = useState<RequisitionLine[]>([])
  const [viewLinesLoading, setViewLinesLoading] = useState(false)
  const [viewLineQtyDrafts, setViewLineQtyDrafts] = useState<Record<number, string>>({})
  const [updatingViewLineId, setUpdatingViewLineId] = useState<number | null>(null)
  const [selectedRequisition, setSelectedRequisition] = useState<RequisitionDisplay | null>(null)
  const selectedRequisitionRef = useRef<RequisitionDisplay | null>(null)
  const [requisitionDownloadDoc, setRequisitionDownloadDoc] = useState<RequisitionDownloadDoc | null>(null)
  const openedRequisitionIdRef = useRef<number | null>(null)
  const autoRefreshTimeoutsRef = useRef<number[]>([])
  const [statusFilter, setStatusFilter] = useState('')
  const [referenceFilter, setReferenceFilter] = useState('')
  const [vesselFilter, setVesselFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  // Sync global header vessel filter → local vessel filter
  useEffect(() => {
    const gvId = globalState.vessel?.id
    setVesselFilter(gvId != null ? String(gvId) : '')
  }, [globalState.vessel])
  const [headFilter, setHeadFilter] = useState('')
  const [subHeadFilter, setSubHeadFilter] = useState('')
  const [requisitionTitleFilter, setRequisitionTitleFilter] = useState('')
  const [approvalPendingWithFilter, setApprovalPendingWithFilter] = useState('')
  const [poDateFromFilter, setPoDateFromFilter] = useState('')
  const [poDateToFilter, setPoDateToFilter] = useState('')
  const [deliveredToVesselFilter, setDeliveredToVesselFilter] = useState('')
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false)
  const [activeRequisitionTab, setActiveRequisitionTab] = useState<'pending' | 'past'>('pending')
  const [isLoading, setIsLoading] = useState(false)
  const {
    loading: procurementPermissionsLoading,
    can: canProcurementPermission,
    canAny: canAnyProcurementPermission,
    getScope: getProcurementScope,
  } = useProcurementPermissions()
  const canViewRequisition = canProcurementPermission(PROCUREMENT_PERMS.READ_REQUISITION)
  const canExportRequisitions = canAnyProcurementPermission([
    PROCUREMENT_PERMS.REQUISITION_EXPORT,
    PROCUREMENT_PERMS.REQUISITION_DOWNLOAD,
  ])
  const canCreateRequisition = canProcurementPermission(PROCUREMENT_PERMS.CREATE_REQUISITION)
  const canCreateRFQ = canProcurementPermission(PROCUREMENT_PERMS.RFQ_CREATE)
  const canEditRequisition = canProcurementPermission(PROCUREMENT_PERMS.UPDATE_REQUISITION)
  const canDeleteRequisitionPermission = canProcurementPermission(PROCUREMENT_PERMS.DELETE_REQUISITION)
  const canSubmitRequisitionPermission = canProcurementPermission(PROCUREMENT_PERMS.REQUISITION_SUBMIT)
  const canApproveVesselRequisition = canProcurementPermission(
    PROCUREMENT_PERMS.REQUISITION_APPROVE_VESSEL
  )
  const canApproveShoreRequisition = canProcurementPermission(
    PROCUREMENT_PERMS.REQUISITION_APPROVE_SHORE
  )
  const canAddItemsToRequisition = canProcurementPermission(PROCUREMENT_PERMS.REQUISITION_ADD_ITEMS)
  const canViewCategoryLibrary = libraryViewAccess.category(canProcurementPermission)
  const canViewHeadLibrary = libraryViewAccess.head(canProcurementPermission)
  const canViewSubHeadLibrary = libraryViewAccess.subHead(canProcurementPermission)
  const canViewSubCategoryLibrary = libraryViewAccess.subCategory(canProcurementPermission)
  const canViewAddressLibrary = libraryViewAccess.address(canProcurementPermission)
  const canViewAccountingLibrary = libraryViewAccess.accounting(canProcurementPermission)
  const canViewSubAccountingLibrary = libraryViewAccess.subAccounting(canProcurementPermission)
  const canManageCategoryLibrary = libraryManageAccess.category(canProcurementPermission)
  const canManageHeadLibrary = libraryManageAccess.head(canProcurementPermission)
  const canManageSubHeadLibrary = libraryManageAccess.subHead(canProcurementPermission)
  const canManageSubCategoryLibrary = libraryManageAccess.subCategory(canProcurementPermission)
  const canManageAddressLibrary = libraryManageAccess.address(canProcurementPermission)
  const canViewTermsLibrary = libraryViewAccess.terms(canProcurementPermission)
  const canManageTermsLibrary = libraryManageAccess.terms(canProcurementPermission)
  const canManageAccountingLibrary = libraryManageAccess.accounting(canProcurementPermission)
  const canManageSubAccountingLibrary = libraryManageAccess.subAccounting(canProcurementPermission)
  const canCreateCategoryLibrary = libraryCrudAccess.category.create(canProcurementPermission)
  const canCreateHeadLibrary = canManageHeadLibrary
  const canCreateSubHeadLibrary = canManageSubHeadLibrary
  const canCreateSubCategoryLibrary = canManageSubCategoryLibrary
  const canCreateAddressLibrary = canManageAddressLibrary
  const canCreateAccountingLibrary = canManageAccountingLibrary
  const canCreateSubAccountingLibrary = canManageSubAccountingLibrary
  const canUpdateCategoryLibrary = libraryCrudAccess.category.update(canProcurementPermission)
  const canUpdateHeadLibrary = canManageHeadLibrary
  const canUpdateSubHeadLibrary = canManageSubHeadLibrary
  const canUpdateSubCategoryLibrary = canManageSubCategoryLibrary
  const canUpdateAddressLibrary = canManageAddressLibrary
  const canUpdateAccountingLibrary = canManageAccountingLibrary
  const canUpdateSubAccountingLibrary = canManageSubAccountingLibrary
  const canDeleteCategoryLibrary = libraryCrudAccess.category.delete(canProcurementPermission)
  const canDeleteTermsLibrary = libraryCrudAccess.terms.delete(canProcurementPermission)
  const canDeleteHeadLibrary = canManageHeadLibrary
  const canDeleteSubHeadLibrary = canManageSubHeadLibrary
  const canDeleteSubCategoryLibrary = canManageSubCategoryLibrary
  const canDeleteAddressLibrary = canManageAddressLibrary
  const canDeleteAccountingLibrary = canManageAccountingLibrary
  const canDeleteSubAccountingLibrary = canManageSubAccountingLibrary
  const canViewUomLibrary = canProcurementPermission(PROCUREMENT_PERMS.READ_UOM)
  const canCreateUomLibrary = canProcurementPermission(PROCUREMENT_PERMS.CREATE_UOM)
  const canUpdateUomLibrary = canProcurementPermission(PROCUREMENT_PERMS.UPDATE_UOM)
  const canDeactivateUomLibrary = canProcurementPermission(PROCUREMENT_PERMS.DEACTIVATE_UOM)
  const canViewPortLibrary = canProcurementPermission(PROCUREMENT_PERMS.READ_PORT)
  const canCreatePortLibrary = canProcurementPermission(PROCUREMENT_PERMS.CREATE_PORT)
  const canUpdatePortLibrary = canProcurementPermission(PROCUREMENT_PERMS.UPDATE_PORT)
  const canDeactivatePortLibrary = canProcurementPermission(PROCUREMENT_PERMS.DEACTIVATE_PORT)
  const canOpenAnyLibrary = canAnyProcurementPermission([
    PROCUREMENT_PERMS.READ_LIBRARY_CATEGORY,
    PROCUREMENT_PERMS.CREATE_LIBRARY_CATEGORY,
    PROCUREMENT_PERMS.UPDATE_LIBRARY_CATEGORY,
    PROCUREMENT_PERMS.DELETE_LIBRARY_CATEGORY,
    PROCUREMENT_PERMS.LIBRARY_HEAD_VIEW,
    PROCUREMENT_PERMS.LIBRARY_HEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBHEAD_VIEW,
    PROCUREMENT_PERMS.LIBRARY_SUBHEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_VIEW,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_ADDRESS_VIEW,
    PROCUREMENT_PERMS.LIBRARY_ADDRESS_MANAGE,
    PROCUREMENT_PERMS.READ_TERMS_CONDITIONS_LIBRARY,
    PROCUREMENT_PERMS.CREATE_TERMS_CONDITIONS_LIBRARY,
    PROCUREMENT_PERMS.UPDATE_TERMS_CONDITIONS_LIBRARY,
    PROCUREMENT_PERMS.DELETE_TERMS_CONDITIONS_LIBRARY,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_VIEW,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_VIEW,
    PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.READ_REQUISITION,
    PROCUREMENT_PERMS.CREATE_REQUISITION,
  ])
  const requisitionViewScope = getProcurementScope(PROCUREMENT_PERMS.READ_REQUISITION)
  const requisitionScopeAll = requisitionViewScope === 'ALL'
  const requisitionScopeCompany = requisitionViewScope === 'COMPANY'
  const requisitionScopeVessel = requisitionViewScope === 'VESSEL'
  const showVesselColumn = requisitionScopeAll
  const canViewLibraryTab = useCallback(
    (
      key: 'category' | 'head' | 'subHead' | 'subCategory' | 'address' | 'terms' | 'accountingCode' | 'subAccounting' | 'uom' | 'port'
    ) => {
      switch (key) {
        case 'category':
          return canViewCategoryLibrary || canManageCategoryLibrary
        case 'head':
          return canViewHeadLibrary || canManageHeadLibrary
        case 'subHead':
          return canViewSubHeadLibrary || canManageSubHeadLibrary
        case 'subCategory':
          return canViewSubCategoryLibrary || canManageSubCategoryLibrary
        case 'address':
          return canViewAddressLibrary || canManageAddressLibrary
        case 'terms':
          return canViewTermsLibrary || canManageTermsLibrary
        case 'accountingCode':
          return canViewAccountingLibrary || canManageAccountingLibrary
        case 'subAccounting':
          return canViewSubAccountingLibrary || canManageSubAccountingLibrary
        case 'uom':
          return canViewUomLibrary
        case 'port':
          return canViewPortLibrary
        default:
          return false
      }
    },
    [
      canViewCategoryLibrary,
      canManageCategoryLibrary,
      canViewHeadLibrary,
      canManageHeadLibrary,
      canViewSubHeadLibrary,
      canManageSubHeadLibrary,
      canViewSubCategoryLibrary,
      canManageSubCategoryLibrary,
      canViewAddressLibrary,
      canManageAddressLibrary,
      canViewTermsLibrary,
      canManageTermsLibrary,
      canViewAccountingLibrary,
      canManageAccountingLibrary,
      canViewSubAccountingLibrary,
      canManageSubAccountingLibrary,
      canViewUomLibrary,
      canViewPortLibrary,
    ]
  )
  useEffect(() => {
    console.log('PROCUREMENT_SCOPE_PATCH_SIGNATURE', PROCUREMENT_SCOPE_PATCH_SIGNATURE)
  }, [])
  const parsePositiveId = (value: any): number | null => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }
  const crewLibraryCompanyScopeId = parsePositiveId(
    (currentUser as any)?.vessel?.companyGroupAdminId ??
      (currentUser as any)?.vessel?.companyGroupAdmin?.id ??
      (currentUser as any)?.companyGroupAdminId ??
      (currentUser as any)?.companyGroupAdmin?.id ??
      (currentUser as any)?.companyAdmin?.cgaid?.id ??
      (currentUser as any)?.cgaid?.id ??
      (currentUser as any)?.cgaid ??
      (currentUser as any)?.roleEntityId
  )
  const crewLibraryVesselScopeId = parsePositiveId(
    (currentUser as any)?.vessel?.id ?? (currentUser as any)?.vesselId
  )
  const crewLibraryFallbackScope = useMemo(
    () => normalizeCrewScopeFromUser(currentUser as any),
    [currentUser]
  )
  const crewLibraryResolvedCompanyScopeId =
    crewLibraryCompanyScopeId ?? crewLibraryFallbackScope.companyId
  const crewLibraryResolvedVesselScopeId =
    crewLibraryVesselScopeId ?? crewLibraryFallbackScope.vesselId
  const crewLibraryCompanyScopeName = String(
    (currentUser as any)?.vessel?.companyGroupAdmin?.name ??
      (currentUser as any)?.companyGroupAdmin?.name ??
      ''
  ).trim()
  const crewLibraryVesselScopeName = String(
    (currentUser as any)?.vessel?.fleet_name ?? (currentUser as any)?.vessel?.name ?? ''
  ).trim()
  const crewLibraryVesselTypeScope =
    String((currentUser as any)?.vessel?.vesselType ?? '').trim() || undefined
  const normalizeScopeText = useCallback(
    (value: any) => String(value ?? '').trim().toUpperCase(),
    []
  )
  const rowMatchesCrewCompanyScope = useCallback(
    (rowCompanyIdRaw: any, rowCompanyNameRaw: any) => {
      if (!requisitionScopeVessel) return true
      const rowCompanyId = parsePositiveId(rowCompanyIdRaw)
      const rowCompanyName = normalizeScopeText(rowCompanyNameRaw)
      const crewCompanyName = normalizeScopeText(crewLibraryCompanyScopeName)

      if (crewLibraryCompanyScopeId && rowCompanyId && rowCompanyId !== crewLibraryCompanyScopeId) {
        return false
      }
      if (crewLibraryCompanyScopeId && !rowCompanyId && rowCompanyName && crewCompanyName) {
        return rowCompanyName === crewCompanyName
      }
      if (crewLibraryCompanyScopeId && !rowCompanyId && !rowCompanyName) {
        return false
      }
      return true
    },
    [
      requisitionScopeVessel,
      crewLibraryCompanyScopeId,
      crewLibraryCompanyScopeName,
      normalizeScopeText,
    ]
  )
  const rowMatchesCrewVesselScope = useCallback(
    (rowVesselIdRaw: any, rowVesselNameRaw: any) => {
      if (!requisitionScopeVessel) return true
      const rowVesselId = parsePositiveId(rowVesselIdRaw)
      const rowVesselName = normalizeScopeText(rowVesselNameRaw)
      const crewVesselName = normalizeScopeText(crewLibraryVesselScopeName)

      if (crewLibraryVesselScopeId && rowVesselId && rowVesselId !== crewLibraryVesselScopeId) {
        return false
      }
      if (crewLibraryVesselScopeId && !rowVesselId && rowVesselName && crewVesselName) {
        return rowVesselName === crewVesselName
      }
      // Company-wide rows without vessel assignment remain visible to crew of that company.
      return true
    },
    [
      requisitionScopeVessel,
      crewLibraryVesselScopeId,
      crewLibraryVesselScopeName,
      normalizeScopeText,
    ]
  )
  // ALL scope: no vessel filter on list fetch. Otherwise use tenant vessel from profile (backend enforces scope).
  const [vesselId] = useState<number | null>(
    requisitionScopeAll ? null : procurementTenantVesselId(currentUser) ?? null
  )
  // Users with ALL scope can switch vessels for list-level fetches
  const [selectedVesselId, setSelectedVesselId] = useState<number | null>(
    requisitionScopeAll ? null : procurementTenantVesselId(currentUser) ?? null
  )
  const [availableVessels, setAvailableVessels] = useState<Vessel[]>([])
  const [selectedAttachment, setSelectedAttachment] = useState<FileRefDto | null>(null)
  const [remarkActionModal, setRemarkActionModal] = useState<{
    open: boolean
    type: 'review' | 'reject'
    requisitionId: number | null
  }>({open: false, type: 'review', requisitionId: null})
  const [remarkActionText, setRemarkActionText] = useState('')
  const [remarkActionSubmitting, setRemarkActionSubmitting] = useState(false)
  // Store demo-only extra fields per requisition_code
  const [extraMetaByCode, setExtraMetaByCode] = useState<Record<string, ReqExtraMeta>>({})
  const requisitionLookupCacheRef = useRef<{
    loaded: boolean
    subCatalogueMap: Map<number, string>
    subComponentMap: Map<number, string>
    vesselsMap: Map<number, Vessel>
  }>({
    loaded: false,
    subCatalogueMap: new Map<number, string>(),
    subComponentMap: new Map<number, string>(),
    vesselsMap: new Map<number, Vessel>(),
  })

  const [addItemsModalVisible, setAddItemsModalVisible] = useState(false)
  const [selectedRequisitionId, setSelectedRequisitionId] = useState<number | null>(null)
  const latestRequisitionRequestRef = useRef(0)

  useEffect(() => {
    selectedRequisitionRef.current = selectedRequisition
  }, [selectedRequisition])

  useEffect(() => {
    return () => {
      autoRefreshTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId))
      autoRefreshTimeoutsRef.current = []
    }
  }, [])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [searchTerm])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedLibrarySearchByTab(librarySearchByTab)
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [librarySearchByTab])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedPortLibrarySearch(portLibrarySearch.trim())
      setPortLibraryPage(0)
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [portLibrarySearch])

  const upsertExtras = React.useCallback((code: string, extras: ReqExtraMeta) => {
    setExtraMetaByCode((prev: Record<string, ReqExtraMeta>) => ({
      ...prev,
      [code]: {...prev[code], ...extras},
    }))
  }, [])

  const ensureRequisitionLookupCache = useCallback(async () => {
    if (requisitionLookupCacheRef.current.loaded) return

    const [subcatsResult, vesselsResult] = await Promise.allSettled([
      getAllSubCatalogues(),
      getAllVessels(),
    ])

    requisitionLookupCacheRef.current.subCatalogueMap =
      subcatsResult.status === 'fulfilled'
        ? new Map((subcatsResult.value || []).map((sc) => [sc.id, sc.name]))
        : new Map<number, string>()

    requisitionLookupCacheRef.current.subComponentMap = new Map<number, string>()

    requisitionLookupCacheRef.current.vesselsMap =
      vesselsResult.status === 'fulfilled'
        ? new Map((vesselsResult.value || []).map((v) => [v.id, v]))
        : new Map<number, Vessel>()

    requisitionLookupCacheRef.current.loaded = true
  }, [])

  const normalizeRequisitionStatus = (status?: string) =>
    String(status || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_')

  const extractWorkflowRemarkMeta = (description?: string): WorkflowRemarkMeta => {
    if (!description) return null
    const lines = String(description)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)

    for (let i = lines.length - 1; i >= 0; i -= 1) {
      const line = lines[i]
      if (line.toUpperCase().startsWith('REJECTED:')) {
        return {stage: 'Rejected', remark: line.replace(/^REJECTED:\s*/i, '').trim()}
      }
      if (line.toUpperCase().startsWith('REVISION REQUEST:')) {
        return {stage: 'Review', remark: line.replace(/^REVISION REQUEST:\s*/i, '').trim()}
      }
    }
    return null
  }

  const getRequisitionStatusLabel = (status?: string) => {
    const normalized = normalizeRequisitionStatus(status)
    switch (normalized) {
      case 'DRAFT':
        return 'Draft'
      case 'SUBMITTED':
      case 'SENT':
        return 'Submitted'
      case 'PENDING_VESSEL_APPROVAL':
        return 'Pending Vessel Approval'
      case 'VESSEL_APPROVED':
        return 'Vessel Approved'
      case 'PENDING_SHORE_APPROVAL':
        return 'Pending Shore Approval'
      case 'SHORE_APPROVED':
        return 'Shore Approved'
      case 'RFQ_ISSUED':
        return 'RFQ Issued'
      case 'RFQ_REWORK_PENDING':
        return 'RFQ Rework Pending'
      case 'QUOTATION_RECEIVED':
        return 'Quotation Received'
      case 'PO_CREATED':
        return 'PO Created'
      case 'PARTIALLY_COMPLETED':
        return 'Partially Completed'
      case 'FULFILLED':
        return 'Fulfilled'
      case 'TO_BE_REVISED':
        return 'To Be Revised'
      case 'REJECTED':
        return 'Rejected'
      case 'CANCELLED':
        return 'Cancelled'
      default:
        return normalized
          ? normalized
              .toLowerCase()
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
          : 'Unknown'
    }
  }

  const resolveRequisitionCompanyName = (req?: Requisition | null) =>
    (req as any)?.companyName ||
    req?.category?.cgaid?.name ||
    (req?.category as any)?.caid?.name ||
    null

  const resolveRequisitionSubmittedAt = (req?: Requisition | null) =>
    (req as any)?.submittedAt ||
    (req as any)?.submittedDate ||
    (req as any)?.dateSent ||
    (req as any)?.updatedAt ||
    (req as any)?.createdAt ||
    null

  const canSubmitForStatus = (status?: string) => {
    const norm = normalizeRequisitionStatus(status)
    return norm === 'DRAFT' || norm === 'TO_BE_REVISED'
  }

  const canAddItemsForStatus = (status?: string) => {
    const norm = normalizeRequisitionStatus(status)
    return (
      norm === 'DRAFT' ||
      norm === 'SUBMITTED' ||
      norm === 'SENT' ||
      norm === 'VESSEL_APPROVED' ||
      norm === 'TO_BE_REVISED'
    )
  }

  const crewMayEditRequisitionLines = (status?: string) => {
    if (!canAddItemsToRequisition) return false
    const norm = normalizeRequisitionStatus(status)
    if (requisitionViewScope === 'VESSEL') {
      return (
        norm === 'DRAFT' ||
        norm === 'TO_BE_REVISED' ||
        norm === 'SUBMITTED' ||
        norm === 'SENT' ||
        norm === 'PENDING_VESSEL_APPROVAL'
      )
    }
    return norm === 'DRAFT' || norm === 'TO_BE_REVISED'
  }

  const canCompanyReviewForStatus = (status?: string) => {
    const norm = normalizeRequisitionStatus(status)
    return norm === 'VESSEL_APPROVED' || norm === 'DRAFT'
  }

  const canCompanyShoreApproveForStatus = (status?: string) =>
    normalizeRequisitionStatus(status) === 'VESSEL_APPROVED'

  const canEditLineQuantitiesForStatus = (status?: string) => {
    const norm = normalizeRequisitionStatus(status)
    if (norm === 'SUBMITTED' || norm === 'SENT') {
      return (
        (requisitionViewScope === 'VESSEL' || requisitionViewScope === 'ALL') && canEditRequisition
      )
    }
    if (norm === 'VESSEL_APPROVED') {
      return (
        (requisitionViewScope === 'COMPANY' || requisitionViewScope === 'ALL') && canEditRequisition
      )
    }
    return false
  }

  const isPastRequisitionStatus = (status?: string) => {
    const norm = normalizeRequisitionStatus(status)
    return (
      norm === 'RFQ_ISSUED' ||
      norm === 'QUOTATION_RECEIVED' ||
      norm === 'PO_CREATED' ||
      norm === 'PARTIALLY_COMPLETED' ||
      norm === 'FULFILLED' ||
      norm === 'COMPLETED' ||
      norm === 'CANCELLED' ||
      norm === 'REJECTED'
    )
  }

  const isCompanyVisibleRequisitionStatus = (status?: string) => {
    const norm = normalizeRequisitionStatus(status)
    return !(
      norm === 'DRAFT' ||
      norm === 'SUBMITTED' ||
      norm === 'SENT' ||
      norm === 'PENDING_VESSEL_APPROVAL'
    )
  }

  const isPendingApprovalStatus = (status?: string) => !isPastRequisitionStatus(status)

  // Debug logging
  useEffect(() => {
    console.log('Current User:', currentUser)
    console.log('Auth:', auth)

    // Decode JWT token to see what's inside
    if (auth?.auth?.jwt) {
      try {
        const base64Url = auth.auth.jwt.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(function (c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            })
            .join('')
        )
        console.log('JWT Payload:', JSON.parse(jsonPayload))
      } catch (e) {
        console.error('Error decoding JWT:', e)
      }
    }
  }, [currentUser, auth])

  const [parts, setParts] = useState<Part[]>([])
  const [ports, setPorts] = useState<Port[]>([])
  const [categories, setCategories] = useState<SubcatalogueType[]>([])
  const [inventoryCategories, setInventoryCategories] = useState<InventoryCategory[]>([])
  const [inventoryItemHeads, setInventoryItemHeads] = useState<InventoryItemHead[]>([])
  const [inventoryItemSubHeads, setInventoryItemSubHeads] = useState<InventoryItemSubHead[]>([])
  const [inventoryItemSubCategories, setInventoryItemSubCategories] = useState<InventoryItemSubCategory[]>([])
  const [inventoryAddresses, setInventoryAddresses] = useState<InventoryAddress[]>([])
  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[]>([])
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([])
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)

const loadInitialData = async () => {
  try {
    const [portsResult] = await Promise.allSettled([
      getPorts(),
    ])

    setPorts(portsResult.status === 'fulfilled' ? portsResult.value : [])

    // Do not preload library popup data here anymore
    setInventoryCategories([])
    setInventoryItemSubCategories([])
    setInventoryItemHeads([])
    setInventoryItemSubHeads([])
    setInventoryAddresses([])
    setAccountingAccounts([])
    setSubAccounts([])
    setCategories([])
    setParts([])

    const failedLookupNames: string[] = []

    if (failedLookupNames.length > 0) {
      console.warn('Some requisition lookups failed:', failedLookupNames)
      toast.warning(`Some lookups failed to load: ${failedLookupNames.join(', ')}`)
    }
  } catch (error) {
    console.error('Error loading requisition initial data:', error)
    toast.error('Failed to load requisition data')
  }
}

const loadCategoryLibrary = useCallback(async () => {
  try {
    setIsLoadingCategories(true)

    let categoriesData: InventoryCategory[] = []
    const companyIdForScopedLoad = requisitionScopeVessel
      ? crewLibraryResolvedCompanyScopeId
      : Number(
          (currentUser as any)?.vessel?.companyGroupAdminId ??
            (currentUser as any)?.vessel?.companyGroupAdmin?.id ??
            (currentUser as any)?.companyGroupAdminId ??
            (currentUser as any)?.companyGroupAdmin?.id ??
            (currentUser as any)?.cgaid?.id ??
            (currentUser as any)?.cgaid ??
            (currentUser as any)?.roleEntityId ??
            0
        ) || null

    if (requisitionScopeAll) {
      categoriesData = normalizeInventoryCategoryRows(await getAllInventoryCategoriesForAllCompanies())
    } else if (companyIdForScopedLoad) {
      categoriesData = normalizeInventoryCategoryRows(
        await getAllInventoryCategoriesForCompany(companyIdForScopedLoad)
      )
    }

    setInventoryCategories(categoriesData || [])
  } catch (error) {
    console.error('Error loading category library:', error)
    setInventoryCategories([])
    toast.error('Failed to load category library')
  } finally {
    setIsLoadingCategories(false)
  }
}, [
  currentUser,
  requisitionScopeAll,
  crewLibraryResolvedCompanyScopeId,
])

const loadSubCategoryLibrary = useCallback(async () => {
  try {
    const subCategoryData = await getInventoryItemSubCategories(true)
    setInventoryItemSubCategories(subCategoryData || [])
  } catch (error) {
    console.error('Error loading sub category library:', error)
    setInventoryItemSubCategories([])
    toast.error('Failed to load sub category library')
  }
}, [])

const loadAddressLibrary = useCallback(async () => {
  try {
    if (requisitionScopeAll && (!libraryCompanyMasterOptions || libraryCompanyMasterOptions.length === 0)) {
      const companies = await getCompanyGroupAdmins()
      setLibraryCompanyMasterOptions(companies || [])
    }

    const scopedCompanyId = requisitionScopeVessel
      ? crewLibraryResolvedCompanyScopeId
      : libraryCompanyFilterId
    const companyIdForApi = requisitionScopeAll
      ? libraryCompanyFilterId ?? undefined
      : scopedCompanyId ?? undefined

    const addressData = await getInventoryAddresses(companyIdForApi, false)
    setInventoryAddresses(addressData || [])
  } catch (error) {
    console.error('Error loading address library:', error)
    setInventoryAddresses([])
    toast.error('Failed to load address library')
  }
}, [
  requisitionScopeAll,
  libraryCompanyMasterOptions,
  requisitionScopeVessel,
  crewLibraryResolvedCompanyScopeId,
  libraryCompanyFilterId,
])

const loadHeadLibrary = useCallback(async () => {
  try {
    console.log('LIBRARY FILTER STATE', {
      requisitionScopeAll,
      libraryCompanyFilterId,
      libraryVesselFilterId,
      libraryHeadFilterId,
      selectedVesselId_global: selectedVesselId,
      vesselId_global: vesselId,
    })

    if (requisitionScopeAll) {
      const selectedCompanyId = libraryCompanyFilterId ?? null
      const selectedVesselId = libraryVesselFilterId ?? null
      const selectedHeadId = libraryHeadFilterId ?? null

      const headsData =
        (await getInventoryItemHeads(
          undefined,
          selectedCompanyId ?? undefined
        )) || []

      console.log('LIBRARY RESOLVED SCOPE', {
        companyId: selectedCompanyId,
        vesselId: selectedVesselId,
        headId: selectedHeadId,
        source: 'library-filter-only',
        flow: 'library-head',
      })
      setInventoryItemHeads(sharedDedupeInventoryHeadRows(headsData))
      return
    }

    const selectedCompanyIdForScope = requisitionScopeVessel
      ? crewLibraryResolvedCompanyScopeId
      : libraryCompanyFilterId
    const selectedVesselIdForScope = requisitionScopeVessel
      ? crewLibraryResolvedVesselScopeId
      : libraryVesselFilterId

    if (requisitionScopeVessel && (!selectedCompanyIdForScope || !selectedVesselIdForScope)) {
      console.warn(
        '[Requisition Libraries][Head] Crew scope missing company/vessel. Skipping API call.'
      )
      setInventoryItemHeads([])
      return
    }

    const headsResult = await sharedLoadScopedInventoryHeads({
      currentUser,
      inventoryScopeAll: requisitionScopeAll,
      inventoryScopeCompany: requisitionScopeCompany,
      inventoryScopeVessel: requisitionScopeVessel,
      isServicesCategory: false,
      selectedCompanyId: selectedCompanyIdForScope,
      selectedVesselId: selectedVesselIdForScope,
      selectedHeadId: libraryHeadFilterId,
      debugContext: 'Requisition Libraries',
    })
    console.log('LIBRARY RESOLVED SCOPE', {
      companyId: headsResult.apiScope.companyId ?? null,
      vesselId: headsResult.apiScope.vesselId ?? null,
      headId: headsResult.apiScope.headId ?? null,
      source: headsResult.scopeSource,
      flow: 'library-head',
    })
    setInventoryItemHeads(headsResult.rows || [])
  } catch (error) {
    console.error('Error loading head library:', error)
    setInventoryItemHeads([])
    toast.error('Failed to load head library')
  }
}, [
  requisitionScopeVessel,
  crewLibraryResolvedVesselScopeId,
  crewLibraryResolvedCompanyScopeId,
  crewLibraryFallbackScope.companyId,
  crewLibraryFallbackScope.vesselId,
  libraryCompanyFilterId,
  libraryHeadFilterId,
  libraryVesselFilterId,
  selectedVesselId,
  vesselId,
  currentUser,
  requisitionScopeAll,
  requisitionScopeCompany,
  requisitionScopeVessel,
])

const loadSubHeadLibrary = useCallback(async () => {
  try {
    console.log('LIBRARY FILTER STATE', {
      requisitionScopeAll,
      libraryCompanyFilterId,
      libraryVesselFilterId,
      libraryHeadFilterId,
      selectedVesselId_global: selectedVesselId,
      vesselId_global: vesselId,
    })

    if (requisitionScopeAll) {
      const selectedCompanyId = libraryCompanyFilterId ?? null
      const selectedVesselId = libraryVesselFilterId ?? null
      const selectedHeadId = libraryHeadFilterId ?? null

      const subHeadsData =
        (await getInventoryItemSubHeads(
          undefined,
          selectedHeadId ?? undefined,
          selectedCompanyId ?? undefined
        )) || []

      console.log('LIBRARY RESOLVED SCOPE', {
        companyId: selectedCompanyId,
        vesselId: selectedVesselId,
        headId: selectedHeadId,
        source: 'library-filter-only',
        flow: 'library-subhead',
      })
      setInventoryItemSubHeads(sharedDedupeInventorySubHeadRows(subHeadsData))
      return
    }

    const selectedCompanyIdForScope = requisitionScopeVessel
      ? crewLibraryResolvedCompanyScopeId
      : libraryCompanyFilterId
    const selectedVesselIdForScope = requisitionScopeVessel
      ? crewLibraryResolvedVesselScopeId
      : libraryVesselFilterId

    if (requisitionScopeVessel && (!selectedCompanyIdForScope || !selectedVesselIdForScope)) {
      console.warn(
        '[Requisition Libraries][Sub Head] Crew scope missing company/vessel. Skipping API call.'
      )
      setInventoryItemSubHeads([])
      return
    }

    const subHeadsResult = await sharedLoadScopedInventorySubHeads({
      currentUser,
      inventoryScopeAll: requisitionScopeAll,
      inventoryScopeCompany: requisitionScopeCompany,
      inventoryScopeVessel: requisitionScopeVessel,
      isServicesCategory: false,
      selectedCompanyId: selectedCompanyIdForScope,
      selectedVesselId: selectedVesselIdForScope,
      selectedHeadId: libraryHeadFilterId,
      debugContext: 'Requisition Libraries',
    })
    console.log('LIBRARY RESOLVED SCOPE', {
      companyId: subHeadsResult.apiScope.companyId ?? null,
      vesselId: subHeadsResult.apiScope.vesselId ?? null,
      headId: subHeadsResult.apiScope.headId ?? null,
      source: subHeadsResult.scopeSource,
      flow: 'library-subhead',
    })
    setInventoryItemSubHeads(subHeadsResult.rows || [])
  } catch (error) {
    console.error('Error loading sub head library:', error)
    setInventoryItemSubHeads([])
    toast.error('Failed to load sub head library')
  }
}, [
  requisitionScopeVessel,
  crewLibraryResolvedVesselScopeId,
  crewLibraryResolvedCompanyScopeId,
  crewLibraryFallbackScope.companyId,
  crewLibraryFallbackScope.vesselId,
  libraryCompanyFilterId,
  libraryHeadFilterId,
  libraryVesselFilterId,
  selectedVesselId,
  vesselId,
  currentUser,
  requisitionScopeAll,
  requisitionScopeCompany,
  requisitionScopeVessel,
])

const loadAccountingLibrary = useCallback(async () => {
  try {
    if (
      requisitionScopeVessel &&
      !crewLibraryResolvedCompanyScopeId &&
      !crewLibraryResolvedVesselScopeId
    ) {
      setAccountingAccounts([])
      return
    }
    const accountingData = requisitionScopeVessel
      ? await getAccountingAccounts(
          crewLibraryResolvedCompanyScopeId ?? undefined,
          crewLibraryResolvedVesselScopeId ?? undefined,
          crewLibraryVesselTypeScope
        )
      : await getAllAccountingAccounts()
    setAccountingAccounts(accountingData || [])
  } catch (error) {
    console.error('Error loading accounting codes:', error)
    setAccountingAccounts([])
    toast.error('Failed to load accounting codes')
  }
}, [
  requisitionScopeVessel,
  crewLibraryResolvedCompanyScopeId,
  crewLibraryResolvedVesselScopeId,
  crewLibraryVesselTypeScope,
])

const loadSubAccountingLibrary = useCallback(async () => {
  try {
    if (requisitionScopeVessel && !crewLibraryResolvedCompanyScopeId) {
      setSubAccounts([])
      return
    }
    const subAccountsData = requisitionScopeVessel
      ? await getAllSubAccounts(crewLibraryResolvedCompanyScopeId ?? undefined)
      : await getAllSubAccounts()
    setSubAccounts(subAccountsData || [])
  } catch (error) {
    console.error('Error loading sub accounting:', error)
    setSubAccounts([])
    toast.error('Failed to load sub accounting')
  }
}, [requisitionScopeVessel, crewLibraryResolvedCompanyScopeId])

const loadPortLibrary = useCallback(
  async (page = portLibraryPage, size = portLibrarySize, q = debouncedPortLibrarySearch) => {
    setPortLibraryLoading(true)
    try {
      const response = await getPortsPage({page, size, q})
      setPortLibraryRows(response.content || [])
      setPortLibraryTotalElements(response.totalElements || 0)
      setPortLibraryTotalPages(response.totalPages || 0)
    } catch (error) {
      console.error('Error loading Port Library:', error)
      setPortLibraryRows([])
      setPortLibraryTotalElements(0)
      setPortLibraryTotalPages(0)
      toast.error('Failed to load ports')
    } finally {
      setPortLibraryLoading(false)
    }
  },
  [portLibraryPage, portLibrarySize, debouncedPortLibrarySearch]
)

useEffect(() => {
  if (!isRequisitionLibrariesOpen || activeRequisitionLibrary !== 'port' || !canViewPortLibrary) return
  void loadPortLibrary()
}, [
  isRequisitionLibrariesOpen,
  activeRequisitionLibrary,
  canViewPortLibrary,
  loadPortLibrary,
])

  // Load lookups once; for superadmin also fetch vessel list so we can pick a valid vesselId
  useEffect(() => {
    loadInitialData()
    ensureRequisitionLookupCache().catch((err) =>
      console.error('Error preloading requisition lookup cache:', err)
    )
    if (requisitionScopeAll) {
      getAllVessels()
        .then((vs) => {
          setAvailableVessels(vs)
          // keep list unfiltered (all vessels) for superadmin; do not auto-select
        })
        .catch((err) => console.error('Error preloading vessels for superadmin:', err))
    }
  }, [ensureRequisitionLookupCache, requisitionScopeAll])
useEffect(() => {
  if (!isRequisitionLibrariesOpen) return
  if (!canViewLibraryTab(activeRequisitionLibrary)) return

  switch (activeRequisitionLibrary) {
    case 'category':
      if (!inventoryCategories.length) {
        loadCategoryLibrary()
      }
      break

    case 'subCategory':
      if (!inventoryItemSubCategories.length) {
        loadSubCategoryLibrary()
      }
      break

    case 'address':
      if (!inventoryAddresses.length) {
        loadAddressLibrary()
      }
      break

    case 'head':
      if (!inventoryItemHeads.length) {
        loadHeadLibrary()
      }
      break

    case 'subHead':
      if (!inventoryItemHeads.length) {
        loadHeadLibrary()
      }
      if (!inventoryItemSubHeads.length) {
        loadSubHeadLibrary()
      }
      break

    case 'accountingCode':
      if (!accountingAccounts.length) {
        loadAccountingLibrary()
      }
      break

    case 'subAccounting':
      if (!subAccounts.length) {
        loadSubAccountingLibrary()
      }
      break

    default:
      break
  }
}, [
  isRequisitionLibrariesOpen,
  activeRequisitionLibrary,
  inventoryCategories.length,
  inventoryItemSubCategories.length,
  inventoryAddresses.length,
  accountingAccounts.length,
  subAccounts.length,
  loadCategoryLibrary,
  loadSubCategoryLibrary,
  loadAddressLibrary,
  loadHeadLibrary,
  loadSubHeadLibrary,
  loadAccountingLibrary,
  loadSubAccountingLibrary,
  canViewLibraryTab,
])

useEffect(() => {
  if (!isRequisitionLibrariesOpen) return
  if (activeRequisitionLibrary !== 'address') return
  if (!canViewAddressLibrary && !canManageAddressLibrary) return
  loadAddressLibrary()
}, [
  isRequisitionLibrariesOpen,
  activeRequisitionLibrary,
  canViewAddressLibrary,
  canManageAddressLibrary,
  loadAddressLibrary,
])
  useEffect(() => {
    void loadRequisitions()
  }, [
    vesselId,
    requisitionViewScope,
    currentPage,
    rowsPerPage,
    debouncedSearchTerm,
    statusFilter,
    referenceFilter,
    vesselFilter,
    categoryFilter,
    headFilter,
    subHeadFilter,
    requisitionTitleFilter,
    approvalPendingWithFilter,
    poDateFromFilter,
    poDateToFilter,
    deliveredToVesselFilter,
    activeRequisitionTab,
    sortConfig,
  ])

  const loadInventoryData = () => loadInitialData()

  const loadRequisitions = async () => {
    setIsLoading(true)
    const requestId = latestRequisitionRequestRef.current + 1
    latestRequisitionRequestRef.current = requestId
    try {
      const currentPorts = ports
      const currentParts = parts
      const currentInventoryCategories = inventoryCategories

      const effectiveVesselId = vesselFilter ? parseInt(vesselFilter) : (requisitionScopeAll || requisitionScopeCompany ? null : vesselId)
      const sortParams =
        sortConfig.key === 'title'
          ? [`title,${sortConfig.direction}`, 'id,desc']
          : sortConfig.key === 'requiredDate'
          ? [`requiredDeliveryDate,${sortConfig.direction}`, 'id,desc']
          : ['updatedAt,desc', 'id,desc']

      const response = await getRequisitions(currentPage - 1, rowsPerPage, {
        vesselId: effectiveVesselId,
        search: debouncedSearchTerm,
        status: statusFilter || undefined,
        reference: referenceFilter || undefined,
        title: requisitionTitleFilter || undefined,
        category: categoryFilter || undefined,
        head: headFilter || undefined,
        subHead: subHeadFilter || undefined,
        dateFrom: poDateFromFilter || undefined,
        dateTo: poDateToFilter || undefined,
        tab: activeRequisitionTab,
        approvalPendingWith: approvalPendingWithFilter || undefined,
        deliveredToVessel: deliveredToVesselFilter || undefined,
        sort: sortParams,
      })
      if (requestId !== latestRequisitionRequestRef.current) {
        return
      }
      const filteredRequisitions: Requisition[] = response.content || []

      const categoryMap = new Map(currentInventoryCategories.map((cat) => [cat.id, cat.name]))

      if (!requisitionLookupCacheRef.current.loaded) {
        // Do not block requisition rendering on heavy lookup APIs.
        // Cache warms in background and will be reused on subsequent loads.
        ensureRequisitionLookupCache().catch((err) =>
          console.error('Error warming requisition lookup cache:', err)
        )
      }
      const subComponentMap = requisitionLookupCacheRef.current.subComponentMap
      const vesselsMap = requisitionLookupCacheRef.current.vesselsMap
      const inventoryItemHeadsMap = new Map((inventoryItemHeads || []).map((h) => [h.id, h.name]))
      const inventoryItemSubHeadsMap = new Map(
        (inventoryItemSubHeads || []).map((sh) => [sh.id, sh.name])
      )

      const displayItems: RequisitionDisplay[] = filteredRequisitions.map((req: Requisition) => {
        const partId = req.partId || (req.lines && req.lines.length > 0 ? req.lines[0].partId : null)
        const part = partId ? currentParts.find((p) => p.id === partId) : null
        const port = currentPorts.find((p) => p.id === req.requestedPortId)
        const vessel = vesselsMap.get(req.vesselId)

        const categoryId = req.category?.id || req.categoryId
        const categoryName = req.category?.name || (categoryId ? categoryMap.get(categoryId) || 'N/A' : 'N/A')

        const subComponentName =
          req.subcomponentSummary ||
          (req as any).subcomponent?.name ||
          (req.subcomponent?.id
            ? subComponentMap.get(req.subcomponent.id) || `ID:${req.subcomponent.id}`
            : null)

        const subComponentsFromLines =
          req.lines && req.lines.length > 0
            ? req.lines
                .filter((line: any) => line.subcomponentId !== null && line.subcomponentId !== undefined)
                .map(
                  (line: any) =>
                    line.subcomponent?.name ||
                    subComponentMap.get(line.subcomponentId) ||
                    `ID:${line.subcomponentId}`
                )
                .filter(Boolean)
            : req.subcomponentSummary
            ? [req.subcomponentSummary]
            : []

        const status = getRequisitionStatusLabel(req.status)
        const workflowRemarkMeta = extractWorkflowRemarkMeta(req.description)
        const companyName = resolveRequisitionCompanyName(req)
        const submittedAt = resolveRequisitionSubmittedAt(req)

        const urgency = req.type === 'HIGH' ? 'High' : req.type === 'MEDIUM' ? 'Medium' : 'Low'
        const extras = extraMetaByCode[req.requisition_code] || {}

        const totalQuantity =
          req.totalRequestedQuantity ??
          (req.lines && req.lines.length > 0
            ? req.lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0)
            : req.quantity || 0)
        const totalEstimatedValue =
          req.estimatedTotalValue ??
          (req.lines && req.lines.length > 0
            ? req.lines.reduce(
                (sum, line) => sum + Number(line.quantity || 0) * Number(line.estimatedUnitPrice || 0),
                0
              )
            : Number(req.quantity || 0) * Number(req.estimatedUnitPrice || 0))

        const lineManualNames =
          req.lines && req.lines.length > 0
            ? req.lines
                .map((l) => l.manualItemName)
                .filter((n): n is string => Boolean(n && n.trim()))
            : []
        const itemName =
          lineManualNames.length > 0
            ? Array.from(new Set(lineManualNames)).join(', ')
            : req.primaryItemName || req.manualItemName || part?.name || 'N/A'

        let unitLabel = req.displayUnit || req.uom || '-'
        if (req.lines && req.lines.length > 0) {
          const units = Array.from(
            new Set(
              req.lines
                .map((l) => l.unitOfMeasurement)
                .filter((u): u is string => Boolean(u && u.trim()))
            )
          )
          if (units.length === 1) unitLabel = units[0]
          if (units.length > 1) unitLabel = ''
        }

        return {
          id: req.id,
          reqNumber: req.requisition_code,
          name: req.name || '-',
          title: req.title,
          itemName,
          itemDescription: req.description || '',
          category: categoryName,
          urgency: urgency as 'High' | 'Medium' | 'Low',
          requestedPort:
            req.customPortName || req.requestedPortName || port?.name || port?.main_port || 'N/A',
          requiredDate: req.requiredDeliveryDate,
          quantity:
            totalQuantity > 0
              ? unitLabel
                ? `${totalQuantity} ${unitLabel}`.trim()
                : `${totalQuantity}`
              : 'N/A',
          estimatedValue: totalEstimatedValue > 0 ? totalEstimatedValue.toLocaleString() : '0',
          equipmentLink: req.equipmentLink || 'N/A',
          status: status,
          rawStatus: req.status,
          requestFor: req.requestFor || null,
          workflowRemark: workflowRemarkMeta?.remark,
          workflowStage: workflowRemarkMeta?.stage,
          vesselStatus: req.vesselApproved ? 'Approved' : 'Pending',
          shoreStatus: req.shoreApproved ? 'Approved' : 'Pending',
          companyName,
          submittedAt,
          raisedAt: req.raisedAt || null,
          inventoryItemSubCategoryName: req.inventoryItemSubCategoryName || null,
          updatedAt: (req as any)?.updatedAt || null,
          linkedDefectId: req.linkedDefectId ?? null,
          linkedDefectNumber: req.linkedDefectNumber ?? null,
          linkedDefectTitle: req.linkedDefectTitle ?? null,
          type: req.type,
          // @ts-ignore (table will read these keys)
          inventoryItemHead:
            req.inventoryItemHead?.name ||
            (req.inventoryItemHeadId
              ? inventoryItemHeadsMap.get(req.inventoryItemHeadId) || `ID:${req.inventoryItemHeadId}`
              : null),
          // @ts-ignore
          inventoryItemSubHead:
            req.inventoryItemSubHead?.name ||
            (req.inventoryItemSubHeadId
              ? inventoryItemSubHeadsMap.get(req.inventoryItemSubHeadId) || `ID:${req.inventoryItemSubHeadId}`
              : null),
          // @ts-ignore
          part: part?.name || (partId ? `ID:${partId}` : null),
          // @ts-ignore
          subComponent: subComponentName || undefined,
          // @ts-ignore
          subComponents: subComponentsFromLines.length > 0 ? subComponentsFromLines : null,
          // @ts-ignore
          make: req.make || extras.make || '-',
          // @ts-ignore
          model: req.model || extras.model || '-',
          // @ts-ignore
          serialNumber: req.serialNumber || extras.serialNumber || '-',
          vesselName:
            vessel?.fleet_name ||
            (vessel as any)?.fleetName ||
            req.vesselName ||
            (req as any)?.vessel?.fleet_name ||
            (req as any)?.vessel?.fleetName ||
            (req as any)?.vessel?.name ||
            'N/A',
          vesselId: req.vesselId,
        }
      })

      setRequisitionData(displayItems)
      setRequisitionTotalElements(response.totalElements ?? displayItems.length)
      setRequisitionTotalPages(Math.max(response.totalPages ?? 1, 1))

      const attachmentEntries = await Promise.all(
        filteredRequisitions.map(async (req) => {
          if (!req.id || req.hasAttachments === false) {
            return [req.id, []] as const
          }
          try {
            return [req.id, await getRequisitionAttachments(req.id)] as const
          } catch (attachmentError) {
            console.error(`Failed to load requisition attachments for ${req.id}:`, attachmentError)
            return [req.id, []] as const
          }
        })
      )
      setRequisitionAttachments(Object.fromEntries(attachmentEntries))
    } catch (error) {
      const message = isNetworkError(error)
        ? 'Unable to reach the server. Check your connection and try again.'
        : formatApiError(error, 'Failed to load requisitions', 'load')
      toast.error(message)
      setRequisitionData([])
      setRequisitionTotalElements(0)
      setRequisitionTotalPages(1)
    } finally {
      if (requestId === latestRequisitionRequestRef.current) {
        setIsLoading(false)
      }
    }
  }

  const clearScheduledAutoRefreshes = () => {
    autoRefreshTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId))
    autoRefreshTimeoutsRef.current = []
  }

  const refreshSelectedRequisitionSnapshot = async (id: number) => {
    if (!isViewModalVisible || selectedRequisitionRef.current?.id !== id) return

    try {
      const full = await getRequisitionById(id)
      const refreshedStatus = getRequisitionStatusLabel(full.status)
      const workflowRemarkMeta = extractWorkflowRemarkMeta(full.description)
      setSelectedRequisition((prev) => {
        if (!prev || prev.id !== id) return prev
        return {
          ...prev,
          status: refreshedStatus,
          ...requisitionDetailFields(full),
          rawStatus: full.status,
          requestFor: full.requestFor || null,
          workflowRemark: workflowRemarkMeta?.remark,
          workflowStage: workflowRemarkMeta?.stage,
          vesselStatus: full.vesselApproved ? 'Approved' : 'Pending',
          shoreStatus: full.shoreApproved ? 'Approved' : 'Pending',
          companyName: resolveRequisitionCompanyName(full),
          submittedAt: resolveRequisitionSubmittedAt(full),
          raisedAt: full.raisedAt || null,
          inventoryItemSubCategoryName: full.inventoryItemSubCategoryName || null,
          linkedDefectId: full.linkedDefectId ?? null,
          linkedDefectNumber: full.linkedDefectNumber ?? null,
          linkedDefectTitle: full.linkedDefectTitle ?? null,
        }
      })

      const lines = (full?.lines || []).map((l) => ({
        ...l,
        notes: l.notes ?? undefined,
      }))
      setViewLines(lines as RequisitionLine[])
      const qtyMap: Record<number, string> = {}
      lines.forEach((line) => {
        if (line.id) qtyMap[line.id] = String(line.quantity ?? '')
      })
      setViewLineQtyDrafts(qtyMap)
    } catch (err) {
      console.error('Error refreshing requisition snapshot:', err)
    }
  }

  const runPostActionAutoRefresh = async (id: number) => {
    await loadRequisitions()
    await refreshSelectedRequisitionSnapshot(id)

    clearScheduledAutoRefreshes()

    const firstRefresh = window.setTimeout(() => {
      void loadRequisitions()
      void refreshSelectedRequisitionSnapshot(id)
    }, 1200)

    const secondRefresh = window.setTimeout(() => {
      void loadRequisitions()
      void refreshSelectedRequisitionSnapshot(id)
    }, 3500)

    autoRefreshTimeoutsRef.current = [firstRefresh, secondRefresh]
  }

  const handleViewRequisition = async (record: RequisitionDisplay) => {
    clearScheduledAutoRefreshes()
    setSelectedRequisition(record)
    setIsViewModalVisible(true)
    setViewLines([])
    setViewLineQtyDrafts({})
    setViewLinesLoading(true)
    try {
      const full = await getRequisitionById(record.id)
      setSelectedRequisition((prev) => {
        if (!prev || prev.id !== record.id) return prev
        return {
          ...prev,
          title: full.title || prev.title,
          name: full.name || prev.name,
          ...requisitionDetailFields(full),
          requestFor: full.requestFor || prev.requestFor || null,
          companyName: resolveRequisitionCompanyName(full) || prev.companyName || null,
          submittedAt: resolveRequisitionSubmittedAt(full) || prev.submittedAt || null,
          linkedDefectId: full.linkedDefectId ?? prev.linkedDefectId ?? null,
          linkedDefectNumber: full.linkedDefectNumber ?? prev.linkedDefectNumber ?? null,
          linkedDefectTitle: full.linkedDefectTitle ?? prev.linkedDefectTitle ?? null,
        }
      })
      const lines = (full?.lines || []).map((l) => ({
        ...l,
        notes: l.notes ?? undefined,
      }))
      setViewLines(lines as RequisitionLine[])
      const qtyMap: Record<number, string> = {}
      lines.forEach((line) => {
        if (line.id) qtyMap[line.id] = String(line.quantity ?? '')
      })
      setViewLineQtyDrafts(qtyMap)
    } catch (err) {
      console.error('Error loading requisition details', err)
      toast.error('Failed to load requisition items')
    } finally {
      setViewLinesLoading(false)
    }
  }

  const openRequisitionById = useCallback(
    async (requisitionId: number) => {
      clearScheduledAutoRefreshes()
      setIsViewModalVisible(true)
      setViewLines([])
      setViewLineQtyDrafts({})
      setViewLinesLoading(true)
      try {
        const full = await getRequisitionById(requisitionId)
        const workflowRemarkMeta = extractWorkflowRemarkMeta(full.description)
        const summary = {
          ...(full as any),
          ...requisitionDetailFields(full),
          id: requisitionId,
          reqNumber: full.requisition_code,
          urgency: full.type === 'HIGH' ? 'High' : full.type === 'MEDIUM' ? 'Medium' : 'Low',
          requiredDate: full.requiredDeliveryDate,
          category: full.category?.name || '-',
          requestedPort: full.customPortName || full.requestedPortName || '-',
          itemDescription: full.description || '',
          title: full.title || full.name || '-',
          name: full.name || full.title || '-',
          status: getRequisitionStatusLabel(full.status),
          rawStatus: full.status,
          requestFor: full.requestFor || null,
          workflowRemark: workflowRemarkMeta?.remark,
          workflowStage: workflowRemarkMeta?.stage,
          vesselStatus: full.vesselApproved ? 'Approved' : 'Pending',
          shoreStatus: full.shoreApproved ? 'Approved' : 'Pending',
          companyName: resolveRequisitionCompanyName(full),
          submittedAt: resolveRequisitionSubmittedAt(full),
          raisedAt: full.raisedAt || null,
          inventoryItemSubCategoryName: full.inventoryItemSubCategoryName || null,
          linkedDefectId: full.linkedDefectId ?? null,
          linkedDefectNumber: full.linkedDefectNumber ?? null,
          linkedDefectTitle: full.linkedDefectTitle ?? null,
        } as RequisitionDisplay
        setSelectedRequisition(summary)
        const lines = (full?.lines || []).map((l) => ({
          ...l,
          notes: l.notes ?? undefined,
        }))
        setViewLines(lines as RequisitionLine[])
        const qtyMap: Record<number, string> = {}
        lines.forEach((line) => {
          if (line.id) qtyMap[line.id] = String(line.quantity ?? '')
        })
        setViewLineQtyDrafts(qtyMap)
      } catch (err) {
        console.error('Error loading requisition details', err)
        toast.error('Failed to load requisition items')
        setIsViewModalVisible(false)
        setSelectedRequisition(null)
      } finally {
        setViewLinesLoading(false)
      }
    },
    []
  )

  const handleDownloadRequisition = useCallback(
    async (requisitionId: number, options?: {silent?: boolean}) => {
      if (!canExportRequisitions) {
        if (!options?.silent) toast.error('You do not have permission to download requisitions.')
        return
      }
      try {
        const requisition = await getRequisitionById(requisitionId)
        const payload = buildRequisitionDownloadDoc(requisition)
        setRequisitionDownloadDoc(payload)
        await new Promise((resolve) => window.setTimeout(resolve, 160))
        await downloadElementAsPdf(
          '#requisition-download-host',
          `Requisition_${payload.requisition.reqNumber || requisitionId}`
        )
        if (!options?.silent) toast.success('Requisition PDF downloaded')
      } catch (error) {
        console.error('Failed to download requisition PDF', error)
        if (!options?.silent) toast.error('Failed to download requisition PDF')
        throw error
      } finally {
        setRequisitionDownloadDoc(null)
      }
    },
    [canExportRequisitions]
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const requisitionId = Number(urlParams.get('requisitionId') || 0)
    if (
      !Number.isFinite(requisitionId) ||
      requisitionId <= 0 ||
      openedRequisitionIdRef.current === requisitionId
    ) {
      return
    }
    openedRequisitionIdRef.current = requisitionId
    void openRequisitionById(requisitionId)
  }, [openRequisitionById, currentPage, rowsPerPage])

  const canEditLineQuantities = canEditLineQuantitiesForStatus(
    selectedRequisition?.rawStatus || selectedRequisition?.status
  )

  const handleViewLineQuantityDraftChange = (lineId: number, value: string) => {
    setViewLineQtyDrafts((prev) => ({
      ...prev,
      [lineId]: value,
    }))
  }

  const handleSaveViewLineQuantity = async (lineId: number) => {
    if (!selectedRequisition) return
    const isAllowed = canEditLineQuantitiesForStatus(
      selectedRequisition.rawStatus || selectedRequisition.status
    )
    if (!isAllowed) {
      toast.error('Quantity editing is not allowed for this status.')
      return
    }
    const rawQty = viewLineQtyDrafts[lineId]
    const quantity = Number(rawQty)
    if (!rawQty || Number.isNaN(quantity) || quantity <= 0) {
      toast.error('Quantity must be greater than 0')
      return
    }

    try {
      setUpdatingViewLineId(lineId)
      const updated = await updateRequisitionLineQuantity(
        selectedRequisition.id,
        lineId,
        quantity,
        'Quantity updated from company-side requisition view'
      )

      const updatedLines = (updated?.lines || []).map((l) => ({
        ...l,
        notes: l.notes ?? undefined,
      }))

      if (updatedLines.length > 0) {
        setViewLines(updatedLines as RequisitionLine[])
        const qtyMap: Record<number, string> = {}
        updatedLines.forEach((line) => {
          if (line.id) qtyMap[line.id] = String(line.quantity ?? '')
        })
        setViewLineQtyDrafts(qtyMap)
      } else {
        setViewLines((prev) =>
          prev.map((line) => (line.id === lineId ? {...line, quantity} : line))
        )
      }

      toast.success('Line quantity updated')
      await loadRequisitions()
    } catch (error: any) {
      console.error('Error updating line quantity:', error)
      toast.error('Failed to update quantity')
    } finally {
      setUpdatingViewLineId(null)
    }
  }

  const handleDeleteRequisition = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this requisition?')) return

    try {
      await deleteRequisition(id)
      await loadRequisitions()
      toast.success('Requisition deleted successfully!')
    } catch (error: any) {
      console.error('Error deleting requisition:', error)
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to delete requisition'

      if (
        errorMessage.includes('foreign key constraint') ||
        errorMessage.includes('Cannot delete or update a parent row')
      ) {
        toast.error(
          'Cannot delete this requisition because it has associated records. Please check dependencies first.'
        )
      } else {
        toast.error('Failed to delete requisition')
      }
    }
  }

  const handleSubmitRequisition = async (id: number) => {
    if (!window.confirm('Submit this requisition to master?')) return

    try {
      await submitRequisition(id)
      toast.success('Requisition submitted successfully!')
      await runPostActionAutoRefresh(id)
    } catch (error: any) {
      console.error('Error submitting requisition:', error)
      toast.error('Failed to submit requisition')
    }
  }

  const doVesselApprove = async (id: number) => {
    const resp = await approveVesselRequisition(id, {
      status: 'VESSEL_APPROVED',
      vesselApproved: true,
    })
    toast.success('Requisition approved for vessel successfully!')
    if (resp?.noLinesWarning) {
      toast.warning('Warning: this requisition has no line items. Approved anyway.')
    }
    return resp
  }

  const handleVesselApproval = async (id: number) => {
    try {
      // Fetch details to check if lines exist
      const reqDetails = await getRequisitionById(id)
      const hasLines = (reqDetails?.lines?.length ?? 0) > 0

      if (!hasLines) {
        setVesselNoLinesConfirm({open: true, reqId: id})
        return
      }

      if (!window.confirm('Approve this requisition for vessel?')) return
      await doVesselApprove(id)
      await runPostActionAutoRefresh(id)
    } catch (error: any) {
      console.error('Error with vessel approval:', error)
      console.error('Full error response:', error.response)
      toast.error('Failed to approve for vessel')
    }
  }

  const handleShoreApproval = async (id: number) => {
    if (!window.confirm('Approve this requisition for shore?')) return

    try {
      const resp = await approveShoreRequisition(id, {
        status: 'SHORE_APPROVED',
        shoreApproved: true,
      })
      toast.success('Requisition approved for shore successfully!')
      if (resp?.noLinesWarning) {
        toast.warning('Warning: this requisition has no line items. Approved anyway.')
      }
      await runPostActionAutoRefresh(id)
    } catch (error: any) {
      console.error('Error with shore approval:', error)
      toast.error('Failed to approve for shore')
    }
  }

  const openRemarkActionModal = (type: 'review' | 'reject', requisitionId: number) => {
    setRemarkActionModal({open: true, type, requisitionId})
    setRemarkActionText('')
  }

  const closeRemarkActionModal = () => {
    setRemarkActionModal({open: false, type: 'review', requisitionId: null})
    setRemarkActionText('')
  }

  const submitRemarkAction = async () => {
    const requisitionId = remarkActionModal.requisitionId
    if (!requisitionId) return

    const remark = remarkActionText.trim()
    if (!remark) {
      toast.error(
        remarkActionModal.type === 'review'
          ? 'Review remark is required'
          : 'Rejection cause is required'
      )
      return
    }

    try {
      setRemarkActionSubmitting(true)
      if (remarkActionModal.type === 'review') {
        await sendReworkToVessel(requisitionId, remark)
        toast.info('Rework sent to vessel successfully')
      } else {
        await rejectRequisitionFromCompany(requisitionId, remark)
        toast.success('Requisition rejected successfully')
      }
      closeRemarkActionModal()
      await runPostActionAutoRefresh(requisitionId)
    } catch (error: any) {
      console.error('Error processing remark action:', error)
      toast.error(
        error.message ||
          (remarkActionModal.type === 'review'
            ? 'Failed to request review'
            : 'Failed to reject requisition')
      )
    } finally {
      setRemarkActionSubmitting(false)
    }
  }

  const handleRequestRevision = (id: number) => {
    openRemarkActionModal('review', id)
  }

  const handleCompanyRejection = (id: number) => {
    openRemarkActionModal('reject', id)
  }

  const stageOptions: FilterOption[] = [
    {value: '', label: 'All Stages'},
    {value: 'DRAFT', label: 'Draft'},
    {value: 'SUBMITTED', label: 'Submitted'},
    {value: 'VESSEL_APPROVED', label: 'Vessel Approved'},
    {value: 'SHORE_APPROVED', label: 'Shore Approved'},
    {value: 'RFQ_REWORK_PENDING', label: 'RFQ Rework Pending'},
    {value: 'RFQ_ISSUED', label: 'RFQ Issued'},
    {value: 'QUOTATION_RECEIVED', label: 'Quotation Received'},
    {value: 'PO_CREATED', label: 'PO Created'},
    {value: 'TO_BE_REVISED', label: 'To Be Revised'},
    {value: 'REJECTED', label: 'Rejected'},
    {value: 'PARTIALLY_COMPLETED', label: 'Partially Completed'},
    {value: 'FULFILLED', label: 'Fulfilled'},
    {value: 'CANCELLED', label: 'Cancelled'},
  ]
  const approvalPendingWithOptions: FilterOption[] = [
    {value: '', label: 'All'},
    {value: 'CREATOR', label: 'Creator'},
    {value: 'MASTER', label: 'Master'},
    {value: 'COMPANY', label: 'Company'},
    {value: 'NONE', label: 'None'},
  ]

  const departmentOptions = useMemo<FilterOption[]>(
    () => [
      {value: '', label: 'All Heads'},
      ...Array.from(
        new Set(
          requisitionData
            .map((r) => (r as any).inventoryItemHead)
            .filter((head) => head && head !== '-')
        )
      )
        .sort()
        .map((head) => ({value: head, label: head})),
    ],
    [requisitionData]
  )

  const catalogueOptions = useMemo<FilterOption[]>(
    () => [
      {value: '', label: 'All Catalogues'},
      ...Array.from(new Set(requisitionData.map((r) => r.category).filter((c) => c && c !== 'N/A')))
        .sort()
        .map((c) => ({value: c, label: c})),
    ],
    [requisitionData]
  )

  const resetFilters = () => {
    setSearchTerm('')
    setReferenceFilter('')
    setStatusFilter('')
    setVesselFilter('')
    setCategoryFilter('')
    setHeadFilter('')
    setSubHeadFilter('')
    setRequisitionTitleFilter('')
    setApprovalPendingWithFilter('')
    setPoDateFromFilter('')
    setPoDateToFilter('')
    setDeliveredToVesselFilter('')
    setCurrentPage(1)
  }

  const filteredData = requisitionData

  const pendingTabCount = activeRequisitionTab === 'pending' ? requisitionTotalElements : 0
  const pastTabCount = activeRequisitionTab === 'past' ? requisitionTotalElements : 0
  const sortedData = filteredData

  const handleSort = (key: keyof RequisitionDisplay) => {
    setCurrentPage(1)
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const filteredTotalElements = requisitionTotalElements
  const currentRecords = sortedData
  const totalPages = Math.max(1, requisitionTotalPages)

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  // Fetch all requisitions matching current filters (for export)
  const fetchAllRequisitionsForExport = async () => {
    const effectiveVesselId = vesselFilter ? parseInt(vesselFilter) : (requisitionScopeAll || requisitionScopeCompany ? null : vesselId)
    const response = await getRequisitions(0, requisitionTotalElements || 1000, {
      vesselId: effectiveVesselId,
      search: debouncedSearchTerm,
      status: statusFilter || undefined,
      reference: referenceFilter || undefined,
      title: requisitionTitleFilter || undefined,
      category: categoryFilter || undefined,
      head: headFilter || undefined,
      subHead: subHeadFilter || undefined,
      dateFrom: poDateFromFilter || undefined,
      dateTo: poDateToFilter || undefined,
      tab: activeRequisitionTab,
      approvalPendingWith: approvalPendingWithFilter || undefined,
      deliveredToVessel: deliveredToVesselFilter || undefined,
    })
    return response.content || []
  }

  // Export requisitions to PDF (ALL pages matching current filters)
  const exportRequisitionsToPDF = async () => {
    if (!canExportRequisitions) {
      toast.error('You do not have permission to export requisitions.')
      return
    }
    const toastId = toast.loading('Generating PDF...')
    try {
      const rawRequisitions = await fetchAllRequisitionsForExport()
      const doc = new jsPDF({orientation: 'landscape', format: 'a3'})

      // Title
      doc.setFontSize(16)
      doc.text('Requisition List', 14, 15)

      // Subtitle with date
      doc.setFontSize(10)
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22)
      doc.text(`Total Requisitions: ${rawRequisitions.length}`, 14, 28)

      const categoryMap = new Map(inventoryCategories.map((cat) => [cat.id, cat.name]))
      const vesselsMap = requisitionLookupCacheRef.current.vesselsMap

      // PDF columns: include vessel when requisition view scope is ALL
      const tableData = rawRequisitions.map((record) => {
        const vessel = vesselsMap.get(record.vesselId)
        const vesselName =
          vessel?.fleet_name ||
          (vessel as any)?.fleetName ||
          record.vesselName ||
          (record as any)?.vessel?.fleet_name ||
          (record as any)?.vessel?.fleetName ||
          (record as any)?.vessel?.name ||
          'N/A'

        const categoryId = record.category?.id || record.categoryId
        const categoryName = record.category?.name || (categoryId ? categoryMap.get(categoryId) || 'N/A' : 'N/A')
        const urgency = record.type === 'HIGH' ? 'High' : record.type === 'MEDIUM' ? 'Medium' : 'Low'
        const status = getRequisitionStatusLabel(record.status)

        const baseData = [
          record.title || '',
          record.description || '',
          categoryName,
          urgency,
          record.requiredDeliveryDate || '',
          status,
        ]

        // Add vessel name for all-scope requisition viewers
        if (showVesselColumn) {
          return [vesselName, ...baseData]
        }
        return baseData
      })

      const headers =
        showVesselColumn
          ? [
              [
                'Vessel',
                'Title',
                'Description',
                'Category',
                'Urgency',
                'Due Date',
                'Status',
              ],
            ]
          : [['Title', 'Description', 'Category', 'Urgency', 'Due Date', 'Status']]

      // Generate table
      ;(doc as any).autoTable({
        head: headers,
        body: tableData,
        startY: 35,
        styles: {
          fontSize: 7,
          cellPadding: 2.5,
          overflow: 'linebreak',
          cellWidth: 'wrap',
          minCellHeight: 6,
          valign: 'middle',
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 8,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        columnStyles:
          showVesselColumn
            ? {
                0: {cellWidth: 32}, // Vessel
                1: {cellWidth: 42}, // Title
                2: {cellWidth: 62}, // Description
                3: {cellWidth: 30}, // Category
                4: {cellWidth: 25}, // Urgency
                5: {cellWidth: 30}, // Due Date
                6: {cellWidth: 30}, // Status
              }
            : {
                0: {cellWidth: 45}, // Title
                1: {cellWidth: 62}, // Description
                2: {cellWidth: 35}, // Category
                3: {cellWidth: 28}, // Urgency
                4: {cellWidth: 35}, // Due Date
                5: {cellWidth: 35}, // Status
              },
        margin: {top: 35, left: 10, right: 10},
        pageBreak: 'auto',
        tableLineWidth: 0.1,
        tableLineColor: [200, 200, 200],
      })

      // Save the PDF
      doc.save(`Requisition_List_${new Date().toISOString().slice(0, 10)}.pdf`)
      toast.update(toastId, {
        render: 'PDF exported successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      })
    } catch (error) {
      console.error('PDF export failed:', error)
      toast.update(toastId, {
        render: 'Failed to export PDF',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  // Export requisitions to Excel (ALL pages matching current filters)
  const exportRequisitionsToExcel = async () => {
    if (!canExportRequisitions) {
      toast.error('You do not have permission to export requisitions.')
      return
    }
    const toastId = toast.loading('Generating Excel...')
    try {
      const rawRequisitions = await fetchAllRequisitionsForExport()
      const categoryMap = new Map(inventoryCategories.map((cat) => [cat.id, cat.name]))
      const vesselsMap = requisitionLookupCacheRef.current.vesselsMap

      const exportRows = rawRequisitions.map((record) => {
        const vessel = vesselsMap.get(record.vesselId)
        const vesselName =
          vessel?.fleet_name ||
          (vessel as any)?.fleetName ||
          record.vesselName ||
          (record as any)?.vessel?.fleet_name ||
          (record as any)?.vessel?.fleetName ||
          (record as any)?.vessel?.name ||
          'N/A'

        const categoryId = record.category?.id || record.categoryId
        const categoryName = record.category?.name || (categoryId ? categoryMap.get(categoryId) || 'N/A' : 'N/A')
        const urgency = record.type === 'HIGH' ? 'High' : record.type === 'MEDIUM' ? 'Medium' : 'Low'
        const status = getRequisitionStatusLabel(record.status)

        const row: Record<string, string> = {}
        if (showVesselColumn) {
          row['Vessel'] = vesselName
        }
        row['Title'] = record.title || ''
        row['Description'] = record.description || ''
        row['Category'] = categoryName
        row['Urgency'] = urgency
        row['Due Date'] = record.requiredDeliveryDate || ''
        row['Status'] = status

        return row
      })

      const worksheet = XLSX.utils.json_to_sheet(exportRows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Requisitions')
      XLSX.writeFile(workbook, `Requisition_List_${new Date().toISOString().slice(0, 10)}.xlsx`)

      toast.update(toastId, {
        render: 'Excel exported successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      })
    } catch (error) {
      console.error('Excel export failed:', error)
      toast.update(toastId, {
        render: 'Failed to export Excel',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const handleAddRequisitionSuccess = () => {
    console.log('ðŸ”„ Reloading requisitions after creation...')
    console.log('ðŸ”„ Current vesselId:', vesselId)
    loadRequisitions()
  }

  const handleAttachmentClick = (attachment: FileRefDto) => {
    setSelectedAttachment(attachment)
  }

  const handleAddItems = (requisitionId: number) => {
    setSelectedRequisitionId(requisitionId)
    setAddItemsModalVisible(true)
  }

  const handleHeadSubmit = async (payload: CreateInventoryItemHeadRequest) => {
    await createInventoryItemHead(payload)
    toast.success('Head saved')
    setShowHeadForm(false)
    setInventoryItemHeads([])
    await loadHeadLibrary()
  }

  const handleSubHeadSubmit = async (payload: { name: string; inventoryItemHeadId: number }) => {
    await createInventoryItemSubHead(payload)
    toast.success('Sub Head saved')
    setShowSubHeadForm(false)
    setInventoryItemSubHeads([])
    await loadSubHeadLibrary()
  }

  const handleAddItemsSuccess = () => {
    setAddItemsModalVisible(false)
    setSelectedRequisitionId(null)
    loadRequisitions()
  }

  const handleCategorySubmit = () => {
    setShowCategoryForm(false)
    setInventoryCategories([])
    loadCategoryLibrary()
  }

  const handleEditCategorySubmit = () => {
    setEditingCategory(null)
    setInventoryCategories([])
    loadCategoryLibrary()
  }

  const handleEditHeadSubmit = async (
    id: number,
    payload: CreateInventoryItemHeadRequest
  ) => {
    await updateInventoryItemHead(id, payload)
    toast.success('Head updated')
    setEditingHead(null)
    setInventoryItemHeads([])
    await loadHeadLibrary()
  }

  const handleEditSubHeadSubmit = async (
    id: number,
    payload: { name: string; inventoryItemHeadId: number }
  ) => {
    await updateInventoryItemSubHead(id, payload)
    toast.success('Sub Head updated')
    setEditingSubHead(null)
    setInventoryItemSubHeads([])
    await loadSubHeadLibrary()
  }

  const handleAccountingCodeSubmit = () => {
    setShowAccountingCodeForm(false)
    setAccountingAccounts([])
    loadAccountingLibrary()
  }

  const handleEditAccountingCodeSubmit = () => {
    setEditingAccountingCode(null)
    setAccountingAccounts([])
    loadAccountingLibrary()
  }

  const handleSubAccountingSubmit = () => {
    setShowSubAccountingForm(false)
    setSubAccounts([])
    loadSubAccountingLibrary()
  }

  const handleEditSubAccountingSubmit = () => {
    setEditingSubAccounting(null)
    setSubAccounts([])
    loadSubAccountingLibrary()
  }

  const resolveAddressCompanyScopeId = useCallback((): number | null => {
    if (requisitionScopeAll) {
      return addressForm.companyId ?? libraryCompanyFilterId ?? null
    }
    if (requisitionScopeVessel) {
      return crewLibraryResolvedCompanyScopeId ?? null
    }
    return libraryCompanyFilterId ?? crewLibraryResolvedCompanyScopeId ?? null
  }, [
    requisitionScopeAll,
    requisitionScopeVessel,
    addressForm.companyId,
    libraryCompanyFilterId,
    crewLibraryResolvedCompanyScopeId,
  ])

  const handleSaveAddressLibrary = useCallback(async () => {
    try {
      const companyId = resolveAddressCompanyScopeId()
      if (requisitionScopeAll && !companyId) {
        toast.error('Select company before saving address')
        return
      }
      if (!addressForm.addressName.trim()) {
        toast.error('Address label is required')
        return
      }
      if (!addressForm.addressLine1.trim()) {
        toast.error('Address line 1 is required')
        return
      }

      const payload: CreateInventoryAddressRequest = {
        addressType: addressForm.addressType,
        addressName: addressForm.addressName.trim(),
        addressLine1: addressForm.addressLine1.trim(),
        addressLine2: addressForm.addressLine2.trim() || undefined,
        city: addressForm.city.trim() || undefined,
        state: addressForm.state.trim() || undefined,
        country: addressForm.country.trim() || undefined,
        postalCode: addressForm.postalCode.trim() || undefined,
        active: addressForm.active,
        cgaid: companyId ? {id: companyId} : undefined,
      }

      if (addressForm.id) {
        await updateInventoryAddress(addressForm.id, payload)
        toast.success('Address updated')
      } else {
        await createInventoryAddress(payload)
        toast.success('Address created')
      }

      setAddressForm((prev) => ({...defaultAddressFormState, companyId: prev.companyId}))
      setShowAddressForm(false)
      await loadAddressLibrary()
    } catch (error: any) {
      toast.error('Failed to save address')
    }
  }, [addressForm, requisitionScopeAll, resolveAddressCompanyScopeId, loadAddressLibrary])

  const handleEditAddressLibrary = useCallback((address: InventoryAddress) => {
    setAddressForm({
      id: address.id,
      companyId: address.cgaid?.id ?? null,
      addressType: (address.addressType || 'BOTH') as 'BILLING' | 'SHIPPING' | 'BOTH',
      addressName: address.addressName || '',
      addressLine1: address.addressLine1 || '',
      addressLine2: address.addressLine2 || '',
      city: address.city || '',
      state: address.state || '',
      country: address.country || '',
      postalCode: address.postalCode || '',
      active: Boolean(address.active),
    })
    setShowAddressForm(true)
  }, [])

  const handleDeleteAddressLibrary = useCallback(
    async (addressId?: number) => {
      if (!addressId) return
      if (!window.confirm('Delete this address?')) return
      try {
        await deleteInventoryAddress(addressId)
        toast.success('Address deleted')
        await loadAddressLibrary()
      } catch (error: any) {
        toast.error('Failed to delete address')
      }
    },
    [loadAddressLibrary]
  )

  const closeRequisitionLibraries = () => {
    setIsRequisitionLibrariesOpen(false)
    setActiveRequisitionLibrary(defaultLibraryTab)
    setLibraryCompanyFilterId(null)
    setLibraryVesselFilterId(null)
    setLibraryHeadFilterId(null)
    setShowAddressForm(false)
    setAddressForm(defaultAddressFormState)
  }

  type LibraryScopeMeta = {
    companyId: number | null
    companyName: string
    vesselId: number | null
    vesselName: string
  }

  type LibrarySelectOption = {
    id: number
    name: string
  }

  const parseScopeId = (value: any): number | null => parsePositiveId(value)

  const inventoryCategoryById = useMemo(
    () => new Map((inventoryCategories || []).map((category) => [category.id, category])),
    [inventoryCategories]
  )

  const inventoryHeadById = useMemo(
    () => new Map((inventoryItemHeads || []).map((head) => [head.id, head])),
    [inventoryItemHeads]
  )

  const accountingById = useMemo(
    () => new Map((accountingAccounts || []).map((account) => [account.id, account])),
    [accountingAccounts]
  )

  const resolveCategoryScopeMeta = useCallback((category: InventoryCategory): LibraryScopeMeta => {
    const companyId = parseScopeId(category?.cgaid?.id)
    const caId = parseScopeId((category as any)?.caid?.id)
    const caName = (category as any)?.caid?.name
    return {
      companyId,
      companyName: category?.cgaid?.name || (companyId ? `Company ${companyId}` : ''),
      vesselId: null,
      vesselName: caId ? (caName || `Company ${caId}`) : 'Company-wide',
    }
  }, [])

  const resolveHeadScopeMeta = useCallback(
    (head: InventoryItemHead): LibraryScopeMeta => {
      const linkedCategory = head?.inventoryItemCategoryId
        ? inventoryCategoryById.get(Number(head.inventoryItemCategoryId))
        : undefined
      const linkedCategoryScope = linkedCategory ? resolveCategoryScopeMeta(linkedCategory) : null

      const companyId = parseScopeId(head?.cgaid?.id) ?? linkedCategoryScope?.companyId ?? null
      const vesselId =
        parseScopeId((head as any)?.vessel?.id ?? head?.vesselId) ?? linkedCategoryScope?.vesselId ?? null

      return {
        companyId,
        companyName:
          head?.cgaid?.name ||
          linkedCategoryScope?.companyName ||
          (companyId ? `Company ${companyId}` : ''),
        vesselId,
        vesselName:
          (head as any)?.vessel?.fleet_name ||
          head?.vesselName ||
          linkedCategoryScope?.vesselName ||
          (vesselId ? `Vessel ${vesselId}` : ''),
      }
    },
    [inventoryCategoryById, resolveCategoryScopeMeta]
  )

  const resolveSubHeadScopeMeta = useCallback(
    (subHead: InventoryItemSubHead): LibraryScopeMeta => {
      const linkedHead = subHead?.inventoryItemHeadId
        ? inventoryHeadById.get(Number(subHead.inventoryItemHeadId))
        : undefined
      const linkedHeadScope = linkedHead ? resolveHeadScopeMeta(linkedHead) : null

      const companyId = parseScopeId(subHead?.cgaid?.id) ?? linkedHeadScope?.companyId ?? null
      const vesselId =
        parseScopeId((subHead as any)?.vessel?.id ?? subHead?.vesselId) ?? linkedHeadScope?.vesselId ?? null

      return {
        companyId,
        companyName:
          subHead?.cgaid?.name ||
          linkedHeadScope?.companyName ||
          (companyId ? `Company ${companyId}` : ''),
        vesselId,
        vesselName:
          (subHead as any)?.vessel?.fleet_name ||
          subHead?.vesselName ||
          linkedHeadScope?.vesselName ||
          (vesselId ? `Vessel ${vesselId}` : ''),
      }
    },
    [inventoryHeadById, resolveHeadScopeMeta]
  )

  const resolveAccountingScopeMeta = useCallback((account: AccountingAccount): LibraryScopeMeta => {
    const companyId =
      typeof account?.cgaid === 'object' ? parseScopeId(account?.cgaid?.id) : parseScopeId(account?.cgaid)
    const vesselId = parseScopeId((account as any)?.vessel?.id ?? account?.vesselId)

    return {
      companyId,
      companyName:
        (typeof account?.cgaid === 'object' ? account?.cgaid?.name : '') ||
        (companyId ? `Company ${companyId}` : ''),
      vesselId,
      vesselName:
        (account as any)?.vessel?.fleet_name ||
        account?.vesselName ||
        (vesselId ? `Vessel ${vesselId}` : ''),
    }
  }, [])

  const resolveSubAccountingScopeMeta = useCallback(
    (subAccount: SubAccount): LibraryScopeMeta => {
      const parentAccount = (subAccount as any)?.accountCodeId
        ? accountingById.get(Number((subAccount as any)?.accountCodeId))
        : undefined
      const parentScope = parentAccount ? resolveAccountingScopeMeta(parentAccount) : null

      const companyId = parseScopeId(subAccount?.cgaid?.id) ?? parentScope?.companyId ?? null
      const vesselId =
        parseScopeId((subAccount as any)?.vessel?.id ?? subAccount?.vesselId) ?? parentScope?.vesselId ?? null

      return {
        companyId,
        companyName:
          subAccount?.cgaid?.name ||
          parentScope?.companyName ||
          (companyId ? `Company ${companyId}` : ''),
        vesselId,
        vesselName:
          (subAccount as any)?.vessel?.fleet_name ||
          subAccount?.vesselName ||
          parentScope?.vesselName ||
          (vesselId ? `Vessel ${vesselId}` : ''),
      }
    },
    [accountingById, resolveAccountingScopeMeta]
  )

  const resolveAddressScopeMeta = useCallback((address: InventoryAddress): LibraryScopeMeta => {
    const companyId = parseScopeId(address?.cgaid?.id)
    return {
      companyId,
      companyName: address?.cgaid?.name || (companyId ? `Company ${companyId}` : ''),
      vesselId: null,
      vesselName: '',
    }
  }, [])

  const effectiveLibraryCompanyFilterId = requisitionScopeVessel
    ? crewLibraryResolvedCompanyScopeId
    : libraryCompanyFilterId
  const effectiveLibraryVesselFilterId = requisitionScopeVessel
    ? crewLibraryResolvedVesselScopeId
    : libraryVesselFilterId

  const matchesLibraryScope = useCallback(
    (scope: LibraryScopeMeta) => {
      if (
        effectiveLibraryCompanyFilterId &&
        scope.companyId !== null &&
        scope.companyId !== effectiveLibraryCompanyFilterId
      ) {
        return false
      }

      if (
        effectiveLibraryVesselFilterId &&
        scope.vesselId !== null &&
        scope.vesselId !== effectiveLibraryVesselFilterId
      ) {
        return false
      }

      return true
    },
    [effectiveLibraryCompanyFilterId, effectiveLibraryVesselFilterId]
  )

  const formatLibraryVesselLabel = useCallback((scope: LibraryScopeMeta) => {
    if (!scope.vesselId) return 'All Vessels'
    return scope.vesselName || `Vessel ${scope.vesselId}`
  }, [])

  const confirmLibraryDelete = (label: string, scopeLabel: string) =>
    window.confirm(`Delete "${label}"?\n\nScope: ${scopeLabel}\n\nThis cannot be undone.`)

  const renderLibraryRowActions = useCallback(
    (
      canUpdate: boolean,
      canDelete: boolean,
      onEdit: () => void,
      onDelete: () => void
    ) => {
      if (!canUpdate && !canDelete) return null
      return (
        <div className='d-flex justify-content-center gap-1'>
          {canUpdate && (
            <button type='button' className='btn btn-sm btn-light-warning px-2' onClick={onEdit}>
              Edit
            </button>
          )}
          {canDelete && (
            <button type='button' className='btn btn-sm btn-light-danger px-2' onClick={onDelete}>
              Delete
            </button>
          )}
        </div>
      )
    },
    []
  )

  const handleDeleteCategoryLibrary = useCallback(
    async (category: InventoryCategory) => {
      if (!category?.id) return
      const scope = resolveCategoryScopeMeta(category)
      const scopeLabel = `${scope.companyName || 'Company'} / ${formatLibraryVesselLabel(scope)}`
      if (!confirmLibraryDelete(category.name || 'Category', scopeLabel)) return
      try {
        await deleteInventoryCategory(category.id)
        toast.success('Category deleted')
        setInventoryCategories([])
        await loadCategoryLibrary()
      } catch (error: any) {
        toast.error('Failed to delete category')
      }
    },
    [loadCategoryLibrary, resolveCategoryScopeMeta, formatLibraryVesselLabel]
  )

  const handleDeleteHeadLibrary = useCallback(
    async (head: InventoryItemHead) => {
      if (!head?.id) return
      const scope = resolveHeadScopeMeta(head)
      const scopeLabel = `${scope.companyName || 'Company'} / ${formatLibraryVesselLabel(scope)}`
      if (!confirmLibraryDelete(head.name || 'Head', scopeLabel)) return
      try {
        await deleteInventoryItemHead(head.id)
        toast.success('Head deleted')
        setInventoryItemHeads([])
        await loadHeadLibrary()
      } catch (error: any) {
        toast.error('Failed to delete head')
      }
    },
    [loadHeadLibrary, resolveHeadScopeMeta, formatLibraryVesselLabel]
  )

  const handleDeleteSubHeadLibrary = useCallback(
    async (subHead: InventoryItemSubHead) => {
      if (!subHead?.id) return
      const scope = resolveSubHeadScopeMeta(subHead)
      const scopeLabel = `${scope.companyName || 'Company'} / ${formatLibraryVesselLabel(scope)}`
      if (!confirmLibraryDelete(subHead.name || 'Sub Head', scopeLabel)) return
      try {
        await deleteInventoryItemSubHead(subHead.id)
        toast.success('Sub Head deleted')
        setInventoryItemSubHeads([])
        await loadSubHeadLibrary()
      } catch (error: any) {
        toast.error('Failed to delete sub head')
      }
    },
    [loadSubHeadLibrary, resolveSubHeadScopeMeta, formatLibraryVesselLabel]
  )

  const handleDeleteAccountingLibrary = useCallback(
    async (account: AccountingAccount) => {
      if (!account?.id) return
      const scope = resolveAccountingScopeMeta(account)
      const scopeLabel = `${scope.companyName || 'Company'} / ${formatLibraryVesselLabel(scope)}`
      if (!confirmLibraryDelete(account.accountCodes || 'Accounting Code', scopeLabel)) return
      try {
        await deleteAccountingAccount(account.id)
        toast.success('Accounting code deleted')
        setAccountingAccounts([])
        await loadAccountingLibrary()
      } catch (error: any) {
        toast.error('Failed to delete accounting code')
      }
    },
    [loadAccountingLibrary, resolveAccountingScopeMeta, formatLibraryVesselLabel]
  )

  const handleDeleteSubAccountingLibrary = useCallback(
    async (subAccount: SubAccount) => {
      if (!subAccount?.id) return
      const scope = resolveSubAccountingScopeMeta(subAccount)
      const scopeLabel = `${scope.companyName || 'Company'} / ${formatLibraryVesselLabel(scope)}`
      if (!confirmLibraryDelete(subAccount.subaccountCodes || 'Sub Accounting', scopeLabel)) return
      try {
        await deleteSubAccount(subAccount.id)
        toast.success('Sub accounting deleted')
        setSubAccounts([])
        await loadSubAccountingLibrary()
      } catch (error: any) {
        toast.error('Failed to delete sub accounting')
      }
    },
    [loadSubAccountingLibrary, resolveSubAccountingScopeMeta, formatLibraryVesselLabel]
  )

  const activeLibrarySearchTerm = debouncedLibrarySearchByTab[activeRequisitionLibrary] ?? ''

  const filteredCategoryLibraryRows = useMemo(
    () =>
      (inventoryCategories || []).filter((category) => {
        const scope = resolveCategoryScopeMeta(category)
        if (
          effectiveLibraryCompanyFilterId &&
          scope.companyId !== null &&
          scope.companyId !== effectiveLibraryCompanyFilterId
        ) {
          return false
        }
        return matchesLibrarySearch(
          [
            category.name,
            category.cgaid?.name,
            formatLibraryVesselLabel(scope),
            typeof category.vesselType === 'string'
              ? category.vesselType
              : (category.vesselType as any)?.fleet_name,
          ],
          activeLibrarySearchTerm
        )
      }),
    [
      inventoryCategories,
      effectiveLibraryCompanyFilterId,
      resolveCategoryScopeMeta,
      formatLibraryVesselLabel,
      activeLibrarySearchTerm,
    ],
  )

  const filteredHeadLibraryRows = useMemo(
    () =>
      (inventoryItemHeads || []).filter((head) => {
        if (!matchesLibraryScope(resolveHeadScopeMeta(head))) return false
        const linkedCategory = inventoryCategoryById.get(Number(head.inventoryItemCategoryId))
        const scope = resolveHeadScopeMeta(head)
        return matchesLibrarySearch(
          [
            head.name,
            linkedCategory?.name,
            scope.companyName,
            formatLibraryVesselLabel(scope),
          ],
          activeLibrarySearchTerm
        )
      }),
    [
      inventoryItemHeads,
      matchesLibraryScope,
      resolveHeadScopeMeta,
      inventoryCategoryById,
      formatLibraryVesselLabel,
      activeLibrarySearchTerm,
    ]
  )

  const filteredSubHeadLibraryRows = useMemo(
    () =>
      (inventoryItemSubHeads || []).filter((subHead) => {
        const withinScope = matchesLibraryScope(resolveSubHeadScopeMeta(subHead))
        if (!withinScope) return false
        if (libraryHeadFilterId && Number(subHead.inventoryItemHeadId) !== libraryHeadFilterId) {
          return false
        }
        const linkedHead = inventoryHeadById.get(Number(subHead.inventoryItemHeadId))
        const linkedCategory = linkedHead
          ? inventoryCategoryById.get(Number(linkedHead.inventoryItemCategoryId))
          : undefined
        const scope = resolveSubHeadScopeMeta(subHead)
        return matchesLibrarySearch(
          [
            subHead.name,
            linkedHead?.name,
            linkedCategory?.name,
            scope.companyName,
            formatLibraryVesselLabel(scope),
          ],
          activeLibrarySearchTerm
        )
      }),
    [
      inventoryItemSubHeads,
      matchesLibraryScope,
      resolveSubHeadScopeMeta,
      libraryHeadFilterId,
      inventoryHeadById,
      inventoryCategoryById,
      formatLibraryVesselLabel,
      activeLibrarySearchTerm,
    ]
  )

  const filteredSubCategoryLibraryRows = useMemo(
    () =>
      (inventoryItemSubCategories || []).filter((subCategory) =>
        matchesLibrarySearch([subCategory.name, subCategory.description], activeLibrarySearchTerm)
      ),
    [inventoryItemSubCategories, activeLibrarySearchTerm]
  )

  const filteredAccountingLibraryRows = useMemo(
    () =>
      (accountingAccounts || []).filter((account) => {
        if (!matchesLibraryScope(resolveAccountingScopeMeta(account))) return false
        const scope = resolveAccountingScopeMeta(account)
        return matchesLibrarySearch(
          [account.accountCodes, account.accountName, scope.companyName, formatLibraryVesselLabel(scope)],
          activeLibrarySearchTerm
        )
      }),
    [
      accountingAccounts,
      matchesLibraryScope,
      resolveAccountingScopeMeta,
      formatLibraryVesselLabel,
      activeLibrarySearchTerm,
    ]
  )

  const filteredAddressLibraryRows = useMemo(
    () =>
      (inventoryAddresses || []).filter((address) => {
        if (!matchesLibraryScope(resolveAddressScopeMeta(address))) return false
        const scope = resolveAddressScopeMeta(address)
        return matchesLibrarySearch(
          [
            address.addressName,
            address.address,
            address.city,
            address.state,
            address.country,
            scope.companyName,
          ],
          activeLibrarySearchTerm
        )
      }),
    [
      inventoryAddresses,
      matchesLibraryScope,
      resolveAddressScopeMeta,
      activeLibrarySearchTerm,
    ]
  )

  const filteredSubAccountingLibraryRows = useMemo(
    () =>
      (subAccounts || []).filter((subAccount) => {
        if (!matchesLibraryScope(resolveSubAccountingScopeMeta(subAccount))) return false
        const scope = resolveSubAccountingScopeMeta(subAccount)
        const parentAccount = (subAccount as any)?.accountCodeId
          ? accountingById.get(Number((subAccount as any)?.accountCodeId))
          : undefined
        return matchesLibrarySearch(
          [
            subAccount.subaccountCodes,
            subAccount.subaccountName,
            parentAccount?.accountCodes,
            parentAccount?.accountName,
            scope.companyName,
            formatLibraryVesselLabel(scope),
          ],
          activeLibrarySearchTerm
        )
      }),
    [
      subAccounts,
      matchesLibraryScope,
      resolveSubAccountingScopeMeta,
      accountingById,
      formatLibraryVesselLabel,
      activeLibrarySearchTerm,
    ]
  )

  const activeLibraryFilteredCount = useMemo(() => {
    switch (activeRequisitionLibrary) {
      case 'category':
        return filteredCategoryLibraryRows.length
      case 'head':
        return filteredHeadLibraryRows.length
      case 'subHead':
        return filteredSubHeadLibraryRows.length
      case 'subCategory':
        return filteredSubCategoryLibraryRows.length
      case 'address':
        return filteredAddressLibraryRows.length
      case 'accountingCode':
        return filteredAccountingLibraryRows.length
      case 'subAccounting':
        return filteredSubAccountingLibraryRows.length
      default:
        return 0
    }
  }, [
    activeRequisitionLibrary,
    filteredCategoryLibraryRows,
    filteredHeadLibraryRows,
    filteredSubHeadLibraryRows,
    filteredSubCategoryLibraryRows,
    filteredAddressLibraryRows,
    filteredAccountingLibraryRows,
    filteredSubAccountingLibraryRows,
  ])

  const activeLibraryScopeRows = useMemo(() => {
    switch (activeRequisitionLibrary) {
      case 'category':
        return (inventoryCategories || []).map((category) => resolveCategoryScopeMeta(category))
      case 'head':
        return (inventoryItemHeads || []).map((head) => resolveHeadScopeMeta(head))
      case 'subHead':
        return (inventoryItemSubHeads || []).map((subHead) => resolveSubHeadScopeMeta(subHead))
      case 'accountingCode':
        return (accountingAccounts || []).map((account) => resolveAccountingScopeMeta(account))
      case 'address':
        return (inventoryAddresses || []).map((address) => resolveAddressScopeMeta(address))
      case 'subAccounting':
        return (subAccounts || []).map((subAccount) => resolveSubAccountingScopeMeta(subAccount))
      default:
        return []
    }
  }, [
    activeRequisitionLibrary,
    inventoryCategories,
    inventoryItemHeads,
    inventoryItemSubHeads,
    accountingAccounts,
    inventoryAddresses,
    subAccounts,
    resolveCategoryScopeMeta,
    resolveHeadScopeMeta,
    resolveSubHeadScopeMeta,
    resolveAccountingScopeMeta,
    resolveAddressScopeMeta,
    resolveSubAccountingScopeMeta,
  ])

  const libraryCompanyOptions = useMemo<LibrarySelectOption[]>(() => {
    const companyMap = new Map<number, string>()
    activeLibraryScopeRows.forEach((scope) => {
      if (!scope.companyId) return
      if (!companyMap.has(scope.companyId)) {
        companyMap.set(scope.companyId, scope.companyName || `Company ${scope.companyId}`)
      }
    })
    return Array.from(companyMap.entries())
      .map(([id, name]) => ({id, name}))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [activeLibraryScopeRows])

  const libraryVesselOptions = useMemo<LibrarySelectOption[]>(() => {
    const vesselMap = new Map<number, string>()
    activeLibraryScopeRows.forEach((scope) => {
      if (!scope.vesselId) return
      if (effectiveLibraryCompanyFilterId && scope.companyId !== effectiveLibraryCompanyFilterId) return
      if (!vesselMap.has(scope.vesselId)) {
        vesselMap.set(scope.vesselId, scope.vesselName || `Vessel ${scope.vesselId}`)
      }
    })
    return Array.from(vesselMap.entries())
      .map(([id, name]) => ({id, name}))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [activeLibraryScopeRows, effectiveLibraryCompanyFilterId])

  const addressCompanyOptions = useMemo<LibrarySelectOption[]>(
    () =>
      (libraryCompanyMasterOptions || [])
        .filter((company) => company?.id != null)
        .map((company) => ({
          id: Number(company.id),
          name: company.name || `Company ${company.id}`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [libraryCompanyMasterOptions]
  )

  const subHeadHeadFilterOptions = useMemo<LibrarySelectOption[]>(() => {
    const headMap = new Map<number, string>()
    ;(inventoryItemHeads || []).forEach((head) => {
      if (!matchesLibraryScope(resolveHeadScopeMeta(head))) return
      if (!head?.id) return
      if (!headMap.has(head.id)) {
        headMap.set(head.id, head.name || `ID:${head.id}`)
      }
    })
    return Array.from(headMap.entries())
      .map(([id, name]) => ({id, name}))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [inventoryItemHeads, matchesLibraryScope, resolveHeadScopeMeta])

  useEffect(() => {
    if (!libraryCompanyFilterId) return
    const companyStillValid = libraryCompanyOptions.some((option) => option.id === libraryCompanyFilterId)
    if (!companyStillValid) {
      setLibraryCompanyFilterId(null)
    }
  }, [libraryCompanyFilterId, libraryCompanyOptions])

  useEffect(() => {
    if (!libraryVesselFilterId) return
    const vesselStillValid = libraryVesselOptions.some((option) => option.id === libraryVesselFilterId)
    if (!vesselStillValid) {
      setLibraryVesselFilterId(null)
    }
  }, [libraryVesselFilterId, libraryVesselOptions])

  useEffect(() => {
    if (activeRequisitionLibrary !== 'subHead' || !libraryHeadFilterId) return
    const headStillValid = subHeadHeadFilterOptions.some((option) => option.id === libraryHeadFilterId)
    if (!headStillValid) {
      setLibraryHeadFilterId(null)
    }
  }, [activeRequisitionLibrary, libraryHeadFilterId, subHeadHeadFilterOptions])

  const renderLibraryScopeFilters = (withHeadFilter: boolean) => (
    <div className='card shadow-sm border'>
      <div className='card-body py-3'>
        <div className='row g-3 align-items-end mb-3'>
          <div className='col-md-8'>
            <label className='form-label fw-semibold text-dark mb-1'>Search</label>
            <div className='input-group input-group-sm'>
              <input
                type='text'
                className='form-control'
                placeholder={LIBRARY_SEARCH_PLACEHOLDERS[activeRequisitionLibrary]}
                value={librarySearchByTab[activeRequisitionLibrary] ?? ''}
                onChange={(e) =>
                  setLibrarySearchByTab((prev) => ({
                    ...prev,
                    [activeRequisitionLibrary]: e.target.value,
                  }))
                }
              />
              {(librarySearchByTab[activeRequisitionLibrary] ?? '').length > 0 && (
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() =>
                    setLibrarySearchByTab((prev) => ({
                      ...prev,
                      [activeRequisitionLibrary]: '',
                    }))
                  }
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className='col-md-4 text-md-end text-muted small'>
            {activeLibrarySearchTerm
              ? `${activeLibraryFilteredCount} matching record${activeLibraryFilteredCount === 1 ? '' : 's'}`
              : 'Search is case-insensitive and applies with the current filters.'}
          </div>
        </div>
        <div className='row g-3 align-items-end'>
          <div className={withHeadFilter ? 'col-md-4' : 'col-md-6'}>
            <label className='form-label fw-semibold text-dark mb-1'>Company</label>
            <SearchableSelect
              className='form-select form-select-sm'
              value={effectiveLibraryCompanyFilterId ?? ''}
              disabled={requisitionScopeVessel}
              onChange={(e) => {
                const nextCompanyId = e.target.value ? Number(e.target.value) : null
                setLibraryCompanyFilterId(nextCompanyId)
              }}
            >
              {requisitionScopeVessel ? (
                <option value={effectiveLibraryCompanyFilterId ?? ''}>
                  {(currentUser as any)?.vessel?.companyGroupAdmin?.name ||
                    (currentUser as any)?.companyGroupAdmin?.name ||
                    (effectiveLibraryCompanyFilterId
                      ? `Company ${effectiveLibraryCompanyFilterId}`
                      : 'My Company')}
                </option>
              ) : (
                <option value=''>All Companies</option>
              )}
              {libraryCompanyOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </SearchableSelect>
          </div>

          <div className={withHeadFilter ? 'col-md-4' : 'col-md-6'}>
            <label className='form-label fw-semibold text-dark mb-1'>Vessel</label>
            <SearchableSelect
              className='form-select form-select-sm'
              value={effectiveLibraryVesselFilterId ?? ''}
              disabled={requisitionScopeVessel}
              onChange={(e) => {
                const nextVesselId = e.target.value ? Number(e.target.value) : null
                setLibraryVesselFilterId(nextVesselId)
              }}
            >
              {requisitionScopeVessel ? (
                <option value={effectiveLibraryVesselFilterId ?? ''}>
                  {(currentUser as any)?.vessel?.fleet_name ||
                    (currentUser as any)?.vessel?.name ||
                    (effectiveLibraryVesselFilterId
                      ? `Vessel ${effectiveLibraryVesselFilterId}`
                      : 'My Vessel')}
                </option>
              ) : (
                <option value=''>All Vessels</option>
              )}
              {libraryVesselOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </SearchableSelect>
          </div>

          {withHeadFilter && (
            <div className='col-md-4'>
              <label className='form-label fw-semibold text-dark mb-1'>Head</label>
              <SearchableSelect
                className='form-select form-select-sm'
                value={libraryHeadFilterId ?? ''}
                onChange={(e) => setLibraryHeadFilterId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value=''>All Heads</option>
                {subHeadHeadFilterOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </SearchableSelect>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const getStatusBadge = (status: string) => {
    if (status.includes('Pending') || status === 'Submitted') {
      return <span className='badge bg-warning text-white'>{status}</span>
    }
    if (status.includes('Approved') || status.includes('Completed')) {
      return <span className='badge bg-success text-white'>{status}</span>
    }
    if (status.includes('Rejected')) {
      return <span className='badge bg-danger text-white'>{status}</span>
    }
    return <span className='badge bg-secondary text-white'>{status}</span>
  }

  const handleSavePortLibrary = async () => {
    const name = portForm.name.trim()
    const latitude = portForm.latitude.trim() === '' ? undefined : Number(portForm.latitude)
    const longitude = portForm.longitude.trim() === '' ? undefined : Number(portForm.longitude)
    if (!name) {
      toast.error('Port name is required')
      return
    }
    if ((latitude !== undefined && !Number.isFinite(latitude)) || (longitude !== undefined && !Number.isFinite(longitude))) {
      toast.error('Latitude and longitude must be valid numbers')
      return
    }

    setPortLibrarySaving(true)
    try {
      const payload = {name, main_port: name, latitude, longitude}
      if (editingPort) await updatePort(editingPort.id, payload)
      else await createPort(payload)
      setShowPortForm(false)
      setEditingPort(null)
      setPortForm({name: '', latitude: '', longitude: ''})
      setPortLibraryPage(0)
      await loadPortLibrary(0)
      toast.success(editingPort ? 'Port updated' : 'Port created')
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create port')
    } finally {
      setPortLibrarySaving(false)
    }
  }

  const requisitionLibraryNav = [
    {key: 'category', label: 'Category Library'},
    {key: 'head', label: 'Head Library'},
    {key: 'subHead', label: 'Sub Head Library'},
    {key: 'subCategory', label: 'Sub Category Library'},
    {key: 'uom', label: 'Unit of Measurement Library'},
    {key: 'address', label: 'Address Library'},
    {key: 'terms', label: 'Terms & Conditions Library'},
    {key: 'port', label: 'Port Library'},
    {key: 'accountingCode', label: 'Accounting Code Library'},
    {key: 'subAccounting', label: 'Sub Accounting Library'},
  ]
    .filter((item) => {
      switch (item.key) {
        case 'category':
          return canViewCategoryLibrary || canManageCategoryLibrary
        case 'head':
          return canViewHeadLibrary || canManageHeadLibrary
        case 'subHead':
          return canViewSubHeadLibrary || canManageSubHeadLibrary
        case 'subCategory':
          return canViewSubCategoryLibrary || canManageSubCategoryLibrary
        case 'uom':
          return canViewUomLibrary
        case 'address':
          return canViewAddressLibrary || canManageAddressLibrary
        case 'terms':
          return canViewTermsLibrary || canManageTermsLibrary
        case 'port':
          return canViewPortLibrary
        case 'accountingCode':
          return canViewAccountingLibrary || canManageAccountingLibrary
        case 'subAccounting':
          return canViewSubAccountingLibrary || canManageSubAccountingLibrary
        default:
          return false
      }
    })
    .map((item) => ({...item})) as Array<{
    key: 'category' | 'head' | 'subHead' | 'subCategory' | 'address' | 'terms' | 'accountingCode' | 'subAccounting' | 'uom' | 'port'
    label: string
  }>

  const defaultLibraryTab = requisitionLibraryNav[0]?.key ?? 'category'

  useEffect(() => {
    if (!isRequisitionLibrariesOpen) return
    if (requisitionLibraryNav.length === 0) return
    const currentTabVisible = requisitionLibraryNav.some((item) => item.key === activeRequisitionLibrary)
    if (!currentTabVisible) {
      setActiveRequisitionLibrary(defaultLibraryTab)
    }
  }, [
    isRequisitionLibrariesOpen,
    requisitionLibraryNav,
    activeRequisitionLibrary,
    defaultLibraryTab,
  ])

  const renderActions = (record: RequisitionDisplay) => {
    const statusKey = record.rawStatus || record.status
    const canCreateRFQFromRequisition =
      canCreateRFQ && ['SHORE_APPROVED', 'RFQ_ISSUED'].includes(String(statusKey).toUpperCase())
    const canEditRequisition =
      canAddItemsToRequisition &&
      (((requisitionViewScope === 'ALL' || requisitionViewScope === 'COMPANY') &&
        canAddItemsForStatus(statusKey)) ||
        (requisitionViewScope === 'VESSEL' && crewMayEditRequisitionLines(statusKey)))

    return (
      <div
        className='procurement-action-grid'
        style={{display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: '4px', justifyContent: 'center', alignItems: 'center'}}
      >
        <button
          className='btn btn-sm btn-light-primary px-3'
          title='View'
          style={{height: '30px', fontSize: '12px'}}
          onClick={() => handleViewRequisition(record)}
        >
          View
        </button>
        {canEditRequisition && (
          <button
            className='btn btn-sm btn-light-warning px-3'
            title='Edit'
            style={{height: '30px', fontSize: '12px'}}
            onClick={() => handleAddItems(record.id)}
          >
            Edit
          </button>
        )}
        {canCreateRFQFromRequisition && (
          <button
            type='button'
            className='btn btn-sm btn-light-success px-2'
            title='Create RFQ'
            aria-label={`Create RFQ for ${record.reqNumber || record.id}`}
            style={{height: '30px', fontSize: '12px'}}
            onClick={() => navigate(`/procurement/rfq-management?requisitionId=${record.id}`)}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-4' />
          </button>
        )}
        {canExportRequisitions && (
          <button
            className='btn btn-sm btn-light-primary px-3'
            title='Download'
            style={{height: '30px', fontSize: '12px'}}
            onClick={() => {
              void handleDownloadRequisition(record.id)
            }}
          >
            Download
          </button>
        )}
      </div>
    )
  }

  if (procurementPermissionsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading procurement permissions...</span>
        </div>
      </div>
    )
  }

  if (!canViewRequisition) {
    return <div className='alert alert-warning m-6'>You do not have requisition view access.</div>
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between align-items-center bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Requisition Management</h3>
                <p className='text-muted mb-0'>
                  {canCreateRequisition && 'Create new requisitions. '}
                  {canAddItemsToRequisition && 'Edit and add items. '}
                  {canApproveVesselRequisition && 'Approve submitted requisitions for vessel flow. '}
                  {canApproveShoreRequisition &&
                    'Approve/review requisitions on shore workflow. '}
                </p>
              </div>
              <div className='card-toolbar d-flex gap-2'>
                <button
                  type='button'
                  className='btn btn-light btn-sm'
                  disabled={!canOpenAnyLibrary}
                  onClick={() => {
                    if (!canOpenAnyLibrary || requisitionLibraryNav.length === 0) return
                    setIsRequisitionLibrariesOpen(true)
                    setActiveRequisitionLibrary(defaultLibraryTab)
                  }}
                >
                  Requisition Libraries
                </button>
                {canExportRequisitions && (
                  <button
                    type='button'
                    className='btn btn-light-primary btn-sm'
                    onClick={() => setIsExportModalOpen(true)}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-3' />
                    Export
                  </button>
                )}
                {canCreateRequisition && (
                  <button
                    type='button'
                    className='btn btn_primary'
                    onClick={() => setIsModalVisible(true)}
                  >
                    <span className='me-2'>+</span>
                    New Requisition
                  </button>
                )}
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <ProcurementFilterPanel
                searchValue={searchTerm}
                onSearchChange={(value) => {
                  setSearchTerm(value)
                  setCurrentPage(1)
                }}
                searchPlaceholder='Search requisitions...'
                referenceValue={referenceFilter}
                onReferenceChange={(value) => {
                  setReferenceFilter(value)
                  setCurrentPage(1)
                }}

                departmentLabel="Head"
                catalogueLabel="Category"
                departmentValue={headFilter}
                onDepartmentChange={(value) => {
                  setHeadFilter(value)
                  setCurrentPage(1)
                }}
                departmentOptions={departmentOptions}
                catalogueValue={categoryFilter}
                onCatalogueChange={(value) => {
                  setCategoryFilter(value)
                  setCurrentPage(1)
                }}
                catalogueOptions={catalogueOptions}
                requisitionTitleValue={requisitionTitleFilter}
                onRequisitionTitleChange={(value) => {
                  setRequisitionTitleFilter(value)
                  setCurrentPage(1)
                }}
                approvalPendingWithValue={approvalPendingWithFilter}
                onApprovalPendingWithChange={(value) => {
                  setApprovalPendingWithFilter(value)
                  setCurrentPage(1)
                }}
                approvalPendingWithOptions={approvalPendingWithOptions}
                poDateFrom={poDateFromFilter}
                onPoDateFromChange={(value) => {
                  setPoDateFromFilter(value)
                  setCurrentPage(1)
                }}
                poDateTo={poDateToFilter}
                onPoDateToChange={(value) => {
                  setPoDateToFilter(value)
                  setCurrentPage(1)
                }}
                deliveredToVesselValue={deliveredToVesselFilter}
                onDeliveredToVesselChange={(value) => {
                  setDeliveredToVesselFilter(value)
                  setCurrentPage(1)
                }}
                stageValue={statusFilter}
                onStageChange={(value) => {
                  setStatusFilter(value)
                  setCurrentPage(1)
                }}
                stageOptions={stageOptions}
                additionalOpen={additionalFiltersOpen}
                onToggleAdditional={() => setAdditionalFiltersOpen((prev) => !prev)}
                onReset={resetFilters}
                extraAdditional={
                  <div className='col-12 col-md-6 col-lg-3'>
                    <label className='form-label text-muted fw-semibold fs-7 mb-2'>Sub Head</label>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      value={subHeadFilter}
                      onChange={(e) => {
                        setSubHeadFilter(e.target.value)
                        setCurrentPage(1)
                      }}
                    >
                      <option value=''>All Sub Heads</option>
                      {Array.from(
                        new Set(
                          requisitionData
                            .map((r) => (r as any).inventoryItemSubHead)
                            .filter((sh) => sh && sh !== '-')
                        )
                      )
                        .sort()
                        .map((subHead) => (
                          <option key={subHead} value={subHead}>
                            {subHead}
                          </option>
                        ))}
                    </SearchableSelect>
                  </div>
                }
              />

              <div className='mb-8'>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
                  <div className='btn-group' role='group' aria-label='Requisition Tabs'>
                    <button
                      type='button'
                      className={`btn btn-sm ${
                        activeRequisitionTab === 'pending' ? 'btn-primary' : 'btn-light-primary'
                      }`}
                      onClick={() => {
                        setActiveRequisitionTab('pending')
                        setCurrentPage(1)
                      }}
                    >
                      Pending Approval ({pendingTabCount})
                    </button>
                    <button
                      type='button'
                      className={`btn btn-sm ${
                        activeRequisitionTab === 'past' ? 'btn-primary' : 'btn-light-primary'
                      }`}
                      onClick={() => {
                        setActiveRequisitionTab('past')
                        setCurrentPage(1)
                      }}
                    >
                      Archive ({pastTabCount})
                    </button>
                  </div>
                  <h4 className='fw-bold text-primary mb-0'>Requisitions ({filteredTotalElements})</h4>
                </div>

                {isLoading ? (
                  <div className='text-center py-5'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className='report-table table-responsive'>
                    <div style={{overflowX: 'auto'}}>
                      <table
                        className='table table-bordered align-middle procurement-list-table'
                        style={{tableLayout: 'fixed', width: '100%', minWidth: '1000px'}}
                      >
                        <thead className='table-header text-start'>
                          <tr>
                            <th
                              onClick={() => handleSort('title')}
                              className='cursor-pointer align-middle'
                              style={{padding: '12px 16px', width: '160px', minWidth: '160px'}}
                            >
                              <div className='d-flex align-items-center'>
                                <span style={{color: '#3F4254', fontWeight: 600, fontSize: '13px'}}>
                                  TITLE
                                </span>
                                <div style={{transform: 'translateY(-2px)'}}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${
                                      sortConfig.key === 'title'
                                        ? sortConfig.direction === 'asc'
                                          ? 'up-black'
                                          : 'down-black'
                                        : 'grey'
                                    }.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>
                            {showVesselColumn && (
                              <th
                                className='align-middle text-nowrap'
                                style={{
                                  padding: '12px 16px',
                                  color: '#3F4254',
                                  fontWeight: 600,
                                  fontSize: '13px',
                                  width: '150px',
                                }}
                              >
                                VESSEL
                              </th>
                            )}
                            <th
                              className='align-middle'
                              style={{
                                padding: '12px 16px',
                                color: '#3F4254',
                                fontWeight: 600,
                                fontSize: '13px',
                                width: '120px',
                              }}
                            >
                              CATEGORY
                            </th>
                            <th
                              className='align-middle text-nowrap'
                              style={{
                                padding: '12px 8px',
                                color: '#3F4254',
                                fontWeight: 600,
                                fontSize: '13px',
                                width: '120px',
                              }}
                            >
                              Urgency & Due Date
                            </th>
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '110px' }}>HEAD</th> */}
                            {/* <th className='align-middle text-nowrap' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '135px' }}>SUB HEAD</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '150px' }}>PART</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '180px' }}>SUBCOMPONENTS</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '110px' }}>MAKE</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '110px' }}>MODEL</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '120px' }}>SERIAL NO.</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '120px' }}>PORT</th> */}

                            <th
                              className='align-middle'
                              style={{padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '110px'}}
                            >
                              RAISED DATE
                            </th>

                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '80px' }}>QTY</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '100px' }}>EST. VALUE</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '150px' }}>EQUIPMENT</th> */}
                            {/* <th className='align-middle' style={{ padding: '12px 8px', color: '#3F4254', fontWeight: 600, fontSize: '13px', width: '120px' }}>ATTACHMENTS</th> */}
                            <th
                              className='align-middle'
                              style={{
                                padding: '12px 8px',
                                color: '#3F4254',
                                fontWeight: 600,
                                fontSize: '13px',
                                width: '120px',
                              }}
                            >
                              STATUS
                            </th>
                            <th
                              className='align-middle text-center'
                              style={{
                                padding: '12px 8px',
                                color: '#3F4254',
                                fontWeight: 600,
                                fontSize: '13px',
                                width: '110px',
                                minWidth: '110px',
                              }}
                            >
                              ACTIONS
                            </th>
                          </tr>
                        </thead>

                        <tbody className='table-body text-start'>
                          {currentRecords.length === 0 ? (
                            <tr>
                              <td
                                colSpan={showVesselColumn ? 7 : 6}
                                className='text-center text-muted py-5'
                              >
                                {requisitionScopeCompany
                                  ? activeRequisitionTab === 'pending'
                                    ? 'No approval-pending requisitions found.'
                                    : 'No archived requisitions found.'
                                  : 'No requisitions found for the selected criteria.'}
                              </td>
                            </tr>
                          ) : (
                            currentRecords.map((record) => (
                              <tr key={record.id} style={{borderBottom: '1px solid #E4E6EF'}}>
                                <td
                                  className='text-dark fs-6'
                                  style={{
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    width: '160px',
                                    minWidth: '160px',
                                    maxWidth: '160px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  <div className='d-flex flex-column'>
                                    <span className='fw-semibold'>{record.name || '-'}</span>
                                    <span className='text-muted fs-8'>{record.title}</span>
                                  </div>
                                </td>
                                {showVesselColumn && (
                                  <td
                                    className='text-dark fs-6'
                                    style={{
                                      padding: '12px 16px',
                                      fontSize: '14px',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    <VesselLandingLink
                                      vesselId={record.vesselId ?? null}
                                      label={record.vesselName}
                                      className='text-dark text-decoration-underline'
                                    />
                                  </td>
                                )}
                                <td
                                  className='text-dark fs-6'
                                  style={{
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {record.category}
                                </td>
                                <td style={{padding: '12px 8px'}}>
                                  <RequisitionUrgencyDueDate date={record.requiredDate} urgency={record.urgency} />
                                </td>
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.inventoryItemHead || '-'}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.inventoryItemSubHead || '-'}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.part}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                  {(record as any).subComponents && (record as any).subComponents.length > 0 ? (
                                    <div style={{ maxHeight: '60px', overflowY: 'auto' }}>
                                      {(record as any).subComponents.map((sc: string, idx: number) => (
                                        <div key={idx} className='badge badge-light-primary me-1 mb-1' style={{ fontSize: '11px' }}>
                                          {sc}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    record.subComponent || '-'
                                  )}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.make}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.model}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.serialNumber}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.requestedPort}
                                </td> */}
                                <td className='text-dark fs-6' style={{padding: '12px 16px'}}>
                                  {formatRequisitionRaisedDate(record)}
                                </td>
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                  {record.quantity}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 8px', fontSize: '14px' }}>
                                  ${record.estimatedValue}
                                </td> */}
                                {/* <td className='text-dark fs-6' style={{ padding: '12px 16px', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {record.equipmentLink !== 'N/A' ? (
                                    <a href={record.equipmentLink} target='_blank' rel='noopener noreferrer'
                                      className='text-primary text-hover-primary text-decoration-underline'>
                                      Link
                                    </a>
                                  ) : 'N/A'}
                                </td> */}
                                {/* <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                                  {requisitionAttachments[record.id]?.length > 0 ? (
                                    <div>
                                      <button
                                        className='btn btn-link p-0 text-primary text-hover-primary d-flex align-items-center'
                                        style={{ fontSize: '13px' }}
                                        onClick={() => handleAttachmentClick(requisitionAttachments[record.id][0])}
                                      >
                                        <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-4 me-1' />
                                        {requisitionAttachments[record.id][0].fileName.length > 12
                                          ? requisitionAttachments[record.id][0].fileName.substring(0, 12) + '...'
                                          : requisitionAttachments[record.id][0].fileName}
                                      </button>
                                      {requisitionAttachments[record.id].length > 1 && (
                                        <small className='text-muted d-block mt-1'>+{requisitionAttachments[record.id].length - 1} more</small>
                                      )}
                                    </div>
                                  ) : (
                                    <span className='text-muted'>-</span>
                                  )}
                                </td> */}
                                <td style={{padding: '12px 8px'}}>
                                  {getStatusBadge(record.status)}
                                </td>
                                <td
                                  className='text-center'
                                  style={{
                                    padding: '12px 8px',
                                    width: '110px',
                                    minWidth: '110px',
                                    maxWidth: '110px',
                                  }}
                                >
                                  {renderActions(record)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div
                      className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                      style={{
                        position: 'static',
                        bottom: 0,
                        backgroundColor: '#fff',
                        zIndex: 10,
                        borderTop: '1px solid #dee2e6',
                        marginTop: 'auto',
                      }}
                    >
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Rows per page</span>
                        <SearchableSelect
                          className='form-select'
                          style={{
                            borderRadius: '20px',
                            width: '70px',
                            border: '1px solid #dee2e6',
                            fontSize: '14px',
                            padding: '4px 8px',
                          }}
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </SearchableSelect>
                      </div>
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-3' style={{fontSize: '14px'}}>
                          Showing{' '}
                          <strong>
                            {currentRecords.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}-
                            {Math.min(currentPage * rowsPerPage, filteredTotalElements)}
                          </strong>{' '}
                          of <strong>{filteredTotalElements}</strong>
                        </span>

                        <nav>
                          <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px',
                                }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                ‹
                              </button>
                            </li>

                            {(() => {
                              const pages = []
                              const showPages = 5
                              let startPage = Math.max(1, currentPage - 2)
                              let endPage = Math.min(totalPages, startPage + showPages - 1)

                              if (endPage - startPage < showPages - 1) {
                                startPage = Math.max(1, endPage - showPages + 1)
                              }

                              if (startPage > 1) {
                                pages.push(
                                  <li key={1} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                      }}
                                      onClick={() => handlePageChange(1)}
                                    >
                                      1
                                    </button>
                                  </li>
                                )

                                if (startPage > 2) {
                                  pages.push(
                                    <li key='ellipsis1' className='page-item disabled'>
                                      <span
                                        className='page-link border-0 text-muted'
                                        style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  )
                                }
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <li
                                    key={i}
                                    className={`page-item ${currentPage === i ? 'active' : ''}`}
                                  >
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor:
                                          currentPage === i ? '#F4F9FF' : 'transparent',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        boxShadow: 'none',
                                      }}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  </li>
                                )
                              }

                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push(
                                    <li key='ellipsis2' className='page-item disabled'>
                                      <span
                                        className='page-link border-0 text-muted'
                                        style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  )
                                }

                                pages.push(
                                  <li key={totalPages} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                      }}
                                      onClick={() => handlePageChange(totalPages)}
                                    >
                                      {totalPages}
                                    </button>
                                  </li>
                                )
                              }

                              return pages
                            })()}

                            <li
                              className={`page-item ${
                                currentPage === totalPages || totalPages === 0 ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px',
                                }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                              >
                                ›
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {canCreateRequisition && (
        <RequisitionExportModal
          open={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          initialFilters={{
            requisitionCode: referenceFilter || null,
            status: statusFilter || null,
            search: debouncedSearchTerm || null,
            dateFrom: poDateFromFilter || null,
            dateTo: poDateToFilter || null,
          }}
          currentUser={currentUser}
          requisitionScopeAll={requisitionScopeAll}
          requisitionScopeCompany={requisitionScopeCompany}
          requisitionScopeVessel={requisitionScopeVessel}
        />
      )}

      {canCreateRequisition && (
        <CreateRequisitionModal
          visible={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onSubmit={handleAddRequisitionSuccess}
          onCreated={(requisitionId) => {
            setSelectedRequisitionId(requisitionId)
            setAddItemsModalVisible(true)
          }}
          vesselId={vesselId}
          parts={parts}
          ports={ports}
          setPorts={setPorts}
          categories={categories}
          onSaveExtras={upsertExtras}
        />
      )}

      <ViewRequisitionModal
        visible={isViewModalVisible}
        onClose={() => {
          clearScheduledAutoRefreshes()
          setIsViewModalVisible(false)
          setSelectedRequisition(null)
          closeRemarkActionModal()
          if (openedRequisitionIdRef.current != null) {
            const params = new URLSearchParams(window.location.search)
            params.delete('requisitionId')
            openedRequisitionIdRef.current = null
            navigate({search: params.toString() ? `?${params.toString()}` : ''}, {replace: true})
          }
        }}
        requisition={selectedRequisition}
        attachments={selectedRequisition ? requisitionAttachments[selectedRequisition.id] : []}
        onAttachmentClick={handleAttachmentClick}
        viewLines={viewLines}
        viewLinesLoading={viewLinesLoading}
        canEditQuantities={canEditLineQuantities}
        quantityDrafts={viewLineQtyDrafts}
        updatingLineId={updatingViewLineId}
        onQuantityDraftChange={handleViewLineQuantityDraftChange}
        onSaveLineQuantity={handleSaveViewLineQuantity}
        canSubmitAction={
          !!selectedRequisition &&
          canSubmitRequisitionPermission &&
          canSubmitForStatus(selectedRequisition.rawStatus || selectedRequisition.status)
        }
        canAddItemsAction={
          !!selectedRequisition &&
          canAddItemsToRequisition &&
          (((requisitionViewScope === 'ALL' || requisitionViewScope === 'COMPANY') &&
            canAddItemsForStatus(selectedRequisition.rawStatus || selectedRequisition.status)) ||
            (requisitionViewScope === 'VESSEL' &&
              crewMayEditRequisitionLines(selectedRequisition.rawStatus || selectedRequisition.status)))
        }
        canVesselApproveAction={
          !!selectedRequisition &&
          canApproveVesselRequisition &&
          ['SUBMITTED', 'TO_BE_REVISED'].includes(
            normalizeRequisitionStatus(selectedRequisition.rawStatus || selectedRequisition.status)
          )
        }
        canShoreApproveAction={
          !!selectedRequisition &&
          canApproveShoreRequisition &&
          canCompanyShoreApproveForStatus(selectedRequisition.rawStatus || selectedRequisition.status)
        }
        canReviewAction={
          !!selectedRequisition &&
          canApproveShoreRequisition &&
          canCompanyReviewForStatus(selectedRequisition.rawStatus || selectedRequisition.status)
        }
        canRejectAction={
          !!selectedRequisition &&
          canApproveShoreRequisition &&
          canCompanyReviewForStatus(selectedRequisition.rawStatus || selectedRequisition.status)
        }
        canDeleteAction={
          !!selectedRequisition &&
          canDeleteRequisitionPermission &&
          canSubmitForStatus(selectedRequisition.rawStatus || selectedRequisition.status)
        }
        canDownloadAction={!!selectedRequisition && canExportRequisitions}
        onSubmitAction={() => {
          if (!selectedRequisition) return
          handleSubmitRequisition(selectedRequisition.id)
        }}
        onAddItemsAction={() => {
          if (!selectedRequisition) return
          setIsViewModalVisible(false)
          handleAddItems(selectedRequisition.id)
        }}
        onVesselApproveAction={() => {
          if (!selectedRequisition) return
          handleVesselApproval(selectedRequisition.id)
        }}
        onShoreApproveAction={() => {
          if (!selectedRequisition) return
          handleShoreApproval(selectedRequisition.id)
        }}
        onReviewAction={() => {
          if (!selectedRequisition) return
          handleRequestRevision(selectedRequisition.id)
        }}
        onRejectAction={() => {
          if (!selectedRequisition) return
          handleCompanyRejection(selectedRequisition.id)
        }}
        onDeleteAction={() => {
          if (!selectedRequisition) return
          handleDeleteRequisition(selectedRequisition.id)
        }}
        onDownloadAction={() => {
          if (!selectedRequisition) return
          void handleDownloadRequisition(selectedRequisition.id)
        }}
      />

      {remarkActionModal.open && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 2000,
          }}
        >
          <div className='modal-dialog modal-dialog-centered modal-md'>
            <div className='modal-content bg-white'>
              <div className='modal-header'>
                <h5 className='modal-title text-dark'>
                  {remarkActionModal.type === 'review'
                    ? 'Send Requisition For Review'
                    : 'Reject Requisition'}
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={closeRemarkActionModal}
                  disabled={remarkActionSubmitting}
                ></button>
              </div>
              <div className='modal-body'>
                <label className='form-label fw-semibold text-dark mb-2'>
                  {remarkActionModal.type === 'review' ? 'Review Remark' : 'Rejection Cause'} *
                </label>
                <textarea
                  className='form-control'
                  rows={4}
                  placeholder={
                    remarkActionModal.type === 'review'
                      ? 'Enter review/rework remark to send back to vessel'
                      : 'Enter rejection cause'
                  }
                  value={remarkActionText}
                  onChange={(e) => setRemarkActionText(e.target.value)}
                  disabled={remarkActionSubmitting}
                />
                <small className='text-muted mt-2 d-block'>
                  This remark will be visible in requisition view history.
                </small>
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-light'
                  onClick={closeRemarkActionModal}
                  disabled={remarkActionSubmitting}
                >
                  Cancel
                </button>
                <button
                  className={`btn ${
                    remarkActionModal.type === 'review' ? 'btn-warning text-dark' : 'btn-danger'
                  }`}
                  onClick={submitRemarkAction}
                  disabled={remarkActionSubmitting}
                >
                  {remarkActionSubmitting
                    ? 'Saving...'
                    : remarkActionModal.type === 'review'
                    ? 'Send For Review'
                    : 'Reject Requisition'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedAttachment && (
        <RequisitionAttachmentViewer
          attachment={selectedAttachment}
          onClose={() => setSelectedAttachment(null)}
          onDownload={() => {}}
        />
      )}

      {requisitionDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '900px',
            pointerEvents: 'none',
            opacity: 0,
          }}
        >
          <div id='requisition-download-host'>
            <RequisitionLECRDocument
              requisition={requisitionDownloadDoc.requisition}
              viewLines={requisitionDownloadDoc.lines}
              attachments={requisitionDownloadDoc.attachments}
            />
          </div>
        </div>
      )}

      {selectedRequisitionId && (
        <AddItemsModal
          visible={addItemsModalVisible}
          onClose={() => setAddItemsModalVisible(false)}
          onSubmit={handleAddItemsSuccess}
          requisitionId={selectedRequisitionId}
          parts={parts}
          ports={ports}
          categories={categories}
          companyGroupAdminId={
            (currentUser as any)?.vessel?.companyGroupAdminId ||
            (currentUser as any)?.companyGroupAdminId
          }
        />
      )}

      {/* No-lines confirmation modal */}
      {vesselNoLinesConfirm.open && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 2000,
          }}
        >
          <div className='modal-dialog modal-sm'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title text-dark'>No Line Items</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setVesselNoLinesConfirm({open: false, reqId: null})}
                ></button>
              </div>
              <div className='modal-body text-dark'>
                This requisition has no line items. Do you want to proceed with vessel approval?
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-light btn-sm'
                  onClick={() => setVesselNoLinesConfirm({open: false, reqId: null})}
                >
                  Cancel
                </button>
                <button
                  className='btn btn-danger btn-sm'
                  onClick={async () => {
                    const id = vesselNoLinesConfirm.reqId
                    setVesselNoLinesConfirm({open: false, reqId: null})
                    if (id) {
                      try {
                        await doVesselApprove(id)
                        await runPostActionAutoRefresh(id)
                      } catch (error: any) {
                        console.error('Error with vessel approval:', error)
                        toast.error('Failed to approve for vessel')
                      }
                    }
                  }}
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isRequisitionLibrariesOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.38)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 900,
            padding: '24px',
            backdropFilter: 'blur(2px)',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeRequisitionLibraries()
            }
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '85vw',
              maxWidth: '85vw',
              height: '85vh',
              maxHeight: '85vh',
              background: '#FFFFFF',
              borderRadius: '18px',
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
              border: '1px solid #E9EEF5',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '24px 28px 18px 28px',
                borderBottom: '1px solid #E9EEF5',
                background: '#FFFFFF',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '16px',
                flexShrink: 0,
              }}
            >
              <div style={{minWidth: 0}}>
                <h5
                  style={{
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: 700,
                    color: '#1E293B',
                    lineHeight: 1.2,
                  }}
                >
                  Category & Accounting Libraries
                </h5>
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '13px',
                    color: '#94A3B8',
                    lineHeight: 1.4,
                  }}
                >
                  Manage category, head, and accounting libraries from one workspace.
                </div>
              </div>
              <button
                className='btn btn-light btn-sm'
                onClick={closeRequisitionLibraries}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: 'flex',
                flex: '1 1 auto',
                minHeight: 0,
              }}
            >
              <div
                style={{
                  width: '260px',
                  borderRight: '1px solid #E9EEF5',
                  padding: '16px',
                  background: '#F8FAFC',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {requisitionLibraryNav.map((item) => {
                  const isActive = activeRequisitionLibrary === item.key
                  return (
                    <button
                      key={item.key}
                      type='button'
                      className='btn text-start w-100'
                      onClick={() => setActiveRequisitionLibrary(item.key)}
                      style={{
                        background: isActive ? '#EEF4FF' : '#FFFFFF',
                        border: isActive ? '1px solid #C3D4FF' : '1px solid #E5E7EB',
                        color: isActive ? '#1E293B' : '#475569',
                        fontWeight: isActive ? 700 : 600,
                        borderRadius: '10px',
                        padding: '12px 14px',
                        boxShadow: isActive ? '0 8px 24px rgba(59,130,246,0.12)' : '0 4px 12px rgba(15,23,42,0.04)',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {item.label}
                    </button>
                  )
                })}
              </div>

              <div
                style={{
                  flex: '1 1 auto',
                  background: '#F8FAFC',
                  padding: '12px',
                  overflow: 'hidden',
                }}
              >
                <div style={{height: '100%', overflow: 'auto'}}>
                  {activeRequisitionLibrary === 'category' && (
                    <div className='h-100 d-flex flex-column gap-3'>
                      <div className='d-flex justify-content-between align-items-start'>
                        <div>
                          <h5 className='mb-1 fw-bold text-dark'>Category Library</h5>
                          <div className='text-muted small'>All categories available for requisitions.</div>
                        </div>
                        {canCreateCategoryLibrary && (
                          <button className='btn btn_primary btn-sm' onClick={() => setShowCategoryForm(true)}>
                            Add Category
                          </button>
                        )}
                      </div>
                      {renderLibraryScopeFilters(false)}
                      <div className='card shadow-sm border h-100'>
                        <div className='table-responsive' style={{maxHeight: '100%'}}>
                          <table className='table table-striped table-bordered align-middle mb-0'>
                            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
                              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                                <th style={{minWidth: 160}}>Category</th>
                                <th style={{minWidth: 140}}>Company</th>
                                <th style={{minWidth: 140}}>Vessel</th>
                                <th style={{minWidth: 140}}>Vessel Type</th>
                                <th style={{minWidth: 80}}>ID</th>
                                {(canUpdateCategoryLibrary || canDeleteCategoryLibrary) && (
                                  <th style={{minWidth: 120}}>Actions</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredCategoryLibraryRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={
                                      canUpdateCategoryLibrary || canDeleteCategoryLibrary ? 6 : 5
                                    }
                                    className='text-center text-muted py-4'
                                  >
                                    {activeLibrarySearchTerm ? 'No matching records found' : 'No categories found.'}
                                  </td>
                                </tr>
                              ) : (
                                filteredCategoryLibraryRows.map((cat: InventoryCategory, idx: number) => {
                                  const scope = resolveCategoryScopeMeta(cat)
                                  return (
                                  <tr key={cat.id ?? idx}>
                                    <td className='fw-semibold text-dark'>{cat.name || '-'}</td>
                                    <td className='text-muted'>{cat.cgaid?.name || '-'}</td>
                                    <td className='text-muted'>{formatLibraryVesselLabel(scope)}</td>
                                    <td className='text-muted'>
                                      {typeof cat.vesselType === 'string'
                                        ? cat.vesselType || '-'
                                        : (cat.vesselType as any)?.fleet_name || '-'}
                                    </td>
                                    <td className='text-muted'>{cat.id ?? '-'}</td>
                                    {(canUpdateCategoryLibrary || canDeleteCategoryLibrary) && (
                                      <td>
                                        {renderLibraryRowActions(
                                          canUpdateCategoryLibrary,
                                          canDeleteCategoryLibrary,
                                          () => setEditingCategory(cat),
                                          () => handleDeleteCategoryLibrary(cat)
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                )})
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeRequisitionLibrary === 'head' && (
                    <div className='h-100 d-flex flex-column gap-3'>
                      <div className='d-flex justify-content-between align-items-start'>
                        <div>
                          <h5 className='mb-1 fw-bold text-dark'>Head Library</h5>
                          <div className='text-muted small'>Heads maintained by company/vessel scope.</div>
                        </div>
                        {canCreateHeadLibrary && (
                          <button className='btn btn_primary btn-sm' onClick={() => setShowHeadForm(true)}>
                            Add Head
                          </button>
                        )}
                      </div>
                      {renderLibraryScopeFilters(false)}
                      <div className='card shadow-sm border h-100'>
                        <div className='table-responsive' style={{maxHeight: '100%'}}>
                          <table className='table table-striped table-bordered align-middle mb-0'>
                            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
                              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                                <th style={{minWidth: 140}}>Head</th>
                                <th style={{minWidth: 160}}>Legacy Category</th>
                                <th style={{minWidth: 140}}>Company</th>
                                <th style={{minWidth: 140}}>Vessel</th>
                                <th style={{minWidth: 80}}>ID</th>
                                {(canUpdateHeadLibrary || canDeleteHeadLibrary) && (
                                  <th style={{minWidth: 120}}>Actions</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredHeadLibraryRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={canUpdateHeadLibrary || canDeleteHeadLibrary ? 6 : 5}
                                    className='text-center text-muted py-4'
                                  >
                                    {activeLibrarySearchTerm ? 'No matching records found' : 'No heads found.'}
                                  </td>
                                </tr>
                              ) : (
                                filteredHeadLibraryRows.map((head: InventoryItemHead, idx: number) => {
                                  const cat = inventoryCategories.find((c) => c.id === head.inventoryItemCategoryId)
                                  const scope = resolveHeadScopeMeta(head)
                                  return (
                                    <tr key={head.id ?? idx}>
                                      <td className='fw-semibold text-dark'>{head.name || '-'}</td>
                                      <td className='text-muted'>{cat?.name || head.inventoryItemCategoryName || '-'}</td>
                                      <td className='text-muted'>{head.cgaid?.name || cat?.cgaid?.name || '-'}</td>
                                      <td className='text-muted'>{formatLibraryVesselLabel(scope)}</td>
                                      <td className='text-muted'>{head.id ?? '-'}</td>
                                      {(canUpdateHeadLibrary || canDeleteHeadLibrary) && (
                                        <td>
                                          {renderLibraryRowActions(
                                            canUpdateHeadLibrary,
                                            canDeleteHeadLibrary,
                                            () => setEditingHead(head),
                                            () => handleDeleteHeadLibrary(head)
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  )
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeRequisitionLibrary === 'subHead' && (
                    <div className='h-100 d-flex flex-column gap-3'>
                      <div className='d-flex justify-content-between align-items-start'>
                        <div>
                          <h5 className='mb-1 fw-bold text-dark'>Sub Head Library</h5>
                          <div className='text-muted small'>Sub heads mapped to their heads.</div>
                        </div>
                        {canCreateSubHeadLibrary && (
                          <button className='btn btn_primary btn-sm' onClick={() => setShowSubHeadForm(true)}>
                            Add Sub Head
                          </button>
                        )}
                      </div>
                      {renderLibraryScopeFilters(true)}
                      <div className='card shadow-sm border h-100'>
                        <div className='table-responsive' style={{maxHeight: '100%'}}>
                          <table className='table table-striped table-bordered align-middle mb-0'>
                            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
                              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                                <th style={{minWidth: 160}}>Sub Head</th>
                                <th style={{minWidth: 160}}>Head</th>
                                <th style={{minWidth: 160}}>Legacy Category</th>
                                <th style={{minWidth: 140}}>Company</th>
                                <th style={{minWidth: 140}}>Vessel</th>
                                <th style={{minWidth: 80}}>ID</th>
                                {(canUpdateSubHeadLibrary || canDeleteSubHeadLibrary) && (
                                  <th style={{minWidth: 120}}>Actions</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredSubHeadLibraryRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={canUpdateSubHeadLibrary || canDeleteSubHeadLibrary ? 7 : 6}
                                    className='text-center text-muted py-4'
                                  >
                                    {activeLibrarySearchTerm ? 'No matching records found' : 'No sub heads found.'}
                                  </td>
                                </tr>
                              ) : (
                                filteredSubHeadLibraryRows.map((subHead: InventoryItemSubHead, idx: number) => {
                                  const head = inventoryItemHeads.find((h) => h.id === subHead.inventoryItemHeadId)
                                  const cat = head ? inventoryCategories.find((c) => c.id === head.inventoryItemCategoryId) : undefined
                                  const scope = resolveSubHeadScopeMeta(subHead)
                                  return (
                                    <tr key={subHead.id ?? idx}>
                                      <td className='fw-semibold text-dark'>{subHead.name || '-'}</td>
                                      <td className='text-muted'>{head?.name || subHead.inventoryItemHeadName || '-'}</td>
                                      <td className='text-muted'>{cat?.name || head?.inventoryItemCategoryName || '-'}</td>
                                      <td className='text-muted'>{subHead.cgaid?.name || head?.cgaid?.name || cat?.cgaid?.name || '-'}</td>
                                      <td className='text-muted'>{formatLibraryVesselLabel(scope)}</td>
                                      <td className='text-muted'>{subHead.id ?? '-'}</td>
                                      {(canUpdateSubHeadLibrary || canDeleteSubHeadLibrary) && (
                                        <td>
                                          {renderLibraryRowActions(
                                            canUpdateSubHeadLibrary,
                                            canDeleteSubHeadLibrary,
                                            () => setEditingSubHead(subHead),
                                            () => handleDeleteSubHeadLibrary(subHead)
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  )
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeRequisitionLibrary === 'subCategory' && (
                    <div className='h-100 d-flex flex-column gap-3'>
                      <div className='d-flex justify-content-between align-items-start'>
                        <div>
                          <h5 className='mb-1 fw-bold text-dark'>Sub Category Library</h5>
                          <div className='text-muted small'>Sub categories for Stores items (e.g., IMPA Stores, Chemicals, Gases & Lube oil).</div>
                        </div>
                        {canManageSubCategoryLibrary && (
                          <button
                            className='btn btn_primary btn-sm'
                            onClick={() => {
                              setSubCategoryForm({name: '', description: '', active: true})
                              setShowSubCategoryForm(true)
                            }}
                          >
                            Add Sub Category
                          </button>
                        )}
                      </div>

                      {canManageSubCategoryLibrary && showSubCategoryForm && (
                        <div className='card shadow-sm border'>
                          <div className='card-body row g-3 align-items-end'>
                            <div className='col-md-4'>
                              <label className='form-label fw-semibold'>Name</label>
                              <input
                                className='form-control'
                                value={subCategoryForm.name}
                                onChange={(e) => setSubCategoryForm((p) => ({...p, name: e.target.value}))}
                                placeholder='e.g., IMPA Stores'
                              />
                            </div>
                            <div className='col-md-5'>
                              <label className='form-label fw-semibold'>Description (optional)</label>
                              <input
                                className='form-control'
                                value={subCategoryForm.description}
                                onChange={(e) => setSubCategoryForm((p) => ({...p, description: e.target.value}))}
                                placeholder='Short note'
                              />
                            </div>
                            <div className='col-md-2 d-flex align-items-center gap-2'>
                              <div className='form-check'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  id='subcatActive'
                                  checked={subCategoryForm.active}
                                  onChange={(e) =>
                                    setSubCategoryForm((p) => ({...p, active: e.target.checked}))
                                  }
                                />
                                <label className='form-check-label' htmlFor='subcatActive'>
                                  Active
                                </label>
                              </div>
                            </div>
                            <div className='col-md-1 d-flex justify-content-end'>
                              <button
                                className='btn btn_primary btn-sm'
                                onClick={async () => {
                                  if (!subCategoryForm.name.trim()) {
                                    toast.error('Name is required')
                                    return
                                  }
                                  try {
                                    const created = await createInventoryItemSubCategory({
                                      name: subCategoryForm.name.trim(),
                                      description: subCategoryForm.description?.trim(),
                                      active: subCategoryForm.active,
                                    })
                                    setInventoryItemSubCategories((prev: InventoryItemSubCategory[]) => [...prev, created])
                                    setShowSubCategoryForm(false)
                                    toast.success('Sub Category created')
                                  } catch (err: any) {
                                    toast.error('Failed to create sub category')
                                  }
                                }}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className='card shadow-sm border h-100'>
                        <div className='table-responsive' style={{maxHeight: '100%'}}>
                          <table className='table table-striped table-bordered align-middle mb-0'>
                            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
                              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                                <th style={{minWidth: 180}}>Sub Category</th>
                                <th style={{minWidth: 220}}>Description</th>
                                <th style={{minWidth: 80}}>Active</th>
                                <th style={{minWidth: 80}}>ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredSubCategoryLibraryRows.length === 0 ? (
                                <tr>
                                  <td colSpan={4} className='text-center text-muted py-4'>
                                    {activeLibrarySearchTerm
                                      ? 'No matching records found'
                                      : 'No sub categories found.'}
                                  </td>
                                </tr>
                              ) : (
                                filteredSubCategoryLibraryRows.map((sc: InventoryItemSubCategory) => (
                                  <tr key={sc.id}>
                                    <td className='fw-semibold text-dark'>{sc.name}</td>
                                    <td className='text-muted'>{sc.description || '-'}</td>
                                    <td>
                                      <span className={`badge ${sc.active ? 'bg-success' : 'bg-secondary'}`}>
                                        {sc.active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td className='text-muted'>{sc.id}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeRequisitionLibrary === 'address' && (
                    <div className='h-100 d-flex flex-column gap-3'>
                      <div className='d-flex justify-content-between align-items-start'>
                        <div>
                          <h5 className='mb-1 fw-bold text-dark'>Address Library</h5>
                          <div className='text-muted small'>
                            Billing/Shipping address master scoped by company.
                          </div>
                        </div>
                        {canManageAddressLibrary && (
                          <button
                            className='btn btn_primary btn-sm'
                            onClick={() => {
                              setAddressForm((prev) => ({
                                ...defaultAddressFormState,
                                companyId:
                                  requisitionScopeAll
                                    ? libraryCompanyFilterId ?? prev.companyId ?? null
                                    : crewLibraryResolvedCompanyScopeId ?? libraryCompanyFilterId ?? null,
                              }))
                              setShowAddressForm(true)
                            }}
                          >
                            Add Address
                          </button>
                        )}
                      </div>
                      <div className='card shadow-sm border'>
                        <div className='card-body py-3'>
                          <div className='row g-3 align-items-end'>
                            <div className='col-md-6'>
                              <label className='form-label fw-semibold text-dark mb-1'>Company</label>
                              <SearchableSelect
                                className='form-select form-select-sm'
                                value={effectiveLibraryCompanyFilterId ?? ''}
                                disabled={requisitionScopeVessel}
                                onChange={(e) => {
                                  const nextCompanyId = e.target.value ? Number(e.target.value) : null
                                  setLibraryCompanyFilterId(nextCompanyId)
                                }}
                              >
                                {requisitionScopeVessel ? (
                                  <option value={effectiveLibraryCompanyFilterId ?? ''}>
                                    {(currentUser as any)?.vessel?.companyGroupAdmin?.name ||
                                      (currentUser as any)?.companyGroupAdmin?.name ||
                                      (effectiveLibraryCompanyFilterId
                                        ? `Company ${effectiveLibraryCompanyFilterId}`
                                        : 'My Company')}
                                  </option>
                                ) : (
                                  <option value=''>All Companies</option>
                                )}
                                {libraryCompanyOptions.map((option) => (
                                  <option key={option.id} value={option.id}>
                                    {option.name}
                                  </option>
                                ))}
                              </SearchableSelect>
                            </div>
                          </div>
                        </div>
                      </div>

                      {canManageAddressLibrary && showAddressForm && (
                        <div className='card shadow-sm border'>
                          <div className='card-body row g-3 align-items-end'>
                            {requisitionScopeAll && (
                              <div className='col-md-3'>
                                <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                  Company
                                </label>
                                <SearchableSelect
                                  className='form-select'
                                  value={addressForm.companyId ?? ''}
                                  onChange={(e) =>
                                    setAddressForm((prev) => ({
                                      ...prev,
                                      companyId: e.target.value ? Number(e.target.value) : null,
                                    }))
                                  }
                                >
                                  <option value=''>Select Company</option>
                                  {(addressCompanyOptions.length > 0
                                    ? addressCompanyOptions
                                    : libraryCompanyOptions
                                  ).map((option) => (
                                    <option key={option.id} value={option.id}>
                                      {option.name}
                                    </option>
                                  ))}
                                </SearchableSelect>
                              </div>
                            )}
                            <div className={requisitionScopeAll ? 'col-md-3' : 'col-md-4'}>
                              <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                Address Type
                              </label>
                              <SearchableSelect
                                className='form-select'
                                value={addressForm.addressType}
                                onChange={(e) =>
                                  setAddressForm((prev) => ({
                                    ...prev,
                                    addressType: e.target.value as 'BILLING' | 'SHIPPING' | 'BOTH',
                                  }))
                                }
                              >
                                <option value='BILLING'>Billing</option>
                                <option value='SHIPPING'>Shipping</option>
                                <option value='BOTH'>Both</option>
                              </SearchableSelect>
                            </div>
                            <div className={requisitionScopeAll ? 'col-md-3' : 'col-md-4'}>
                              <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                Address Label
                              </label>
                              <input
                                className='form-control'
                                value={addressForm.addressName}
                                onChange={(e) =>
                                  setAddressForm((prev) => ({...prev, addressName: e.target.value}))
                                }
                                placeholder='Head Office Billing'
                              />
                            </div>
                            <div className={requisitionScopeAll ? 'col-md-3' : 'col-md-4'}>
                              <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                Address Line 1
                              </label>
                              <input
                                className='form-control'
                                value={addressForm.addressLine1}
                                onChange={(e) =>
                                  setAddressForm((prev) => ({...prev, addressLine1: e.target.value}))
                                }
                                placeholder='Street / Building'
                              />
                            </div>
                            <div className='col-md-4'>
                              <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                Address Line 2
                              </label>
                              <input
                                className='form-control'
                                value={addressForm.addressLine2}
                                onChange={(e) =>
                                  setAddressForm((prev) => ({...prev, addressLine2: e.target.value}))
                                }
                                placeholder='Area / Landmark'
                              />
                            </div>
                            <div className='col-md-2'>
                              <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                City
                              </label>
                              <input
                                className='form-control'
                                value={addressForm.city}
                                onChange={(e) =>
                                  setAddressForm((prev) => ({...prev, city: e.target.value}))
                                }
                              />
                            </div>
                            <div className='col-md-2'>
                              <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                State
                              </label>
                              <input
                                className='form-control'
                                value={addressForm.state}
                                onChange={(e) =>
                                  setAddressForm((prev) => ({...prev, state: e.target.value}))
                                }
                              />
                            </div>
                            <div className='col-md-2'>
                              <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                Country
                              </label>
                              <input
                                className='form-control'
                                value={addressForm.country}
                                onChange={(e) =>
                                  setAddressForm((prev) => ({...prev, country: e.target.value}))
                                }
                              />
                            </div>
                            <div className='col-md-2'>
                              <label className='form-label fw-semibold text-dark' style={{color: '#181C32'}}>
                                Postal Code
                              </label>
                              <input
                                className='form-control'
                                value={addressForm.postalCode}
                                onChange={(e) =>
                                  setAddressForm((prev) => ({...prev, postalCode: e.target.value}))
                                }
                              />
                            </div>
                            <div className='col-md-1'>
                              <div className='form-check mt-8'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  id='addressActive'
                                  checked={addressForm.active}
                                  onChange={(e) =>
                                    setAddressForm((prev) => ({...prev, active: e.target.checked}))
                                  }
                                />
                                <label className='form-check-label' htmlFor='addressActive'>
                                  Active
                                </label>
                              </div>
                            </div>
                            <div className='col-md-1 d-flex justify-content-end gap-2'>
                              <button
                                className='btn btn-light btn-sm'
                                onClick={() => {
                                  setShowAddressForm(false)
                                  setAddressForm(defaultAddressFormState)
                                }}
                              >
                                Cancel
                              </button>
                              <button className='btn btn_primary btn-sm' onClick={handleSaveAddressLibrary}>
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className='card shadow-sm border h-100'>
                        <div className='table-responsive' style={{maxHeight: '100%'}}>
                          <table className='table table-striped table-bordered align-middle mb-0'>
                            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
                              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                                <th style={{minWidth: 160}}>Label</th>
                                <th style={{minWidth: 120}}>Type</th>
                                <th style={{minWidth: 260}}>Address</th>
                                <th style={{minWidth: 160}}>Company</th>
                                <th style={{minWidth: 90}}>Active</th>
                                <th style={{minWidth: 80}}>ID</th>
                                {canManageAddressLibrary && <th style={{minWidth: 140}}>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredAddressLibraryRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={canManageAddressLibrary ? 7 : 6}
                                    className='text-center text-muted py-4'
                                  >
                                    {activeLibrarySearchTerm ? 'No matching records found' : 'No addresses found.'}
                                  </td>
                                </tr>
                              ) : (
                                filteredAddressLibraryRows.map((address: InventoryAddress, idx: number) => (
                                  <tr key={address.id ?? idx}>
                                    <td className='fw-semibold text-dark'>{address.displayLabel || address.addressName || '-'}</td>
                                    <td className='text-muted'>{address.addressType || '-'}</td>
                                    <td className='text-muted'>{address.fullAddress || address.addressLine1 || '-'}</td>
                                    <td className='text-muted'>{address.cgaid?.name || '-'}</td>
                                    <td>
                                      <span className={`badge ${address.active ? 'bg-success' : 'bg-secondary'}`}>
                                        {address.active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td className='text-muted'>{address.id ?? '-'}</td>
                                    {canManageAddressLibrary && (
                                      <td>
                                        <div className='d-flex gap-2'>
                                          <button
                                            className='btn btn-light-primary btn-sm'
                                            onClick={() => handleEditAddressLibrary(address)}
                                          >
                                            Edit
                                          </button>
                                          <button
                                            className='btn btn-light-danger btn-sm'
                                            onClick={() => handleDeleteAddressLibrary(address.id)}
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeRequisitionLibrary === 'terms' && (
                    <TermsConditionsLibraryPanel searchTerm={activeLibrarySearchTerm} />
                  )}
                  {activeRequisitionLibrary === 'accountingCode' && (
                    <div className='h-100 d-flex flex-column gap-3'>
                      <div className='d-flex justify-content-between align-items-start'>
                        <div>
                          <h5 className='mb-1 fw-bold text-dark'>Accounting Code Library</h5>
                          <div className='text-muted small'>Accounting codes with vessel scope.</div>
                        </div>
                        {canCreateAccountingLibrary && (
                          <button
                            className='btn btn_primary btn-sm'
                            onClick={() => setShowAccountingCodeForm(true)}
                          >
                            Add Accounting Code
                          </button>
                        )}
                      </div>
                      {renderLibraryScopeFilters(false)}
                      <div className='card shadow-sm border h-100'>
                        <div className='table-responsive' style={{maxHeight: '100%'}}>
                          <table className='table table-striped table-bordered align-middle mb-0'>
                            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
                              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                                <th style={{minWidth: 160}}>Account Code</th>
                                <th style={{minWidth: 160}}>Description / Name</th>
                                <th style={{minWidth: 140}}>Company</th>
                                <th style={{minWidth: 140}}>Vessel</th>
                                <th style={{minWidth: 140}}>Vessel Type</th>
                                <th style={{minWidth: 140}}>Category</th>
                                <th style={{minWidth: 80}}>ID</th>
                                {(canUpdateAccountingLibrary || canDeleteAccountingLibrary) && (
                                  <th style={{minWidth: 120}}>Actions</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredAccountingLibraryRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={
                                      canUpdateAccountingLibrary || canDeleteAccountingLibrary ? 8 : 7
                                    }
                                    className='text-center text-muted py-4'
                                  >
                                    {activeLibrarySearchTerm ? 'No matching records found' : 'No accounting codes found.'}
                                  </td>
                                </tr>
                              ) : (
                                filteredAccountingLibraryRows.map((acc: AccountingAccount, idx: number) => {
                                  const catName = (acc as any)?.category?.name || '-'
                                  const scope = resolveAccountingScopeMeta(acc)
                                  return (
                                    <tr key={acc.id ?? idx}>
                                      <td className='fw-semibold text-dark'>{(acc as any).accountCodes || acc.accountCodes || '-'}</td>
                                      <td className='text-muted'>{(acc as any)?.name || '-'}</td>
                                      <td className='text-muted'>
                                        {typeof acc.cgaid === 'object' ? acc.cgaid?.name : '-'}
                                      </td>
                                      <td className='text-muted'>{formatLibraryVesselLabel(scope)}</td>
                                      <td className='text-muted'>{acc.vesselType || '-'}</td>
                                      <td className='text-muted'>{catName}</td>
                                      <td className='text-muted'>{acc.id ?? '-'}</td>
                                      {(canUpdateAccountingLibrary || canDeleteAccountingLibrary) && (
                                        <td>
                                          {renderLibraryRowActions(
                                            canUpdateAccountingLibrary,
                                            canDeleteAccountingLibrary,
                                            () => setEditingAccountingCode(acc),
                                            () => handleDeleteAccountingLibrary(acc)
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  )
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeRequisitionLibrary === 'uom' && (
                    <UomLibraryPanel
                      canCreate={canCreateUomLibrary}
                      canUpdate={canUpdateUomLibrary}
                      canDelete={canDeactivateUomLibrary}
                    />
                  )}
                  {activeRequisitionLibrary === 'port' && (
                    <div className='h-100 d-flex flex-column gap-3'>
                      <div className='d-flex justify-content-between align-items-start gap-3 flex-wrap'>
                        <div>
                          <h5 className='mb-1 fw-bold text-dark'>Port Library</h5>
                          <div className='text-muted small'>Manage the ports available for procurement requests.</div>
                        </div>
                        {(canCreatePortLibrary || canUpdatePortLibrary || canDeactivatePortLibrary) && (
                          <button className='btn btn_primary btn-sm' onClick={() => { setEditingPort(null); setShowPortForm(true) }} disabled={!canCreatePortLibrary}>
                            Add Port
                          </button>
                        )}
                      </div>
                      <div>
                        <label className='form-label text-muted fw-semibold fs-7 mb-2'>Search</label>
                        <input
                          className='form-control form-control-sm'
                          placeholder='Search ports...'
                          value={portLibrarySearch}
                          onChange={(event) => setPortLibrarySearch(event.target.value)}
                        />
                      </div>
                      <div className='card shadow-sm border h-100 d-flex flex-column'>
                        <div className='table-responsive'>
                          <table className='table table-striped table-bordered align-middle mb-0'>
                            <thead className='table-light'>
                              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                                <th>Port Name</th>
                                <th>Latitude</th>
                                <th>Longitude</th>
                                <th>ID</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {portLibraryLoading ? (
                                <tr><td colSpan={5} className='text-center text-muted py-4'>Loading ports...</td></tr>
                              ) : portLibraryRows.length === 0 ? (
                                <tr><td colSpan={5} className='text-center text-muted py-4'>No ports found.</td></tr>
                              ) : (
                                portLibraryRows.map((port) => (
                                  <tr key={port.id}>
                                    <td className='fw-semibold text-dark'>{port.name || port.main_port}</td>
                                    <td className='text-muted'>{port.latitude ?? '-'}</td>
                                    <td className='text-muted'>{port.longitude ?? '-'}</td>
                                    <td className='text-muted'>{port.id}</td>
                                    <td>
                                      {canUpdatePortLibrary && <button className='btn btn-sm btn-light-warning me-1' onClick={() => { setEditingPort(port); setPortForm({name: port.name || port.main_port || '', latitude: port.latitude == null ? '' : String(port.latitude), longitude: port.longitude == null ? '' : String(port.longitude)}); setShowPortForm(true) }}>Edit</button>}
                                      {canDeactivatePortLibrary && <button className='btn btn-sm btn-light-danger' onClick={() => { if (window.confirm(`Deactivate ${port.name || port.main_port}?`)) void deletePort(port.id).then(() => loadPortLibrary()).catch(() => toast.error('Failed to deactivate port')) }}>Deactivate</button>}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className='pagination-wrapper d-flex justify-content-between align-items-center flex-wrap gap-3 py-3 px-4 mt-auto border-top'>
                          <div className='d-flex align-items-center gap-2'>
                            <span className='text-muted'>Rows per page</span>
                            <SearchableSelect
                              className='form-select form-select-sm'
                              style={{width: '70px'}}
                              value={portLibrarySize}
                              onChange={(event) => {
                                setPortLibrarySize(Number(event.target.value))
                                setPortLibraryPage(0)
                              }}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </SearchableSelect>
                          </div>
                          <div className='d-flex align-items-center gap-2'>
                            <span className='text-muted small'>
                              Showing {portLibraryTotalElements === 0 ? 0 : portLibraryPage * portLibrarySize + 1}-
                              {Math.min((portLibraryPage + 1) * portLibrarySize, portLibraryTotalElements)} of {portLibraryTotalElements}
                            </span>
                            <button
                              className='btn btn-light btn-sm'
                              onClick={() => setPortLibraryPage((page) => Math.max(page - 1, 0))}
                              disabled={portLibraryPage === 0 || portLibraryLoading}
                            >
                              ‹
                            </button>
                            {Array.from({length: Math.min(portLibraryTotalPages, 5)}, (_, index) => index).map((page) => (
                              <button
                                key={page}
                                className={`btn btn-sm ${page === portLibraryPage ? 'btn-primary' : 'btn-light'}`}
                                onClick={() => setPortLibraryPage(page)}
                                disabled={portLibraryLoading}
                              >
                                {page + 1}
                              </button>
                            ))}
                            {portLibraryTotalPages > 5 && <span className='text-muted'>...</span>}
                            <button
                              className='btn btn-light btn-sm'
                              onClick={() => setPortLibraryPage((page) => Math.min(page + 1, portLibraryTotalPages - 1))}
                              disabled={portLibraryTotalPages === 0 || portLibraryPage >= portLibraryTotalPages - 1 || portLibraryLoading}
                            >
                              ›
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeRequisitionLibrary === 'subAccounting' && (
                    <div className='h-100 d-flex flex-column gap-3'>
                      <div className='d-flex justify-content-between align-items-start'>
                        <div>
                          <h5 className='mb-1 fw-bold text-dark'>Sub Accounting Library</h5>
                          <div className='text-muted small'>Sub accounts linked to accounting codes.</div>
                        </div>
                        {canCreateSubAccountingLibrary && (
                          <button
                            className='btn btn_primary btn-sm'
                            onClick={() => setShowSubAccountingForm(true)}
                          >
                            Add Sub Accounting
                          </button>
                        )}
                      </div>
                      {renderLibraryScopeFilters(false)}
                      <div className='card shadow-sm border h-100'>
                        <div className='table-responsive' style={{maxHeight: '100%'}}>
                          <table className='table table-striped table-bordered align-middle mb-0'>
                            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
                              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                                <th style={{minWidth: 170}}>Sub Account Code</th>
                                <th style={{minWidth: 160}}>Parent Account Code</th>
                                <th style={{minWidth: 140}}>Company</th>
                                <th style={{minWidth: 140}}>Vessel</th>
                                <th style={{minWidth: 140}}>Vessel Type</th>
                                <th style={{minWidth: 80}}>ID</th>
                                {(canUpdateSubAccountingLibrary || canDeleteSubAccountingLibrary) && (
                                  <th style={{minWidth: 120}}>Actions</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredSubAccountingLibraryRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={
                                      canUpdateSubAccountingLibrary || canDeleteSubAccountingLibrary ? 7 : 6
                                    }
                                    className='text-center text-muted py-4'
                                  >
                                    {activeLibrarySearchTerm ? 'No matching records found' : 'No sub accounts found.'}
                                  </td>
                                </tr>
                              ) : (
                                filteredSubAccountingLibraryRows.map((sub: SubAccount, idx: number) => {
                                  const parentAcc = accountingAccounts.find(
                                    (acc: AccountingAccount) => acc.id === (sub as any)?.accountCodeId
                                  )
                                  const scope = resolveSubAccountingScopeMeta(sub)
                                  return (
                                    <tr key={sub.id ?? idx}>
                                      <td className='fw-semibold text-dark'>{(sub as any).subaccountCodes || sub.subaccountCodes || '-'}</td>
                                      <td className='text-muted'>
                                        {(sub as any)?.accountCode?.accountCodes ||
                                          parentAcc?.accountCodes ||
                                          sub.accountCode ||
                                          '-'}
                                      </td>
                                      <td className='text-muted'>{sub.cgaid?.name || '-'}</td>
                                      <td className='text-muted'>{formatLibraryVesselLabel(scope)}</td>
                                      <td className='text-muted'>{sub.vesselType || '-'}</td>
                                      <td className='text-muted'>{sub.id ?? '-'}</td>
                                      {(canUpdateSubAccountingLibrary || canDeleteSubAccountingLibrary) && (
                                        <td>
                                          {renderLibraryRowActions(
                                            canUpdateSubAccountingLibrary,
                                            canDeleteSubAccountingLibrary,
                                            () => setEditingSubAccounting(sub),
                                            () => handleDeleteSubAccountingLibrary(sub)
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  )
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{top: '5rem'}}
      />

      {showPortForm && (
        <div className='modal d-block' role='dialog' aria-modal='true' style={{background: 'rgba(0, 0, 0, 0.45)'}}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingPort ? 'Edit Port' : 'Add Port'}</h5>
                <button type='button' className='btn-close' onClick={() => setShowPortForm(false)} disabled={portLibrarySaving} />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label'>Port Name *</label>
                  <input className='form-control' value={portForm.name} onChange={(event) => setPortForm((form) => ({...form, name: event.target.value}))} />
                </div>
                <div className='mb-3'>
                  <label className='form-label'>Latitude</label>
                  <input className='form-control' type='number' step='any' value={portForm.latitude} onChange={(event) => setPortForm((form) => ({...form, latitude: event.target.value}))} />
                </div>
                <div>
                  <label className='form-label'>Longitude</label>
                  <input className='form-control' type='number' step='any' value={portForm.longitude} onChange={(event) => setPortForm((form) => ({...form, longitude: event.target.value}))} />
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-light' onClick={() => setShowPortForm(false)} disabled={portLibrarySaving}>Cancel</button>
                <button type='button' className='btn btn_primary' onClick={() => void handleSavePortLibrary()} disabled={portLibrarySaving}>
                  {portLibrarySaving ? 'Saving...' : editingPort ? 'Save Port' : 'Add Port'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canCreateCategoryLibrary && (
        <CategoryModal
          visible={showCategoryForm}
          onClose={() => setShowCategoryForm(false)}
          onSubmit={handleCategorySubmit}
          vesselId={Number(vesselId ?? 0)}
        />
      )}
      {canUpdateCategoryLibrary && (
        <EditCategoryModal
          visible={Boolean(editingCategory)}
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSubmit={handleEditCategorySubmit}
        />
      )}
      {canCreateHeadLibrary && (
        <HeadModal
          visible={showHeadForm}
          onClose={() => setShowHeadForm(false)}
          onSubmit={handleHeadSubmit}
          vesselId={vesselId ?? undefined}
        />
      )}
      {canUpdateHeadLibrary && (
        <EditHeadModal
          visible={Boolean(editingHead)}
          head={editingHead}
          onClose={() => setEditingHead(null)}
          onSubmit={handleEditHeadSubmit}
        />
      )}
      {canCreateSubHeadLibrary && (
        <SubHeadModal
          visible={showSubHeadForm}
          onClose={() => setShowSubHeadForm(false)}
          onSubmit={handleSubHeadSubmit}
          vesselId={vesselId ?? undefined}
        />
      )}
      {canUpdateSubHeadLibrary && (
        <EditSubHeadModal
          visible={Boolean(editingSubHead)}
          subHead={editingSubHead}
          onClose={() => setEditingSubHead(null)}
          onSubmit={handleEditSubHeadSubmit}
        />
      )}
      {canCreateAccountingLibrary && (
        <AccountingCodeModal
          visible={showAccountingCodeForm}
          onClose={() => setShowAccountingCodeForm(false)}
          onSubmit={handleAccountingCodeSubmit}
          vesselId={Number(vesselId ?? 0)}
        />
      )}
      {canUpdateAccountingLibrary && (
        <EditAccountingCodeModal
          visible={Boolean(editingAccountingCode)}
          accountingCode={editingAccountingCode}
          onClose={() => setEditingAccountingCode(null)}
          onSubmit={handleEditAccountingCodeSubmit}
        />
      )}
      {canCreateSubAccountingLibrary && (
        <SubAccountingModal
          visible={showSubAccountingForm}
          onClose={() => setShowSubAccountingForm(false)}
          onSubmit={handleSubAccountingSubmit}
        />
      )}
      {canUpdateSubAccountingLibrary && (
        <EditSubAccountingModal
          visible={Boolean(editingSubAccounting)}
          subAccount={editingSubAccounting}
          onClose={() => setEditingSubAccounting(null)}
          onSubmit={handleEditSubAccountingSubmit}
        />
      )}

    </div>
  )
}

export {RequisitionList, CreateRequisitionModal}
